import React, { useContext, useState, useMemo, useEffect, createContext, useCallback, useRef, forwardRef } from 'react';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import fetch from 'isomorphic-fetch';
import createLogger from 'debug';
import { useLocation } from 'react-router-dom';
import { useEventEmitter, useLocalStorageState, useSetState, useMemoizedFn, useUpdateEffect, useRequest, useCreation, useThrottleFn, useBoolean } from 'ahooks';
import useSWR, { mutate } from 'swr';
import 'socket.io-client';
import { FormattedMessage, useIntl, IntlProvider } from 'react-intl';
import { FaGoogle } from 'react-icons/fa';
import { clsx } from 'clsx';
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/react-dom';
import { createPortal } from 'react-dom';
import { MdVisibilityOff, MdVisibility } from 'react-icons/md';

const ApiContext = React.createContext(null);
const useApi = () => {
  return useContext(ApiContext);
};

const APIProvider = ({
  api,
  children
}) => {
  return /* @__PURE__ */ jsx(ApiContext.Provider, {
    value: api,
    children: React.Children.only(children)
  });
};

const buildQueryString = (params) => {
  const searchParams = new URLSearchParams();
  for (const key in params) {
    if (params.hasOwnProperty(key)) {
      const value = params[key];
      if (value) {
        searchParams.append(key, value);
      }
    }
  }
  const query = searchParams.toString();
  return query ? "?" + query : "";
};

const getQueryStringParams = (query) => {
  return query ? (/^[?#]/.test(query) ? query.slice(1) : query).split("&").reduce((params, param) => {
    let [key, value] = param.split("=");
    params[key] = value ? decodeURIComponent(value.replace(/\+/g, " ")) : "";
    return params;
  }, {}) : {};
};
const object2QueryString = (object) => {
  const str = [];
  for (var p in object)
    if (object.hasOwnProperty(p)) {
      const value = object[p];
      if (value === void 0 || value === "") {
        continue;
      }
      str.push(encodeURIComponent(p) + "=" + (typeof value === "string" ? encodeURIComponent(value) : value));
    }
  return str.join("&");
};
const setQueryStringParams = (params) => {
  window.location.hash = "?" + object2QueryString(params);
};
const useQueryStringParams = () => {
  const location = useLocation();
  const hash = location.hash.slice(1);
  if (!hash) {
    return {};
  }
  const paramsPart = hash.split("?")[1];
  if (!paramsPart) {
    return {};
  }
  return getQueryStringParams(paramsPart);
};

const buildUrlWithParams = (baseUrl, params) => {
  if (!params || Object.keys(params).length === 0) {
    return baseUrl;
  }
  const queryString = object2QueryString(params);
  if (!queryString) {
    return baseUrl;
  }
  const separator = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${separator}${queryString}`;
};

const createApiError = (type, details) => {
  const error = new Error(details.message);
  error.name = "ApiError";
  error.type = type;
  error.status = details.status;
  error.code = details.status;
  error.statusText = details.statusText;
  error.response = details.response;
  return error;
};
const createUploadError = (type, details) => {
  const error = createApiError(type, details);
  error.name = "UploadError";
  return error;
};
const parseApiResponse = async (response) => {
  try {
    const text = await response.text();
    if (!text)
      return null;
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  } catch {
    return null;
  }
};
const parseErrorResponse = async (xhr) => {
  try {
    if (xhr.response) {
      if (typeof xhr.response === "string") {
        return JSON.parse(xhr.response);
      }
      return xhr.response;
    }
    return null;
  } catch {
    return xhr.responseText || xhr.response;
  }
};
const handleApiError = async (response, parsedResponse) => {
  const status = response.status;
  const statusText = "statusText" in response ? response.statusText : "";
  let errorResponse = parsedResponse;
  if (!errorResponse && "text" in response) {
    errorResponse = await parseApiResponse(response);
  }
  const message = getErrorMessage(status, statusText || "", errorResponse);
  const error = createApiError("server", {
    message,
    status,
    statusText,
    response: errorResponse
  });
  if (errorResponse && typeof errorResponse === "object") {
    if (errorResponse.errors) {
      error.errors = errorResponse.errors;
    } else {
      error.errors = errorResponse;
    }
  }
  return error;
};
const getErrorMessage = (status, statusText, response) => {
  if (response && typeof response === "object") {
    if (response.message)
      return response.message;
    if (response.error && response.error.message)
      return response.error.message;
    if (response.errors && Array.isArray(response.errors) && response.errors[0]) {
      return response.errors[0].message || response.errors[0];
    }
  }
  switch (status) {
    case 400:
      return "Invalid request. Please check your input and try again.";
    case 401:
      return "Authentication required. Please log in and try again.";
    case 403:
      return "You do not have permission to perform this action.";
    case 404:
      return "The requested resource was not found.";
    case 413:
      return "Request too large. Please reduce the size and try again.";
    case 415:
      return "Media type not supported. Please use a different format.";
    case 422:
      return "Validation failed. Please check your input and try again.";
    case 429:
      return "Too many requests. Please wait a moment and try again.";
    case 500:
      return "Server error occurred. Please try again later.";
    case 502:
    case 503:
    case 504:
      return "Service temporarily unavailable. Please try again later.";
    default:
      return statusText || `Request failed with status ${status}`;
  }
};

const info = createLogger("info:ui");
const DEFAULT_PAGE_SIZE = 100;
const DEFAULT_RANGE = { start: 1, end: DEFAULT_PAGE_SIZE };
const simulateErrors = typeof localStorage !== "undefined" && localStorage.simulateErrors && parseInt(localStorage.simulateErrors);
class API {
  constructor({ root = "/ua", domain, credentials, tokenAuth = false }) {
    this.expires = 0;
    this.root = root;
    this.rootWebsockets = root.replace("/ua", "/websockets").replace(":3002", ":3004");
    this.domain = domain;
    this.credentials = credentials;
    this.tokenAuth = tokenAuth;
    this.isEmailAvailable = this.isEmailAvailable.bind(this);
    this.isUsernameAvailable = this.isUsernameAvailable.bind(this);
  }
  setAuthToken(token) {
    this.authToken = token;
  }
  getServiceRoot(service) {
    return this.rootWebsockets;
  }
  createEndpoint(path, { service }) {
    if (path.startsWith("https://") || path.startsWith("http://")) {
      return path;
    }
    const cleaned = path[0] === "/" ? path.slice(1) : path;
    let root;
    if (service) {
      root = this.getServiceRoot(service);
    } else {
      root = cleaned.startsWith(this.root.slice(1) + "/") ? "" : this.root;
    }
    const endpoint = `${root}/${cleaned}`;
    return endpoint;
  }
  createHeaders(opts, req) {
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json"
    };
    if (this.domain) {
      headers["X-Forwarded-Host"] = this.domain;
      headers["X-Domain"] = this.domain;
    }
    if (this.credentials) {
      const cookie = Object.getOwnPropertyNames(this.credentials).reduce((r, n) => r.length ? `${r};${n}=${this.credentials[n]}` : `${n}=${this.credentials[n]}`, "");
      headers["Cookie"] = cookie;
    }
    if (opts) {
      Object.getOwnPropertyNames(opts).forEach((name) => {
        headers[name] = opts[name];
      });
    }
    if (req.service && typeof this.jwtToken === "string") {
      headers["Authorization"] = `Bearer ${this.jwtToken}`;
    } else if (this.tokenAuth && this.authToken) {
      headers["Authorization"] = `Bearer ${this.authToken}`;
    }
    return headers;
  }
  buildUrl(path, params) {
    const endpoint = this.createEndpoint(path, { service: void 0 });
    return buildUrlWithParams(endpoint, params);
  }
  buildServiceUrl(path, service, params) {
    const endpoint = this.createEndpoint(path, { service });
    return buildUrlWithParams(endpoint, params);
  }
  upload(opts) {
    return new Promise((resolve, reject) => {
      try {
        const xhr = new XMLHttpRequest();
        xhr.timeout = opts.timeout || 3e5;
        xhr.addEventListener("load", async () => {
          try {
            if (xhr.status >= 200 && xhr.status <= 300) {
              const response = JSON.parse(xhr.response);
              resolve(response);
            } else {
              const errorResponse = await parseErrorResponse(xhr);
              const message = getErrorMessage(xhr.status, xhr.statusText, errorResponse);
              reject(createUploadError("server", {
                message,
                status: xhr.status,
                statusText: xhr.statusText,
                response: errorResponse
              }));
            }
          } catch (parseError) {
            reject(createUploadError("parse", {
              message: "Failed to parse server response",
              status: xhr.status,
              statusText: xhr.statusText,
              response: xhr.response
            }));
          }
        });
        xhr.addEventListener("error", () => {
          reject(createUploadError("network", {
            message: "Network error occurred during upload. Please check your connection and try again.",
            status: xhr.status,
            statusText: xhr.statusText
          }));
        });
        xhr.addEventListener("abort", () => {
          reject(createUploadError("abort", {
            message: "Upload was cancelled",
            status: xhr.status,
            statusText: xhr.statusText
          }));
        });
        xhr.addEventListener("timeout", () => {
          reject(createUploadError("timeout", {
            message: "Upload timed out. Please try again or select a smaller file.",
            status: xhr.status,
            statusText: xhr.statusText
          }));
        });
        xhr.withCredentials = true;
        xhr.upload.addEventListener("progress", function(ev) {
          if (ev.lengthComputable) {
            const percent = Math.round(ev.loaded / ev.total * 100);
            if (opts.onProgress)
              opts.onProgress(percent);
          }
        });
        const data = new FormData();
        Object.getOwnPropertyNames(opts).forEach((name) => {
          if (name !== "path" && name !== "onProgress" && name !== "timeout" && opts[name] !== void 0) {
            data.append(name, opts[name]);
          }
        });
        xhr.open("POST", this.createEndpoint(opts.path || "uploads", opts));
        if (this.tokenAuth && this.authToken) {
          xhr.setRequestHeader("Authorization", `Bearer ${this.authToken}`);
        }
        xhr.send(data);
      } catch (e) {
        reject(createUploadError("unknown", {
          message: e instanceof Error ? e.message : "Unknown error occurred during upload setup"
        }));
      }
    });
  }
  async get(opts) {
    return await this.makeRequest({ ...opts, method: "GET" });
  }
  async post(opts) {
    return await this.makeRequest({ ...opts, method: "POST" });
  }
  async delete(opts) {
    return await this.makeRequest({ ...opts, method: "DELETE" });
  }
  async patch(opts) {
    return await this.makeRequest({ ...opts, method: "PATCH" });
  }
  async activityRequest(opts) {
    await this.loadJwt();
    return this.makeRequest({ service: "activity", ...opts });
  }
  async makeRequest(opts) {
    if (opts.description) {
      info("%s: %o", opts.description, opts);
    } else {
      info("makeRequest: %o", opts);
    }
    try {
      if (simulateErrors) {
        const up = localStorage.simulateErrors && parseInt(localStorage.simulateErrors) || -1;
        if (Math.random() <= up / 10) {
          console.error("simulating error for request:", { ...opts });
          throw new Error("Simulated error: " + opts.path);
        }
      }
      const uri = this.createEndpoint(opts.path, opts);
      const response = await fetch(uri, {
        credentials: "include",
        mode: "cors",
        method: opts.method || "GET",
        headers: this.createHeaders(opts.headers, opts),
        body: JSON.stringify(opts.body)
      });
      if (opts.raw) {
        return response;
      }
      if (response.status >= 200 && response.status < 400) {
        if (response.status === 204) {
          return void 0;
        } else {
          return await response.json();
        }
      } else {
        const parsedResponse = await parseApiResponse(response);
        const apiError = await handleApiError(response, parsedResponse);
        throw apiError;
      }
    } catch (err) {
      console.error(err);
      if (err && err.type)
        throw err;
      throw createApiError("network", {
        message: err && err.message ? err.message : "Network error"
      });
    }
  }
  async loadJwt() {
    if (!this.jwtToken || this.expires <= Date.now()) {
      this.expires = void 0;
      this.jwtToken = this.get({
        path: "users/jwt"
      }).then((v) => {
        const token = v.token;
        const parsed = this.parseJwt(token);
        const d = new Date(0);
        d.setUTCSeconds(parsed.exp);
        this.expires = d.getTime();
        this.jwtToken = token;
        return token;
      }).catch((e) => {
        this.jwtToken = void 0;
        console.error(e);
        throw e;
      });
    }
    return this.jwtToken;
  }
  parseJwt(token) {
    var base64Url = token.split(".")[1];
    var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    var jsonPayload = decodeURIComponent(window.atob(base64).split("").map(function(c) {
      return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(""));
    return JSON.parse(jsonPayload);
  }
  async loadInstanceInfo() {
    return await this.get({
      path: "info"
    });
  }
  async loginWithEmail(credentials) {
    return await this.post({
      path: "login/credentials",
      body: credentials
    });
  }
  async loginWithFacebook(fb) {
    return await this.post({
      path: "login/facebook",
      body: fb
    });
  }
  async logout() {
    return await this.post({
      path: "logout"
    });
  }
  async loadProfile(adminInfo = false) {
    let path = "profile";
    if (adminInfo) {
      path += "?admin=1";
    }
    return await this.get({
      path
    });
  }
  async updateProfile(profile) {
    return await this.post({
      path: `profile`,
      body: { profile }
    });
  }
  async loadOnboardingTags() {
    return await this.get({
      path: "onboarding/tags"
    });
  }
  async loadUserByEmail(email) {
    return await this.get({
      path: `users/by-email/${email}`
    });
  }
  async isEmailAvailable(email) {
    try {
      const user = await this.loadUserByEmail(email);
      return !user;
    } catch (err) {
      if (err.code === 404) {
        return true;
      } else {
        return false;
      }
    }
  }
  async loadUserByUsername({ username }) {
    return await this.get({
      path: `users/by-username/${encodeURIComponent(username)}`
    });
  }
  async isUsernameAvailable(username) {
    try {
      return !await this.loadUserByUsername({ username });
    } catch (err) {
      if (err.code === 404) {
        return true;
      } else {
        return false;
      }
    }
  }
  async suggestUsers(opts) {
    let filter = typeof opts === "string" ? opts : opts.filter;
    return await this.get({
      path: `users/suggest/${filter}`
    });
  }
  async registerWithEmail(user) {
    return await this.post({
      path: "users/_new/email",
      body: user
    });
  }
  async registerWithFacebook(fb) {
    return await this.post({
      path: "users/_new/facebook",
      body: fb
    });
  }
  async confirmEmailWithPin({ userId, pin }) {
    return await this.get({
      path: `users/confirm-registration/${userId}/${pin}`
    });
  }
  async confirmEmailWithToken(token) {
    return await this.get({
      path: `users/confirm-registration/${token}`
    });
  }
  async requestPhoneOtp(phone, channel, turnstileToken) {
    return await this.post({
      path: "users/otp/request",
      body: { phone, channel, turnstileToken }
    });
  }
  async verifyPhoneOtp({ phone, code, mobiles, dial }) {
    return await this.post({
      path: "users/otp/verify",
      body: { phone, code, mobiles, dial }
    });
  }
  async refreshSession(refreshToken) {
    return await this.post({
      path: "users/token/refresh",
      body: { refreshToken }
    });
  }
  async revokeRefreshTokens() {
    return await this.post({
      path: "users/token/revoke",
      body: {}
    });
  }
  async webauthnRegisterOptions() {
    return await this.post({ path: "users/webauthn/register/options", body: {} });
  }
  async webauthnRegisterVerify({ response, name }) {
    return await this.post({ path: "users/webauthn/register/verify", body: { response, name } });
  }
  async webauthnCredentials() {
    return await this.get({ path: "users/webauthn/credentials" });
  }
  async webauthnLoginOptions(identifier) {
    return await this.post({ path: "users/webauthn/login/options", body: { identifier } });
  }
  async webauthnLoginVerify({ challengeId, response }) {
    return await this.post({ path: "users/webauthn/login/verify", body: { challengeId, response } });
  }
  async loginWithJwt(token) {
    return await this.post({
      path: "login/jwt",
      body: { token }
    });
  }
  async loginCookies(token) {
    const response = await this.post({
      path: "login/jwt",
      body: { token },
      raw: true
    });
    try {
      const cookies = response.headers["_headers"]["set-cookie"];
      const cookieValues = cookies.reduce((acc, setter) => {
        const first = setter.split(";")[0].split("=");
        const key = first[0];
        const value = first.slice(1).join("=");
        acc[key] = value;
        return acc;
      }, {});
      this.credentials = cookieValues;
    } catch (error) {
      console.error("loginCookies", error);
    }
    return response;
  }
  async forgotPassword(email) {
    return await this.get({
      path: `users/forgot-password/${email}`
    });
  }
  async resetPassword(user) {
    return await this.post({
      path: `users/reset-password`,
      body: { password: user.password }
    });
  }
  async loadDrafts(opts) {
    const filter = opts.filter || "_ALL";
    const range = opts.range || DEFAULT_RANGE;
    return await this.get({
      path: `drafts/${filter}/${range.start}-${range.end}`
    });
  }
  async loadDraft(opts) {
    return await this.get({
      path: `drafts/${opts._id}`
    });
  }
  async loadUnpublishedDraft(opts) {
    return await this.get({
      path: `drafts/unpublished-responses/${opts.responseTo}`
    });
  }
  async upsertDraft(opts) {
    if (opts._id) {
      return await this.post({
        path: `drafts/${opts._id}`,
        body: opts
      });
    } else {
      return await this.post({
        path: `drafts/_new`,
        body: opts
      });
    }
  }
  publishEvent(opts) {
    return this.post({
      path: `events/publish/${opts.id}`,
      body: opts
    });
  }
  async deleteDraft(opts) {
    return await this.delete({
      path: `drafts/${opts._id}`
    });
  }
  async publish(draft) {
    return await this.post({
      path: `drafts/${draft._id}/_publish`,
      body: { draft }
    });
  }
  async loadMemberPosts(opts) {
    const filter = opts.filter || "_ALL";
    const range = opts.range || DEFAULT_RANGE;
    return await this.get({
      path: `users/${opts.author}/posts/${filter}/${range.start}-${range.end}`
    });
  }
  async loadMemberEvents(opts) {
    const filter = opts.filter || "_ALL";
    const range = opts.range || DEFAULT_RANGE;
    return await this.get({
      path: `users/${opts.author}/events/${filter}/${range.start}-${range.end}`
    });
  }
  async loadMemberResponses(opts) {
    const filter = opts.filter || "_ALL";
    const range = opts.range || DEFAULT_RANGE;
    return await this.get({
      path: `users/${opts.author}/responses/${filter}/${range.start}-${range.end}`
    });
  }
  async loadPostByUsernameAndPostName(opts) {
    return await this.get({
      path: `users/${encodeURIComponent(opts.username)}/posts/${encodeURIComponent(opts.name)}`
    });
  }
  async loadPostById(opts) {
    return await this.get({
      path: `posts/${opts._id}`
    });
  }
  async loadPostByDraft(draftId) {
    return await this.get({
      path: `drafts/${draftId}/post`
    });
  }
  async unpublishPost(opts) {
    let path = `posts/${opts._id}`;
    if (opts.as_admin) {
      path = `${path}/${opts.as_admin}`;
    }
    return await this.delete({ path });
  }
  async addZoomToPost(opts) {
    return await this.post({
      path: `posts/${opts.post}/zoom`,
      body: opts
    });
  }
  async createZoomMeeting(opts) {
    return await this.post({
      path: `zoom/meeting`,
      body: opts
    });
  }
  async ratePost(opts) {
    return await this.post({
      path: `posts/${opts.post}/ratings`,
      body: opts
    });
  }
  async concludePost(opts) {
    return await this.post({
      path: `posts/${opts.post}/conclusion`,
      body: opts
    });
  }
  async zoomDraft(opts) {
    return await this.post({
      path: `zoomdraft/${opts.post}`,
      body: opts
    });
  }
  async follow(opts) {
    return await this.post({
      path: `${opts.entityType}/${opts.entityId}/followers/${opts.followerId}`
    });
  }
  async unfollow(opts) {
    return await this.delete({
      path: `${opts.entityType}/${opts.entityId}/followers/${opts.followerId}`
    });
  }
  async isFollowing(opts) {
    return await this.get({
      path: `${opts.entityType}/${opts.entityId}/followers/${opts.followerId}`
    });
  }
  async isFollowingMany(opts) {
    return await this.post({
      path: `${opts.entityType}/_ALL/followers/${opts.followerId}`,
      body: {
        entities: opts.entities
      }
    });
  }
  async followUser(opts) {
    return await this.follow({ ...opts, entityType: "user" });
  }
  async unfollowUser(opts) {
    return await this.unfollow({ ...opts, entityType: "user" });
  }
  async isFollowingUser(opts) {
    return await this.isFollowing({ ...opts, entityType: "user" });
  }
  async followTag(opts, getState) {
    const {
      session: { value: profile }
    } = getState();
    let o = typeof opts === "string" ? { entityId: opts } : opts;
    if (profile) {
      return await this.follow({
        ...o,
        entityType: "tag",
        followerId: profile._id
      });
    } else {
      throw new Error({ code: 401, message: "authentication_required" });
    }
  }
  async unfollowTag(opts, getState) {
    const {
      session: { value: profile }
    } = getState();
    let o = typeof opts === "string" ? { entityId: opts } : opts;
    if (profile) {
      return await this.unfollow({
        ...o,
        entityType: "tag",
        followerId: profile._id
      });
    } else {
      throw new Error({ code: 401, message: "authentication_required" });
    }
  }
  async isFollowingTag(opts) {
    return await this.isFollowing({ ...opts, entityType: "tag" });
  }
  async isFollowingTags(opts) {
    return await this.isFollowingMany({ ...opts, entityType: "tag" });
  }
  async loadActivities(type, opts) {
    let filter = opts.filter || "_ALL";
    let path = `activities/${type}/${filter}`;
    if (opts.limit) {
      if (opts.before) {
        path = `${path}/${opts.limit}/${opts.before}`;
      } else {
        path = `${path}/${opts.limit}`;
      }
    }
    return await this.get({ path });
  }
  async loadFlatActivities(opts) {
    return await this.loadActivities("flat", opts);
  }
  async loadAggregatedActivities(opts) {
    return await this.loadActivities("aggregated", opts);
  }
  async loadGroupActivities(opts) {
    let filter = opts.filter || "_ALL";
    let path = `group-activities/${opts.group}/aggregated/${filter}`;
    if (opts.limit) {
      if (opts.before) {
        path = `${path}/${opts.limit}/${opts.before}`;
      } else {
        path = `${path}/${opts.limit}`;
      }
    }
    return await this.get({ path });
  }
  createPathWithLimitAndBefore(path, opts) {
    if (opts.limit) {
      if (opts.before) {
        return `${path}/${opts.limit}/${opts.before}`;
      } else {
        return `${path}/${opts.limit}`;
      }
    } else {
      return `${path}/${DEFAULT_PAGE_SIZE}`;
    }
  }
  async loadTaggedPosts(opts) {
    const range = opts.range || DEFAULT_RANGE;
    const filter = opts.filter || "_ALL";
    const path = `tagged-posts/${encodeURIComponent(opts.tag)}/${encodeURIComponent(filter)}/${range.start}-${range.end}`;
    return await this.get({ path });
  }
  async loadFilteredPosts(opts) {
    const filter = opts.filter || "_ALL";
    const range = opts.range || DEFAULT_RANGE;
    return await this.get({
      path: `posts/${filter}/${range.start}-${range.end}`
    });
  }
  async loadFilteredEvents(opts) {
    const filter = opts.filter || "_ALL";
    const range = opts.range || DEFAULT_RANGE;
    return await this.get({
      path: `events/${filter}/${range.start}-${range.end}`
    });
  }
  async loadInterestingPosts(opts) {
    const filter = opts.filter || "_ALL";
    const range = opts.range || DEFAULT_RANGE;
    return await this.get({
      path: `interesting-posts/${filter}/${range.start}-${range.end}`
    });
  }
  async loadInterestingEvents(opts) {
    const filter = opts.filter || "_ALL";
    const range = opts.range || DEFAULT_RANGE;
    return await this.get({
      path: `interesting-events/${filter}/${range.start}-${range.end}`
    });
  }
  async loadNotifications(opts) {
    return await this.get({
      path: this.createPathWithLimitAndBefore("notifications", opts)
    });
  }
  async loadResponses(opts) {
    const range = opts.range || DEFAULT_RANGE;
    return this.get({
      path: `posts/${opts._id}/responses/${opts.depth || 1}/${range.start}-${range.end}` + buildQueryString({ lang: opts.language })
    });
  }
  async findInterestingPeople({
    filter,
    before = "_ALL",
    limit = DEFAULT_PAGE_SIZE
  } = { filter: "_ALL" }) {
    const f = filter;
    return this.get({ path: `users/interesting/${f}/${before}-${limit}` });
  }
  async findIconsForTags(tags) {
    return this.post({
      path: "icons",
      body: {
        tags
      }
    });
  }
  async upsertGroup(opts) {
    if (opts._id) {
      return await this.post({
        path: `groups/${opts._id}`,
        body: opts
      });
    } else {
      return await this.post({
        path: `groups/_new`,
        body: opts
      });
    }
  }
  async loadGroup(opts) {
    return await this.get({
      path: `groups/${opts._id}`
    });
  }
  async closeGroup(opts) {
    let path = `groups/${opts.group}/_close`;
    return await this.post({
      path,
      body: {
        outcome: opts.outcome
      }
    });
  }
  async reOpenGroup(opts) {
    let path = `groups/${opts.group}/_reopen`;
    return await this.post({ path });
  }
  async increasePrivacy(opts) {
    let path = `groups/${opts.group}/_increase_privacy`;
    return await this.post({ path });
  }
  async updateEndUserDigestFrequency(opts) {
    let path = `groups/${opts.group}/_update_content_digest_frequency`;
    return await this.post({
      path,
      body: {
        frequency: opts.frequency
      }
    });
  }
  async pinIntroToGroup(opts) {
    return await this.post({
      path: `groups/${opts.group}/_intro`,
      body: {
        post: opts.post
      }
    });
  }
  async loadJoinedGroups(opts) {
    let path = `groups/joined`;
    if (opts.access) {
      path = `${path}/${opts.access}`;
    } else {
      path = `${path}/_ALL`;
    }
    if (opts.status) {
      path = `${path}/${opts.status}`;
    } else {
      path = `${path}/_ALL`;
    }
    let range = opts.range || DEFAULT_RANGE;
    let filter = opts.filter || "_ALL";
    let fullPath = `${path}/${filter}/${range.start}-${range.end}`;
    return await this.get({
      path: fullPath
    });
  }
  async joinGroup(opts) {
    return await this.post({
      path: `groups/${opts._id}/members/_new`
    });
  }
  async leaveGroup(opts) {
    return await this.delete({
      path: `groups/${opts._id}/members`
    });
  }
  async discardInvitation(opts) {
    return await this.delete({
      path: `groups/${opts._id}/invitations/_discard`
    });
  }
  async loadAvailableGroups(opts = {}) {
    let path = `groups/discover`;
    let range = opts.range || DEFAULT_RANGE;
    let filter = opts.filter || "_ALL";
    return await this.get({
      path: `${path}/${filter}/${range.start}-${range.end}`
    });
  }
  async loadGroupPosts(opts) {
    const { intro, noIntro, language, chat } = opts;
    const main = `groups/${opts.group}/${opts.events ? "events" : "posts"}`;
    const range = opts.range || DEFAULT_RANGE;
    const filter = opts.filter || "_ALL";
    let path = `${main}/${filter}/${range.start}-${range.end}` + buildQueryString({ intro, noIntro, lang: language, chat });
    return await this.get({
      path
    });
  }
  async loadReferencesForGroup(opts) {
    let path = `groups/${opts.group}/references`;
    let range = opts.range || DEFAULT_RANGE;
    let filter = opts.filter || "_ALL";
    return await this.get({
      path: `${path}/${filter}/${range.start}-${range.end}`
    });
  }
  async loadSubGroups(opts) {
    let path = `groups/${opts.group}/groups`;
    let range = opts.range || DEFAULT_RANGE;
    let filter = opts.filter || "_ALL";
    return await this.get({
      path: `${path}/${filter}/${range.start}-${range.end}`
    });
  }
  async findUsersToInvite(opts) {
    let path = `groups/${opts.group}/non-members/${opts.filter}`;
    return await this.get({ path });
  }
  async invite(opts) {
    let path = `groups/${opts.group}/invitations`;
    return await this.post({
      path,
      body: {
        invitees: opts.invitees
      }
    });
  }
  async loadMembers(opts) {
    let path = `groups/${opts.group}/members`;
    let range = opts.range || DEFAULT_RANGE;
    let filter = opts.filter || "_ALL";
    return await this.get({
      path: `${path}/${filter}/${range.start}-${range.end}`
    });
  }
  async loadInvites(opts) {
    let path = `groups/${opts.group}/invitations`;
    let range = opts.range || DEFAULT_RANGE;
    let filter = opts.filter || "_ALL";
    return await this.get({
      path: `${path}/${filter}/${range.start}-${range.end}`
    });
  }
  async requestGroupAccess(opts) {
    let path = `groups/${opts.group}/requests`;
    return await this.post({ path });
  }
  async loadAccessRequests(opts) {
    let filter = opts.filter || "_ALL";
    let range = opts.range || DEFAULT_RANGE;
    return await this.get({
      path: `groups/${opts.group}/requests/${filter}/${range.start}-${range.end}`
    });
  }
  async grantAccessRequest(opts) {
    return await this.post({
      path: `groups/${opts.group}/requests/_accept/${opts.from._id}`
    });
  }
  async denyAccessRequest(opts) {
    return await this.post({
      path: `groups/${opts.group}/requests/_reject/${opts.from._id}`
    });
  }
  async updateMembership({ group, user, update }) {
    let path = `groups/${group}/members/${user}`;
    return await this.post({
      path,
      body: update
    });
  }
  async suggestTags(opts) {
    let filter = opts.filter || "_ALL";
    let language = opts.language || "en";
    let range = opts.range || DEFAULT_RANGE;
    return await this.get({
      path: `tags/${language}/suggest/${filter}/${range.start}-${range.end}`
    });
  }
  async suggestPosts(opts) {
    let filter = opts.filter || "_ALL";
    let language = opts.language || "en";
    let range = opts.range || DEFAULT_RANGE;
    return await this.get({
      path: `posts/${language}/suggest/${filter}/${range.start}-${range.end}`
    });
  }
  async loadReactions(opts) {
    return await this.post({
      path: `reactions`,
      body: {
        ids: opts.ids
      }
    });
  }
  async updateReaction(opts) {
    return await this.post({
      path: `reactions/${opts.post}`,
      body: {
        reactions: opts.reactions
      }
    });
  }
}

var PostFeatures;
(function(PostFeatures2) {
  PostFeatures2["PAGE"] = "page";
  PostFeatures2["BOOK"] = "book";
})(PostFeatures || (PostFeatures = {}));

var CircleStatTypes;
(function(CircleStatTypes2) {
  CircleStatTypes2[CircleStatTypes2["Members"] = 0] = "Members";
  CircleStatTypes2[CircleStatTypes2["Posts"] = 1] = "Posts";
})(CircleStatTypes || (CircleStatTypes = {}));

const isLocalhost = () => {
  if (typeof location === "undefined") {
    return false;
  }
  const host = location.host;
  return host.startsWith("localhost") || host.startsWith("192.");
};

const getPersonName = (person = {}, context = {}) => {
  if (!person || typeof person !== "object") {
    return "---";
  }
  const { username, firstname, lastname } = person;
  let name = firstname && lastname ? `${firstname} ${lastname}` : firstname || lastname || username;
  if (context.addUserName && name !== username) {
    name += ` (${username})`;
  }
  if (!name) {
    return "---";
  }
  return name;
};

const htmlElementText = (el) => {
  return (el.textContent || el.innerText || "").trim();
};

const html2text = (content) => {
  const tempDivElement = document.createElement("div");
  tempDivElement.innerHTML = content;
  return htmlElementText(tempDivElement);
};

const aiAnswerIntro = ({
  content,
  profile
}) => {
  return `<div class="ai-answer-intro">'${html2text(content)}'. In response to the above message by '${getPersonName(profile)}':</div>`;
};

const swrLoadOnce = {
  revalidateOnMount: true,
  revalidateOnFocus: false,
  revalidateOnReconnect: false
};

const useCircle = (circleId, options) => {
  const api = useApi();
  return useSWR(circleId ? `/groups/${circleId}` : null, async () => {
    const data = await api.loadGroup({
      _id: circleId
    });
    return data;
  }, {
    ...swrLoadOnce,
    ...options
  });
};
const useSubCircles = (circleId, options) => {
  const api = useApi();
  return useSWR(circleId ? `/groups/${circleId}/groups/` : null, async () => {
    const result = await api.loadSubGroups({
      group: circleId
    });
    return result.values;
  }, {
    ...swrLoadOnce,
    ...options
  });
};
const useInstance = (options) => {
  const api = useApi();
  return useSWR(`/info`, () => api.loadInstanceInfo(), {
    ...swrLoadOnce,
    ...options
  });
};
const useAccessRequests = (circleId, options) => {
  const api = useApi();
  return useSWR(circleId ? `/groups/${circleId}/requests` : null, async () => {
    const result = await api.loadAccessRequests({
      group: circleId
    });
    return result.values;
  }, {
    ...swrLoadOnce,
    ...options
  });
};

const useProfile = (op) => {
  const api = useApi();
  const result = useSWR(`/profile`, () => api.loadProfile(), {
    ...swrLoadOnce,
    ...op
  });
  return result;
};

const noop$4 = () => null;
const CircleContext = React.createContext({
  id: void 0,
  circle: void 0,
  postsFilter: "all",
  setPostsFilter: noop$4,
  profile: void 0,
  requests: void 0,
  membership: void 0,
  invitation: void 0,
  requestAccess: false,
  setRequestAccess: noop$4,
  refresh: noop$4,
  error: void 0,
  event$: {
    emit: noop$4,
    useSubscription: noop$4
  },
  loading: false,
  testPromptDialogOpen: null,
  setTestPromptDialogOpen: noop$4,
  instance: {},
  initialPosts: null
});
const useCircleContext = () => {
  return useContext(CircleContext);
};

const CircleContextProvider = ({
  children,
  id,
  circle,
  profile,
  requests,
  membership,
  invitation,
  error,
  event$,
  loading,
  instance,
  initialPosts
}) => {
  const [testPromptDialogOpen, setTestPromptDialogOpen] = useState(null);
  const [requestAccess, setRequestAccess] = useState(false);
  const [postsFilter, setPostsFilter] = useState("all");
  if (!circle) {
    return null;
  }
  const refresh = () => {
    window.location.reload();
  };
  const context = {
    id,
    postsFilter,
    setPostsFilter,
    showNotifications: true,
    circle,
    profile,
    requests,
    membership,
    invitation,
    requestAccess,
    setRequestAccess,
    refresh,
    error,
    event$,
    loading,
    testPromptDialogOpen,
    setTestPromptDialogOpen,
    instance,
    initialPosts
  };
  return /* @__PURE__ */ jsx(CircleContext.Provider, {
    value: context,
    children
  });
};

const CIRCLE_EVENT_REFRESH = "refresh";

const LoadCircleData = ({
  circleId,
  initial = {},
  children,
  initialPosts,
  swrOptions
}) => {
  if (initial.group) {
    initial.circle = initial.group;
  }
  const {
    data: circleData,
    error,
    mutate
  } = useCircle(circleId, {
    ...swrOptions,
    onSuccess() {
      setLoading(false);
    },
    fallbackData: initial
  });
  const {
    data: profile
  } = useProfile({
    ...swrOptions,
    fallbackData: initial.profile
  });
  const {
    data: requests
  } = useAccessRequests(circleId, {
    ...swrOptions,
    fallbackData: initial.requests
  });
  const {
    data: instance
  } = useInstance({
    ...swrOptions,
    fallbackData: initial.instance
  });
  const event$ = useEventEmitter();
  const [loading, setLoading] = useState(!initial);
  event$.useSubscription((event) => {
    switch (event.type) {
      case CIRCLE_EVENT_REFRESH: {
        setLoading(true);
        mutate();
        break;
      }
    }
  });
  return /* @__PURE__ */ jsx(CircleContextProvider, {
    id: circleId,
    circle: circleData && circleData.group,
    invitation: circleData && circleData.invitation,
    membership: circleData && circleData.membership,
    profile,
    requests,
    error,
    event$,
    loading,
    instance,
    initialPosts,
    children
  });
};

const LanguageSelectionContext = React.createContext(void 0);
const useLanguageSelectionContext = () => {
  const context = useContext(LanguageSelectionContext);
  if (context === void 0) {
    throw new Error("useLanguageSelectionContext must be used within a LanguageSelectionContextProvider");
  }
  return context;
};
const isValidLanguageCode = (value) => {
  return typeof value === "string" && (value.length === 2 || value.length === 3);
};
const LanguageSelectionContextProvider = ({
  children
}) => {
  const [language, setLanguageRaw] = useLocalStorageState("lang", {
    defaultValue: "",
    serializer: (value) => value,
    deserializer: (value) => {
      if (isValidLanguageCode(value)) {
        return value;
      }
      return "";
    }
  });
  const setLanguage = (lang) => {
    if (!lang || !isValidLanguageCode(lang)) {
      setLanguageRaw(void 0);
    } else {
      setLanguageRaw(lang);
    }
  };
  const context = useMemo(() => ({
    language: language || "",
    setLanguage
  }), [language]);
  return React.createElement(LanguageSelectionContext.Provider, {
    value: context
  }, children);
};

const EMPTY_RANGE = {
  start: 0,
  end: 0,
  total: 0
};
const listRequest2Redux = (result, state = {}) => {
  const { values = [], range = EMPTY_RANGE, extra } = result;
  return {
    ...state,
    map: Object.assign({}, state.map, mapById(values)),
    list: {
      loading: false,
      values: values.map((p) => p._id || p.id),
      range
    },
    extra
  };
};
const mapById = (list) => list.reduce((r, p) => {
  r[p._id || p.id] = p;
  return r;
}, {});

const extractPosts = (posts) => {
  const {
    map: content,
    list
  } = posts;
  const {
    values: order
  } = list || {};
  return {
    content,
    order,
    intro: false,
    pinned: []
  };
};
const loadCirclePosts = async (api, circleId, filter = "", options = {}) => {
  const opts = {
    group: circleId,
    filter,
    ...options
  };
  const result = await api.loadGroupPosts(opts);
  const responsesRequestResult = extractPosts(listRequest2Redux(result));
  responsesRequestResult.intro = result.intro;
  responsesRequestResult.pinned = result.pinned;
  return responsesRequestResult;
};
const loadCircleReferences = async (api, circleId, filter = "") => {
  const opts = {
    group: circleId,
    filter
  };
  const result = await api.loadReferencesForGroup(opts);
  const responsesRequestResult = extractPosts(listRequest2Redux(result));
  return responsesRequestResult;
};

const isPage = (post) => {
  if (!post || !post.features) {
    return false;
  }
  return post.features.includes(PostFeatures.PAGE) || typeof post.documentPageNumber === "number";
};

const isLocalPlayground = () => {
  if (typeof location === "undefined") {
    return false;
  }
  if (!isLocalhost()) {
    return false;
  }
  if (location.port !== "3030") {
    return false;
  }
  return true;
};

const getResponseUrl = (response, noPrefix = false) => {
  if (typeof response === "string") {
    return `/trans/post-view/${response}`;
  }
  if (!response.group) {
    return `/trans/post-view/${response._id}`;
  }
  const prefix = getUrlPrefix(noPrefix);
  const circle = response.group.group && response.group.group._id || response.group.group;
  if (!circle) {
    return `/trans/post-view/${response._id}`;
  }
  const start = isLocalPlayground() ? "/playground/lib/single-circle/post-view" : `${prefix}/groups/${circle}/posts/post`;
  if (!response.thread || !response.thread.length) {
    return `${start}/${response._id}`;
  }
  const isPagePost = isPage(response);
  if (isPagePost) {
    return `${start}/${response.thread[0]}#?page=${response._id}`;
  }
  return `${start}/${response.thread[0]}#?response=${response._id}`;
};
const getUrlPrefix = (noPrefix = false) => {
  if (noPrefix) {
    return "";
  }
  const isCore = checkIsCoreUrl();
  return isCore ? LOCAL_PLAYGROUND.replace(":3030", ":3000") : "";
};
const checkIsCoreUrl = () => location.href.startsWith(LOCAL_PLAYGROUND);
const LOCAL_PLAYGROUND = "http://localhost:3030";

const circleIdForPost = (post) => {
  if (!post || !post.group || !post.group.group) {
    return void 0;
  }
  const g = post.group.group;
  return g._id || g;
};

const createPostUrl = (id) => `/profile/drafts/_create/article/${id}`;
const editPostUrl = (post) => {
  if (post.pages && post.pages.length) {
    return `/trans/edit-post/${post.draft}`;
  }
  return `/profile/drafts/_edit/${post.type}/${post.draft}`;
};
const postAudienceUrl = (post) => {
  return editPostUrl(post) + "/audience";
};
const objectId = (v) => !v ? v : v._id || v;
const importDocumentUrl = (id) => `/profile/drafts/_import/${id}`;
const circleUrl = (id) => {
  if (!id) {
    id = "";
  }
  if (typeof id !== "string") {
    id = id._id;
  }
  if (isLocalPlayground()) {
    return "/playground/lib/single-circle/design/" + id;
  }
  return `/groups/${id}`;
};
const myCirclesUrl = () => {
  if (isLocalPlayground()) {
    return "/playground/lib/single-circle/my-circles/";
  }
  return `/groups`;
};
const dashboardUrl = () => {
  if (isLocalPlayground()) {
    return "/playground/lib/single-circle/dashboard";
  }
  return `/`;
};
const postCircleUrl = (post) => circleUrl(postCircleId(post));
const circleMembersUrl = (id) => `/groups/${id}/membership`;
const postCircleId = (post) => {
  if (!post || !post.group || !post.group.group) {
    return "";
  }
  const group = post.group.group;
  if (group._id) {
    return group._id;
  }
  if (typeof group === "string") {
    return group;
  }
  return "";
};
const circlePostUrl = ({ circleId, postId }) => {
  if (isLocalPlayground()) {
    return "/playground/lib/single-circle/post-view/" + postId;
  }
  return postUrl({ circleId, postId });
};
const postUrl = ({ circleId, postId }) => `/groups/${circleId}/posts/post/${postId}`;
const bookCairPath = (seg, suffix = "") => isLocalPlayground() ? `/playground/lib/single-circle/${seg}${suffix}` : `/${seg}${suffix}`;
const bookCairBrowseUrl = (circleId) => bookCairPath("book-cair-browse", `/${circleId}`);
const bookCairProfileUrl = () => bookCairPath("book-cair-profile");
const bookCairLibraryUrl = () => bookCairPath("book-cair-library");
const bookCairPurchasesUrl = () => bookCairPath("book-cair-purchases");
const bookCairDashboardUrl = (circleId) => bookCairPath("book-cair-dashboard", circleId ? `/${circleId}` : "");
const bookCairPaymentUrl = (query = "") => bookCairPath("book-cair-payment", query);
const bookCairPaymentResultUrl = (query = "") => bookCairPath("book-cair-payment-result", query);
const bookCairDesktopUrl = (postId) => bookCairPath("live-book-cair-desktop", `/${postId}`);
const bookCairMobileUrl = (postId) => bookCairPath("live-book-cair-mobile", `/${postId}`);
const bookCairReaderUrl = (postId) => typeof window !== "undefined" && window.innerWidth < 768 ? bookCairMobileUrl(postId) : bookCairDesktopUrl(postId);
const url4post = (post) => {
  if (!post) {
    return "/";
  }
  return getResponseUrl(post, true);
};
const eventCalendarLink = (post) => `/ua/event/calendar/${post._id}/calendar.ics`;

let counter = 0;
const useAssistant = (id, options) => {
  const api = useApi();
  const result = useSWR(id ? `/group-assistant/${id}` : null, async (path) => {
    const data = await api.get({
      path
    });
    counter++;
    return { ...data, counter };
  }, {
    ...swrLoadOnce,
    ...options
  });
  return {
    ...result
  };
};

const useAssistants = (circleId, options) => {
  const api = useApi();
  const result = useSWR(circleId ? `/groups/${circleId}/assistants/` : null, async (path) => {
    const data = await api.get({
      path
    });
    return data;
  }, {
    ...swrLoadOnce,
    ...options
  });
  return {
    ...result
  };
};

React.createContext({
  socket: null
});

createLogger("info:ui");

createLogger("info:ui");
const noop$3 = () => null;
const ActivityContext = React.createContext({
  activities$: {
    emit: noop$3,
    useSubscription: noop$3
  }
});
const useActivityContext = () => {
  return useContext(ActivityContext);
};

const MAX_POSTS = 25;
const isPermissionError = (error) => {
  if (!error)
    return false;
  const status = error.status;
  return status === 403 || status === 401;
};
const data2posts = (data) => {
  const {
    content,
    order
  } = data;
  const posts = [];
  for (let i = 0; i < order.length; i++) {
    posts[i] = content[order[i]];
  }
  return posts;
};
const mappedPinned = (data) => {
  const {
    pinned
  } = data;
  if (!pinned) {
    return {};
  }
  const result = {};
  for (const id of pinned) {
    result[id] = true;
  }
  return result;
};
const useCirclePostsState = (circleId, options) => {
  const {
    chat,
    chatId,
    limit
  } = options || {};
  const [state, setState] = useSetState({
    moreLoading: false,
    loadMoreIndex: -1,
    intro: null,
    pinned: {}
  });
  const maxPosts = typeof limit === "number" ? limit : MAX_POSTS;
  const {
    loadMoreIndex,
    moreLoading,
    intro
  } = state;
  const firstLoaded = loadMoreIndex >= maxPosts;
  const [posts, setPosts] = useState();
  const [more, setMore] = useState([]);
  const {
    circle
  } = useCircleContext();
  const {
    language
  } = useLanguageSelectionContext();
  const api = useApi();
  const deletePost = useMemoizedFn((post) => {
    const idPosts = posts.findIndex((p) => {
      return p._id === post._id;
    });
    const idMore = more.findIndex((p) => {
      return p._id === post._id;
    });
    const notFound = idPosts === -1 && idMore === -1;
    if (notFound) {
      return;
    }
    const all2 = idPosts !== -1 ? posts : more;
    const setAll = idPosts !== -1 ? setPosts : setMore;
    setAll(all2.filter((p) => {
      if (p._id === post._id) {
        return false;
      }
      return true;
    }));
  });
  const togglePin = useMemoizedFn(async (id) => {
    const pinned = {
      ...state.pinned
    };
    const alreadyPinned = !!pinned[id];
    await api.post({
      path: alreadyPinned ? "/posts/pin/remove" : "/posts/pin",
      body: {
        ids: [id],
        group: circleId
      }
    });
    if (pinned[id]) {
      delete pinned[id];
    } else {
      pinned[id] = true;
    }
    setState({
      ...state,
      pinned
    });
  });
  const updatePost = useMemoizedFn((post) => {
    const idPosts = posts.findIndex((p) => {
      return p._id === post._id;
    });
    const idMore = more.findIndex((p) => {
      return p._id === post._id;
    });
    const notFound = idPosts === -1 && idMore === -1;
    if (notFound) {
      setPosts([post, ...posts]);
      return;
    }
    const all2 = idPosts !== -1 ? posts : more;
    const setAll = idPosts !== -1 ? setPosts : setMore;
    setAll(all2.map((p) => {
      if (p._id === post._id) {
        return post;
      }
      return p;
    }));
  });
  const load = async ({
    startIndex,
    stopIndex,
    noIntro
  }) => {
    if (!circleId) {
      return {
        content: {},
        order: [],
        pinned: [],
        intro: false
      };
    }
    const data = await loadCirclePosts(api, circleId, "", {
      intro: !noIntro && circle && circle.intro,
      noIntro,
      language,
      range: {
        start: startIndex + 1,
        end: stopIndex + 1
      },
      chat
    });
    return data;
  };
  useUpdateEffect(() => {
    if (isPermissionError(loadError))
      return;
    loadFirstRows();
  }, [language]);
  const {
    run: loadFirstRows,
    error: loadError,
    cancel: cancelLoadFirstRows
  } = useRequest(async () => {
    const range = {
      startIndex: 0,
      stopIndex: maxPosts - 1
    };
    const data = await load(range);
    const posts2 = data2posts(data);
    setState(({
      loadMoreIndex: loadMoreIndex2
    }) => {
      return {
        intro: data.intro && posts2[0] && posts2[0]._id,
        loadMoreIndex: Math.max(data.intro ? posts2.length - 1 : posts2.length, loadMoreIndex2),
        pinned: mappedPinned(data)
      };
    });
    setPosts(posts2);
  }, {
    manual: true,
    retryCount: -1,
    onError: (error) => {
      if (isPermissionError(error))
        cancelLoadFirstRows();
    }
  });
  const {
    run: loadMore
  } = useRequest(async () => {
    if (!firstLoaded) {
      loadFirstRows();
      return;
    }
    setState({
      moreLoading: true
    });
    const range = {
      startIndex: loadMoreIndex,
      stopIndex: loadMoreIndex + maxPosts - 1,
      noIntro: circle && circle.intro
    };
    const data = await load(range);
    const posts2 = data2posts(data);
    setState(({
      loadMoreIndex: loadMoreIndex2
    }) => {
      return {
        moreLoading: false,
        loadMoreIndex: loadMoreIndex2 + posts2.length,
        pinned: mappedPinned(data)
      };
    });
    setMore((prev) => [...prev, ...posts2]);
  }, {
    manual: true,
    retryCount: -1
  });
  useCreation(() => {
    if (!circle || !circleId) {
      return;
    }
    loadFirstRows();
  }, [!!circle, circleId]);
  const {
    activities$
  } = useActivityContext();
  const {
    run: loadFirstRowsThrottled
  } = useThrottleFn(async () => {
    loadFirstRows();
  }, {
    wait: 5e3
  });
  activities$.useSubscription((message) => {
    if (isPermissionError(loadError))
      return;
    const {
      type,
      circleId: circleId2
    } = message;
    if (type === "post" && !chat) {
      loadFirstRowsThrottled();
      return;
    }
    if (!chat) {
      return;
    }
    if (type === "chat" && circleId2 === chatId) {
      loadFirstRowsThrottled();
    }
  });
  const all = useCreation(() => {
    if (!posts) {
      return [];
    }
    const ids = {};
    return [...posts, ...more].filter((p) => {
      const id = p._id;
      if (ids[id]) {
        return false;
      }
      ids[id] = true;
      return true;
    });
  }, [posts, more]);
  return {
    posts,
    postsLoaded: !!posts,
    updatePost,
    deletePost,
    circle,
    state,
    firstLoaded,
    moreLoading,
    loadMore,
    more,
    all,
    intro,
    togglePin,
    loadError
  };
};
const noop$2 = () => null;
const CirclePostsStateContext = React.createContext({
  posts: void 0,
  postsLoaded: false,
  updatePost: noop$2,
  circle: void 0,
  state: {},
  firstLoaded: false,
  moreLoading: false,
  loadMore: noop$2,
  deletePost: noop$2,
  more: [],
  all: [],
  intro: void 0,
  togglePin: noop$2,
  loadError: void 0
});
const useCirclePostsStateContext = () => {
  return useContext(CirclePostsStateContext);
};
const CirclePostsStateContextProvider = ({
  children,
  state
}) => {
  return /* @__PURE__ */ jsx(CirclePostsStateContext.Provider, {
    value: state,
    children
  });
};

const canUseDOM = !!(typeof window !== "undefined" && window.document && window.document.createElement);

const isMobileWrapper = () => {
  const userAgent = typeof navigator !== "undefined" && navigator.userAgent;
  const isMobile = userAgent && /NewsSocial/i.test(userAgent) || typeof window !== "undefined" && window.isNativeApp;
  return !!isMobile;
};
const isExpoWrapper = () => {
  const isMobile = typeof window !== "undefined" && window.isNativeApp;
  return !!isMobile;
};

const errorMessage = (e, count = 0) => {
  if (!e) {
    return "<unknown>";
  }
  if (typeof e === "string") {
    try {
      e = JSON.parse(e);
    } catch (err) {
      return e;
    }
  }
  if (e.message) {
    return e.message;
  }
  if (e.errorMessage) {
    return e.errorMessage;
  }
  if (e.error && count < 5) {
    return errorMessage(e.error, count + 1);
  }
  try {
    return JSON.stringify(e);
  } catch (err) {
    return e;
  }
};

const usePostAssets = (postId, options) => {
  const api = useApi();
  const result = useSWR(postId ? `/posts/${postId}/asset/` : null, async (path) => {
    const data = await api.get({
      path
    });
    return data;
  }, {
    ...swrLoadOnce,
    ...options
  });
  return {
    ...result
  };
};

const usePost = (postId, options) => {
  const api = useApi();
  const result = useSWR(postId ? `/posts/${postId}` : null, async (path) => {
    const data = await api.loadPostById({
      _id: postId
    });
    return data;
  }, {
    ...swrLoadOnce,
    ...options
  });
  return {
    ...result
  };
};

const noop$1 = () => null;
const RegistrationContext = React.createContext({
  pin: null,
  state: "main",
  setState: noop$1,
  userId: null,
  token: null,
  setUserId: noop$1
});
const useRegistrationContext = () => {
  return useContext(RegistrationContext);
};
const MemoryRegistrationContextProvider = ({
  children,
  initialState
}) => {
  const [state, setState] = useState(initialState || "main");
  const [userId, setUserIdRaw] = useState();
  const setUserId = useMemoizedFn((id) => {
    setUserIdRaw(id || void 0);
    if (id) {
      setState("verify");
    }
  });
  const context = useMemo(() => {
    return {
      state,
      setState,
      userId,
      setUserId,
      token: null,
      pin: null
    };
  }, [state, userId]);
  return /* @__PURE__ */ jsx(RegistrationContext.Provider, {
    value: context,
    children
  });
};
const UrlRegistrationContextProvider = ({
  children,
  initialState
}) => {
  const [userId, setUserIdRaw] = useState();
  const params = useQueryStringParams();
  const [token, setToken] = useState();
  const [state, setStateRaw] = useState(initialState || "main");
  const setState = useMemoizedFn((name) => {
    setStateRaw(name);
    setQueryStringParams({
      ...params,
      state: name,
      userId
    });
  });
  const setUserId = useMemoizedFn((id) => {
    setQueryStringParams({
      ...params,
      state: id ? "verify" : state,
      userId: id || void 0
    });
  });
  useEffect(() => {
    const value = params.state || initialState || "main";
    if (value === state) {
      return;
    }
    setStateRaw(value);
  }, [params.state]);
  useEffect(() => {
    const value = params.token || "";
    setToken(value);
  }, [params.token]);
  useEffect(() => {
    const value = params.userId || void 0;
    if (value === state) {
      return;
    }
    setUserIdRaw(value || void 0);
  }, [params.userId]);
  const context = useMemo(() => {
    return {
      state,
      setState,
      userId,
      setUserId,
      token,
      pin: params.pin
    };
  }, [state, userId, token, params.pin]);
  return /* @__PURE__ */ jsx(RegistrationContext.Provider, {
    value: context,
    children
  });
};
const RegistrationContextProvider = ({
  children,
  syncToUrl = true,
  initialState
}) => {
  if (!syncToUrl) {
    return /* @__PURE__ */ jsx(MemoryRegistrationContextProvider, {
      initialState,
      children
    });
  }
  return /* @__PURE__ */ jsx(UrlRegistrationContextProvider, {
    initialState,
    children
  });
};

const noop = () => null;
const ToastContext = createContext({
  toasts: [],
  addToast: noop,
  removeToast: noop,
  clearAllToasts: noop
});
const useToast = () => {
  const context = useContext(ToastContext);
  return context;
};

const useAsyncOperation = (operation, defaultOptions) => {
  const { addToast } = useToast();
  const [state, setState] = useState({
    isLoading: false,
    error: null,
    isSuccess: false,
    data: null
  });
  const [lastOperation, setLastOperation] = useState(null);
  const execute = useMemoizedFn(async (arg, options) => {
    if (!options) {
      options = defaultOptions || {};
    }
    const {
      showSuccessToast = false,
      showErrorToast = true,
      successTitle = "Success",
      errorTitle = "Error",
      onSuccess,
      onError,
      retryable = false
    } = options;
    if (retryable) {
      setLastOperation({ arg, options });
    }
    setState((prev) => ({
      ...prev,
      isLoading: true,
      error: null,
      isSuccess: false
    }));
    try {
      const result = await operation(arg);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        isSuccess: true,
        data: result,
        error: null
      }));
      if (showSuccessToast) {
        const message = typeof showSuccessToast === "string" ? showSuccessToast : "Operation completed successfully";
        addToast({
          type: "success",
          title: successTitle,
          message
        });
      }
      if (onSuccess) {
        onSuccess(result);
      }
      return result;
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorObj,
        isSuccess: false
      }));
      if (showErrorToast) {
        const message = typeof showErrorToast === "string" ? showErrorToast : errorObj.message || "An unexpected error occurred";
        addToast({
          type: "error",
          title: errorTitle,
          message,
          duration: retryable ? 0 : void 0,
          action: retryable ? {
            label: "Retry",
            onClick: () => retry()
          } : void 0
        });
      }
      if (onError) {
        onError(errorObj);
      }
      throw error;
    }
  });
  const retry = useCallback(async () => {
    if (!lastOperation) {
      console.warn("No operation to retry");
      return void 0;
    }
    return execute(lastOperation.arg, lastOperation.options);
  }, [lastOperation, execute]);
  const reset = useCallback(() => {
    setState({
      isLoading: false,
      error: null,
      isSuccess: false,
      data: null
    });
    setLastOperation(null);
  }, []);
  return {
    ...state,
    execute,
    reset,
    retry
  };
};

const useAutoJoinCircleFn = () => {
  const api = useApi();
  return async () => {
    if (localStorage.joinCircle) {
      return api.joinGroup({
        _id: localStorage.joinCircle
      }).then(() => {
        delete localStorage.joinCircle;
      });
    }
  };
};

let onLoginUrl;
let onLogin;
const AUTH_HASH_KEYS = ["state", "token", "pin", "userId"];
const stripAuthHash = (url) => {
  const hashIndex = url.indexOf("#");
  if (hashIndex === -1) {
    return url;
  }
  const base = url.slice(0, hashIndex);
  const hash = url.slice(hashIndex + 1);
  const queryIndex = hash.indexOf("?");
  if (queryIndex === -1) {
    return url;
  }
  const route = hash.slice(0, queryIndex);
  const params = getQueryStringParams(hash.slice(queryIndex + 1));
  AUTH_HASH_KEYS.forEach((key) => delete params[key]);
  const query = object2QueryString(params);
  if (!route && !query) {
    return base;
  }
  return `${base}#${route}${query ? "?" + query : ""}`;
};
const useSetOnLoginUrl = () => {
  onLoginUrl = typeof window !== "undefined" && stripAuthHash(window.location.href);
};
const useSetOnLoginAction = (f) => {
  onLogin = f;
};
const runOnLogin = async (result) => {
  if (onLogin) {
    try {
      await onLogin(result);
    } finally {
      onLogin = null;
    }
  }
};
const openUrl = (url) => {
  const target = stripAuthHash(url);
  if (stripAuthHash(window.location.href) === target) {
    window.history.replaceState(null, "", target);
    window.location.reload();
    return;
  }
  window.location.href = target;
};
const useOnLoginSuccess = () => {
  const autoJoinCircle = useAutoJoinCircleFn();
  return async ({ location = "/", result } = {}) => {
    await runOnLogin(result);
    try {
      await autoJoinCircle();
    } catch (e) {
    }
    if (onLoginUrl) {
      openUrl(onLoginUrl);
      onLoginUrl = void 0;
      return;
    }
    openUrl(location);
  };
};

const OrRuler = () => {
  return /* @__PURE__ */ jsxs("div", {
    className: "w-full flex items-center justify-center my-4",
    children: [/* @__PURE__ */ jsx("div", {
      className: "flex-grow border-t border-gray-300"
    }), /* @__PURE__ */ jsx("span", {
      className: "px-4 text-gray-500",
      children: /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.or"
      })
    }), /* @__PURE__ */ jsx("div", {
      className: "flex-grow border-t border-gray-300"
    })]
  });
};

const viewport = () => {
  let e = window, a = "inner";
  if (!("innerWidth" in window)) {
    a = "client";
    e = document.documentElement || document.body;
  }
  return {
    width: e[a + "Width"] || 0,
    height: e[a + "Height"] || 0
  };
};

const popupFeatures = ({
  width: screenWidth,
  height: screenHeight
}) => {
  const common = "toolbar=no, menubar=no";
  const width = Math.min(Math.max(screenWidth, 350), 500);
  const left = Math.round(Math.max(screenWidth - width, 0) / 2);
  const height = Math.min(Math.max(screenHeight, 400), 560);
  const top = Math.round(Math.max(screenHeight - height, 0) / 2);
  return `width=${width}, left=${left}, height=${height}, top=${top}, ${common}`;
};

const OAUTH_URL_GOOGLE = "https://accounts.google.com/o/oauth2/auth";
const params2popupUrl = (params) => {
  return OAUTH_URL_GOOGLE + "?" + Object.entries(params).map(([key, value]) => {
    return key + "=" + value;
  }).join("&");
};

const options = {
  scopes: ["https://www.googleapis.com/auth/userinfo.email", "https://www.googleapis.com/auth/userinfo.profile"]
};

const useGoogleAuthHelper = (callback, {
  client_id
}) => {
  const ref = useRef();
  if (!ref.current) {
    ref.current = new GoogleAuthHelper(callback, {
      ...options,
      client_id
    });
  }
  useEffect(() => {
    ref.current.listenForAuthReply();
    return () => ref.current.clear();
  }, []);
  return ref.current;
};
React.createContext({
  access_token: null
});
const REDIRECT = "/gpopup/";
const TYPE = "token";
const redirectUrl = (origin) => {
  if (origin) {
    return origin + REDIRECT;
  }
  return (typeof window !== "undefined" ? window.location.origin : "http://localhost:3000") + REDIRECT;
};
class GoogleAuthHelper {
  constructor(callback, options2) {
    this.callback = callback;
    this.options = options2;
    this.openAccessTokenPopup = (options2) => {
      this.window = window.open(this.popupUrl4AccessToken(options2), "Google Auth", popupFeatures(viewport()));
      this.window.focus();
    };
    this.openOfflinePopup = (options2) => {
      this.window = window.open(this.popupUrl4AOffline(options2), "Google Auth", popupFeatures(viewport()));
      this.window.focus();
    };
    this.updateAuthInfo = (ev) => {
      const newValue = ev.newValue;
      if (ev.key == "__goo" && newValue && this.returnUrlRef !== newValue) {
        this.returnUrlRef = newValue;
        const queryString = newValue.split("?")[1] || newValue.split("#")[1];
        this.result = getQueryStringParams(queryString);
        try {
          this.callback(this.result);
        } finally {
          this.closePopup();
        }
      }
    };
  }
  defaultAccessTokenUrlOptions() {
    const {
      scopes,
      client_id
    } = this.options;
    return {
      scope: scopes.join(" "),
      client_id,
      redirect_uri: redirectUrl(),
      response_type: TYPE
    };
  }
  popupUrl4AccessToken(options2) {
    const params = {
      ...this.defaultAccessTokenUrlOptions(),
      ...options2
    };
    return params2popupUrl(params);
  }
  defaultOfflineOptions() {
    const {
      scopes,
      client_id
    } = this.options;
    return {
      scope: scopes.join(" "),
      client_id,
      redirect_uri: window.location.origin + REDIRECT,
      response_type: "code",
      access_type: "offline"
    };
  }
  popupUrl4AOffline(options2) {
    const params = {
      ...this.defaultOfflineOptions(),
      ...options2
    };
    return params2popupUrl(params);
  }
  closePopup() {
    this.window.close();
  }
  listenForAuthReply() {
    window.addEventListener("storage", this.updateAuthInfo);
  }
  clear() {
    window.removeEventListener("storage", this.updateAuthInfo);
  }
}

const ProfileContext = React.createContext({
  profile: void 0,
  instance: void 0,
  error: void 0
});
const useProfileContext = () => {
  return useContext(ProfileContext);
};

const useToastHelpers = () => {
  const { addToast } = useToast();
  const showSuccess = useCallback((message, title) => {
    return addToast({
      type: "success",
      title: title || "Success",
      message
    });
  }, [addToast]);
  const showError = useCallback((message, title) => {
    return addToast({
      type: "error",
      title: title || "Error",
      message,
      duration: 8e3
    });
  }, [addToast]);
  const showWarning = useCallback((message, title) => {
    return addToast({
      type: "warning",
      title: title || "Warning",
      message
    });
  }, [addToast]);
  const showInfo = useCallback((message, title) => {
    return addToast({
      type: "info",
      title: title || "Info",
      message
    });
  }, [addToast]);
  const showGraphQLErrors = useCallback((errors, title) => {
    const message = errors.length === 1 ? errors[0] : `Multiple errors occurred:
${errors.map((err, i) => `${i + 1}. ${err}`).join("\n")}`;
    return addToast({
      type: "error",
      title: title || "GraphQL Error",
      message,
      duration: 1e4
    });
  }, [addToast]);
  return {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showGraphQLErrors,
    addToast
  };
};

const GoogleButtonInner = ({
  children,
  client_id,
  className,
  onSuccess
}) => {
  const {
    showError
  } = useToastHelpers();
  const defaultOnSuccess = useOnLoginSuccess();
  const handleSuccess = onSuccess || defaultOnSuccess;
  const join = useAutoJoinCircleFn();
  const api = useApi();
  const handleError = (error) => {
    if (typeof window !== "undefined" && window.errorToast) {
      window.errorToast({
        error
      });
    }
    showError(errorMessage(error));
  };
  const helper = useGoogleAuthHelper(async (result) => {
    try {
      const user = await api.makeRequest({
        path: "/users/_new/google",
        body: result,
        method: "POST"
      });
      try {
        await join();
      } catch (error) {
        console.error(error);
      }
      handleSuccess({
        result: user
      });
    } catch (error) {
      handleError(error);
    }
  }, {
    client_id
  });
  return /* @__PURE__ */ jsxs("button", {
    className,
    onClick: () => helper.openAccessTokenPopup(),
    children: [/* @__PURE__ */ jsx(FaGoogle, {}), children]
  });
};
const GoogleButton = (props) => {
  const {
    instance
  } = useProfileContext();
  if (!instance || !instance.googleClientId) {
    return null;
  }
  const client_id = instance.googleClientId;
  return /* @__PURE__ */ jsx(GoogleButtonInner, {
    ...props,
    client_id
  });
};

const buttonClassName = "border-2 rounded border-gray-500 border-solid flex gap-1 p-3 items-center text-white justify-center";
const colorRed = "bg-[#e93f2e]";
const NewGoogleButton = () => {
  if (isMobileWrapper()) {
    return null;
  }
  return /* @__PURE__ */ jsx(GoogleButton, {
    className: `${buttonClassName} ${colorRed}`,
    children: /* @__PURE__ */ jsx(FormattedMessage, {
      id: "registration.continue_with_google"
    })
  });
};

let counterGlobal = 0;
const execute = (name, data) => {
  if (typeof window === "undefined") {
    return;
  }
  if (!window.ReactNativeWebView) {
    console.warn("Cant't execute:", name, data);
    return;
  }
  const counter = counterGlobal++;
  const message = {
    counter,
    name,
    data
  };
  const th = data && data.__noExecutionTimeout === true ? void 0 : setTimeout(() => {
    if (!window["ReactNativeWebViewResult" + counter]) {
      return;
    }
    window["ReactNativeWebViewResult" + counter].reject(new Error("timeout"));
    delete window["ReactNativeWebViewResult" + counter];
  }, 5e3);
  const result = new Promise((resolveOriginal, rejectOriginal) => {
    const clear = () => {
      clearTimeout(th);
      delete window["ReactNativeWebViewResult" + counter];
    };
    const resolve = (v) => {
      clear();
      resolveOriginal(v);
    };
    const reject = (v) => {
      clear();
      rejectOriginal(v);
    };
    window["ReactNativeWebViewResult" + counter] = {
      resolve,
      reject
    };
  });
  window.ReactNativeWebView.postMessage(JSON.stringify(message));
  return result;
};

const getDeviceState = () => execute("getDeviceState");

const mobilePhoneData2Save = async () => {
  if (!isMobileWrapper()) {
    return null;
  }
  const {
    userId: player,
    pushToken: token,
    appId
  } = await getDeviceState();
  return {
    player,
    token,
    appId
  };
};

const biometricAvailability = () => {
  if (!isExpoWrapper()) {
    return Promise.resolve({
      available: false,
      hasCredential: false
    });
  }
  return execute("biometricAvailability");
};
const storeBiometricToken = (refreshToken) => {
  return execute("biometricStoreToken", {
    token: refreshToken
  });
};
const getBiometricToken = () => {
  return execute("biometricGetToken");
};
const clearBiometricToken = () => {
  return execute("biometricClearToken");
};

const useBiometricLogin = () => {
  const api = useApi();
  const onLoginSuccess = useOnLoginSuccess();
  const [available, setAvailable] = useState(false);
  const [hasCredential, setHasCredential] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    biometricAvailability().then(({ available: available2, hasCredential: hasCredential2 }) => {
      if (!cancelled) {
        setAvailable(available2);
        setHasCredential(hasCredential2);
      }
    }).catch(() => {
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const loginWithBiometric = async () => {
    setLoading(true);
    try {
      const { token } = await getBiometricToken();
      if (!token) {
        return;
      }
      const user = await api.refreshSession(token);
      if (user && user.refreshToken) {
        await storeBiometricToken(user.refreshToken).catch(() => {
        });
      }
      await onLoginSuccess({ result: user });
    } catch (e) {
      await clearBiometricToken().catch(() => {
      });
      setHasCredential(false);
      throw e;
    } finally {
      setLoading(false);
    }
  };
  return { available, hasCredential, loading, loginWithBiometric };
};
const enableBiometricForSession = async (refreshToken) => {
  if (!refreshToken) {
    return;
  }
  const { available, hasCredential } = await biometricAvailability().catch(() => ({ available: false, hasCredential: false }));
  if (!available || hasCredential) {
    return;
  }
  await storeBiometricToken(refreshToken).catch(() => {
  });
};

const FEATURE_WHATSAPP_OTP = false;

const TURNSTILE_SITEKEY = "0x4AAAAAAEFSfh6aYO7-7NbH";
const TURNSTILE_ACTION = "otp_request";
const DUMMY_SITEKEYS = ["1x00000000000000000000AA", "2x00000000000000000000AB", "3x00000000000000000000FF"];
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const MAX_CHALLENGE_RETRIES = 2;
var loadPromise = null;
const loadTurnstile = () => {
  if (loadPromise) {
    return loadPromise;
  }
  loadPromise = new Promise((resolve, reject) => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      reject(new Error("turnstile_no_dom"));
      return;
    }
    const existing = window.turnstile;
    if (existing) {
      resolve(existing);
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const api = window.turnstile;
      if (api) {
        resolve(api);
      } else {
        reject(new Error("turnstile_missing_after_load"));
      }
    };
    script.onerror = () => {
      script.remove();
      reject(new Error("turnstile_script_blocked"));
    };
    document.head.appendChild(script);
  });
  loadPromise.catch(() => {
    loadPromise = null;
  });
  return loadPromise;
};
const useTurnstile = (visible) => {
  const enabled = Boolean(TURNSTILE_SITEKEY);
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const retriesRef = useRef(0);
  const [token, setToken] = useState("");
  const [blocked, setBlocked] = useState(false);
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (visible && !armed) {
      setArmed(true);
    }
  }, [visible, armed]);
  useEffect(() => {
    if (!enabled || !armed) {
      return;
    }
    if (DUMMY_SITEKEYS.indexOf(TURNSTILE_SITEKEY) !== -1) {
      console.warn("Turnstile: still using Cloudflare's dummy sitekey \u2014 replace TURNSTILE_SITEKEY before enabling enforcement");
    }
    var cancelled = false;
    loadTurnstile().then((api) => {
      if (cancelled || !containerRef.current || widgetIdRef.current) {
        return;
      }
      widgetIdRef.current = api.render(containerRef.current, {
        sitekey: TURNSTILE_SITEKEY,
        action: TURNSTILE_ACTION,
        callback: (value) => {
          retriesRef.current = 0;
          setToken(value);
        },
        "expired-callback": () => setToken(""),
        "error-callback": () => {
          setToken("");
          retriesRef.current += 1;
          if (retriesRef.current > MAX_CHALLENGE_RETRIES) {
            setBlocked(true);
            return;
          }
          if (widgetIdRef.current) {
            api.reset(widgetIdRef.current);
          }
        }
      });
    }).catch((e) => {
      if (!cancelled) {
        console.error("Turnstile failed to load:", e);
        setBlocked(true);
      }
    });
    return () => {
      cancelled = true;
      const api = window.turnstile;
      if (api && widgetIdRef.current) {
        api.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [enabled, armed]);
  const reset = useCallback(() => {
    setToken("");
    const api = window.turnstile;
    if (api && widgetIdRef.current) {
      api.reset(widgetIdRef.current);
    }
  }, []);
  return { enabled, containerRef, token, pending: enabled && armed && !token && !blocked, blocked, reset };
};

const COUNTRY_DIAL_CODES = [
  { iso: "AF", name: "Afghanistan", dial: "93" },
  { iso: "AL", name: "Albania", dial: "355" },
  { iso: "DZ", name: "Algeria", dial: "213" },
  { iso: "AD", name: "Andorra", dial: "376" },
  { iso: "AO", name: "Angola", dial: "244" },
  { iso: "AG", name: "Antigua and Barbuda", dial: "1268" },
  { iso: "AR", name: "Argentina", dial: "54" },
  { iso: "AM", name: "Armenia", dial: "374" },
  { iso: "AU", name: "Australia", dial: "61" },
  { iso: "AT", name: "Austria", dial: "43" },
  { iso: "AZ", name: "Azerbaijan", dial: "994" },
  { iso: "BS", name: "Bahamas", dial: "1242" },
  { iso: "BH", name: "Bahrain", dial: "973" },
  { iso: "BD", name: "Bangladesh", dial: "880" },
  { iso: "BB", name: "Barbados", dial: "1246" },
  { iso: "BY", name: "Belarus", dial: "375" },
  { iso: "BE", name: "Belgium", dial: "32" },
  { iso: "BZ", name: "Belize", dial: "501" },
  { iso: "BJ", name: "Benin", dial: "229" },
  { iso: "BT", name: "Bhutan", dial: "975" },
  { iso: "BO", name: "Bolivia", dial: "591" },
  { iso: "BA", name: "Bosnia and Herzegovina", dial: "387" },
  { iso: "BW", name: "Botswana", dial: "267" },
  { iso: "BR", name: "Brazil", dial: "55" },
  { iso: "BN", name: "Brunei", dial: "673" },
  { iso: "BG", name: "Bulgaria", dial: "359" },
  { iso: "BF", name: "Burkina Faso", dial: "226" },
  { iso: "BI", name: "Burundi", dial: "257" },
  { iso: "KH", name: "Cambodia", dial: "855" },
  { iso: "CM", name: "Cameroon", dial: "237" },
  { iso: "CA", name: "Canada", dial: "1" },
  { iso: "CV", name: "Cape Verde", dial: "238" },
  { iso: "CF", name: "Central African Republic", dial: "236" },
  { iso: "TD", name: "Chad", dial: "235" },
  { iso: "CL", name: "Chile", dial: "56" },
  { iso: "CN", name: "China", dial: "86" },
  { iso: "CO", name: "Colombia", dial: "57" },
  { iso: "KM", name: "Comoros", dial: "269" },
  { iso: "CG", name: "Congo", dial: "242" },
  { iso: "CD", name: "DR Congo", dial: "243" },
  { iso: "CR", name: "Costa Rica", dial: "506" },
  { iso: "CI", name: "Ivory Coast", dial: "225" },
  { iso: "HR", name: "Croatia", dial: "385" },
  { iso: "CU", name: "Cuba", dial: "53" },
  { iso: "CY", name: "Cyprus", dial: "357" },
  { iso: "CZ", name: "Czechia", dial: "420" },
  { iso: "DK", name: "Denmark", dial: "45" },
  { iso: "DJ", name: "Djibouti", dial: "253" },
  { iso: "DM", name: "Dominica", dial: "1767" },
  { iso: "DO", name: "Dominican Republic", dial: "1" },
  { iso: "EC", name: "Ecuador", dial: "593" },
  { iso: "EG", name: "Egypt", dial: "20" },
  { iso: "SV", name: "El Salvador", dial: "503" },
  { iso: "GQ", name: "Equatorial Guinea", dial: "240" },
  { iso: "ER", name: "Eritrea", dial: "291" },
  { iso: "EE", name: "Estonia", dial: "372" },
  { iso: "SZ", name: "Eswatini", dial: "268" },
  { iso: "ET", name: "Ethiopia", dial: "251" },
  { iso: "FJ", name: "Fiji", dial: "679" },
  { iso: "FI", name: "Finland", dial: "358" },
  { iso: "FR", name: "France", dial: "33" },
  { iso: "GA", name: "Gabon", dial: "241" },
  { iso: "GM", name: "Gambia", dial: "220" },
  { iso: "GE", name: "Georgia", dial: "995" },
  { iso: "DE", name: "Germany", dial: "49" },
  { iso: "GH", name: "Ghana", dial: "233" },
  { iso: "GR", name: "Greece", dial: "30" },
  { iso: "GD", name: "Grenada", dial: "1473" },
  { iso: "GT", name: "Guatemala", dial: "502" },
  { iso: "GN", name: "Guinea", dial: "224" },
  { iso: "GW", name: "Guinea-Bissau", dial: "245" },
  { iso: "GY", name: "Guyana", dial: "592" },
  { iso: "HT", name: "Haiti", dial: "509" },
  { iso: "HN", name: "Honduras", dial: "504" },
  { iso: "HK", name: "Hong Kong", dial: "852" },
  { iso: "HU", name: "Hungary", dial: "36" },
  { iso: "IS", name: "Iceland", dial: "354" },
  { iso: "IN", name: "India", dial: "91" },
  { iso: "ID", name: "Indonesia", dial: "62" },
  { iso: "IR", name: "Iran", dial: "98" },
  { iso: "IQ", name: "Iraq", dial: "964" },
  { iso: "IE", name: "Ireland", dial: "353" },
  { iso: "IL", name: "Israel", dial: "972" },
  { iso: "IT", name: "Italy", dial: "39" },
  { iso: "JM", name: "Jamaica", dial: "1" },
  { iso: "JP", name: "Japan", dial: "81" },
  { iso: "JO", name: "Jordan", dial: "962" },
  { iso: "KZ", name: "Kazakhstan", dial: "7" },
  { iso: "KE", name: "Kenya", dial: "254" },
  { iso: "KI", name: "Kiribati", dial: "686" },
  { iso: "XK", name: "Kosovo", dial: "383" },
  { iso: "KW", name: "Kuwait", dial: "965" },
  { iso: "KG", name: "Kyrgyzstan", dial: "996" },
  { iso: "LA", name: "Laos", dial: "856" },
  { iso: "LV", name: "Latvia", dial: "371" },
  { iso: "LB", name: "Lebanon", dial: "961" },
  { iso: "LS", name: "Lesotho", dial: "266" },
  { iso: "LR", name: "Liberia", dial: "231" },
  { iso: "LY", name: "Libya", dial: "218" },
  { iso: "LI", name: "Liechtenstein", dial: "423" },
  { iso: "LT", name: "Lithuania", dial: "370" },
  { iso: "LU", name: "Luxembourg", dial: "352" },
  { iso: "MO", name: "Macau", dial: "853" },
  { iso: "MG", name: "Madagascar", dial: "261" },
  { iso: "MW", name: "Malawi", dial: "265" },
  { iso: "MY", name: "Malaysia", dial: "60" },
  { iso: "MV", name: "Maldives", dial: "960" },
  { iso: "ML", name: "Mali", dial: "223" },
  { iso: "MT", name: "Malta", dial: "356" },
  { iso: "MH", name: "Marshall Islands", dial: "692" },
  { iso: "MR", name: "Mauritania", dial: "222" },
  { iso: "MU", name: "Mauritius", dial: "230" },
  { iso: "MX", name: "Mexico", dial: "52" },
  { iso: "FM", name: "Micronesia", dial: "691" },
  { iso: "MD", name: "Moldova", dial: "373" },
  { iso: "MC", name: "Monaco", dial: "377" },
  { iso: "MN", name: "Mongolia", dial: "976" },
  { iso: "ME", name: "Montenegro", dial: "382" },
  { iso: "MA", name: "Morocco", dial: "212" },
  { iso: "MZ", name: "Mozambique", dial: "258" },
  { iso: "MM", name: "Myanmar", dial: "95" },
  { iso: "NA", name: "Namibia", dial: "264" },
  { iso: "NR", name: "Nauru", dial: "674" },
  { iso: "NP", name: "Nepal", dial: "977" },
  { iso: "NL", name: "Netherlands", dial: "31" },
  { iso: "NZ", name: "New Zealand", dial: "64" },
  { iso: "NI", name: "Nicaragua", dial: "505" },
  { iso: "NE", name: "Niger", dial: "227" },
  { iso: "NG", name: "Nigeria", dial: "234" },
  { iso: "KP", name: "North Korea", dial: "850" },
  { iso: "MK", name: "North Macedonia", dial: "389" },
  { iso: "NO", name: "Norway", dial: "47" },
  { iso: "OM", name: "Oman", dial: "968" },
  { iso: "PK", name: "Pakistan", dial: "92" },
  { iso: "PW", name: "Palau", dial: "680" },
  { iso: "PS", name: "Palestine", dial: "970" },
  { iso: "PA", name: "Panama", dial: "507" },
  { iso: "PG", name: "Papua New Guinea", dial: "675" },
  { iso: "PY", name: "Paraguay", dial: "595" },
  { iso: "PE", name: "Peru", dial: "51" },
  { iso: "PH", name: "Philippines", dial: "63" },
  { iso: "PL", name: "Poland", dial: "48" },
  { iso: "PT", name: "Portugal", dial: "351" },
  { iso: "QA", name: "Qatar", dial: "974" },
  { iso: "RO", name: "Romania", dial: "40" },
  { iso: "RU", name: "Russia", dial: "7" },
  { iso: "RW", name: "Rwanda", dial: "250" },
  { iso: "KN", name: "Saint Kitts and Nevis", dial: "1869" },
  { iso: "LC", name: "Saint Lucia", dial: "1758" },
  { iso: "VC", name: "Saint Vincent and the Grenadines", dial: "1784" },
  { iso: "WS", name: "Samoa", dial: "685" },
  { iso: "SM", name: "San Marino", dial: "378" },
  { iso: "ST", name: "Sao Tome and Principe", dial: "239" },
  { iso: "SA", name: "Saudi Arabia", dial: "966" },
  { iso: "SN", name: "Senegal", dial: "221" },
  { iso: "RS", name: "Serbia", dial: "381" },
  { iso: "SC", name: "Seychelles", dial: "248" },
  { iso: "SL", name: "Sierra Leone", dial: "232" },
  { iso: "SG", name: "Singapore", dial: "65" },
  { iso: "SK", name: "Slovakia", dial: "421" },
  { iso: "SI", name: "Slovenia", dial: "386" },
  { iso: "SB", name: "Solomon Islands", dial: "677" },
  { iso: "SO", name: "Somalia", dial: "252" },
  { iso: "ZA", name: "South Africa", dial: "27" },
  { iso: "KR", name: "South Korea", dial: "82" },
  { iso: "SS", name: "South Sudan", dial: "211" },
  { iso: "ES", name: "Spain", dial: "34" },
  { iso: "LK", name: "Sri Lanka", dial: "94" },
  { iso: "SD", name: "Sudan", dial: "249" },
  { iso: "SR", name: "Suriname", dial: "597" },
  { iso: "SE", name: "Sweden", dial: "46" },
  { iso: "CH", name: "Switzerland", dial: "41" },
  { iso: "SY", name: "Syria", dial: "963" },
  { iso: "TW", name: "Taiwan", dial: "886" },
  { iso: "TJ", name: "Tajikistan", dial: "992" },
  { iso: "TZ", name: "Tanzania", dial: "255" },
  { iso: "TH", name: "Thailand", dial: "66" },
  { iso: "TL", name: "Timor-Leste", dial: "670" },
  { iso: "TG", name: "Togo", dial: "228" },
  { iso: "TO", name: "Tonga", dial: "676" },
  { iso: "TT", name: "Trinidad and Tobago", dial: "1868" },
  { iso: "TN", name: "Tunisia", dial: "216" },
  { iso: "TR", name: "Turkey", dial: "90" },
  { iso: "TM", name: "Turkmenistan", dial: "993" },
  { iso: "TV", name: "Tuvalu", dial: "688" },
  { iso: "UG", name: "Uganda", dial: "256" },
  { iso: "UA", name: "Ukraine", dial: "380" },
  { iso: "AE", name: "United Arab Emirates", dial: "971" },
  { iso: "GB", name: "United Kingdom", dial: "44" },
  { iso: "US", name: "United States", dial: "1" },
  { iso: "UY", name: "Uruguay", dial: "598" },
  { iso: "UZ", name: "Uzbekistan", dial: "998" },
  { iso: "VU", name: "Vanuatu", dial: "678" },
  { iso: "VA", name: "Vatican City", dial: "379" },
  { iso: "VE", name: "Venezuela", dial: "58" },
  { iso: "VN", name: "Vietnam", dial: "84" },
  { iso: "YE", name: "Yemen", dial: "967" },
  { iso: "ZM", name: "Zambia", dial: "260" },
  { iso: "ZW", name: "Zimbabwe", dial: "263" }
];
const findCountry = (iso) => {
  return COUNTRY_DIAL_CODES.find((c) => c.iso === iso);
};
const NSN_RANGES = {
  EG: [10, 10],
  IQ: [10, 10],
  GB: [9, 10],
  US: [10, 10],
  CA: [10, 10],
  DO: [10, 10],
  JM: [10, 10],
  BR: [10, 11],
  SA: [9, 9],
  AE: [8, 9],
  KW: [8, 8],
  QA: [8, 8],
  BH: [8, 8],
  OM: [8, 8],
  JO: [9, 9],
  LB: [7, 8],
  SY: [9, 9],
  YE: [9, 9],
  PS: [9, 9],
  TR: [10, 10],
  IR: [10, 10],
  IN: [10, 10],
  PK: [10, 10],
  BD: [10, 10],
  LK: [9, 9],
  FR: [9, 9],
  DE: [7, 11],
  ES: [9, 9],
  IT: [8, 10],
  NL: [9, 9],
  BE: [8, 9],
  CH: [9, 9],
  PT: [9, 9],
  GR: [10, 10],
  PL: [9, 9],
  RO: [9, 9],
  UA: [9, 9],
  RU: [10, 10],
  KZ: [10, 10],
  MA: [9, 9],
  DZ: [9, 9],
  TN: [8, 8],
  SD: [9, 9],
  ET: [9, 9],
  KE: [9, 9],
  NG: [10, 10],
  GH: [9, 9],
  ZA: [9, 9],
  CN: [11, 11],
  JP: [10, 10],
  KR: [9, 10],
  AU: [9, 9],
  NZ: [8, 10],
  MX: [10, 10]
};
const nationalLengthRange = (country) => {
  return NSN_RANGES[country.iso] || [5, 15 - country.dial.length];
};
const flagEmoji = (iso) => {
  return iso.toUpperCase().split("").map((ch) => String.fromCodePoint(127462 + ch.charCodeAt(0) - 65)).join("");
};
const guessDefaultCountry = (language) => {
  const languages = typeof navigator !== "undefined" && navigator.languages && navigator.languages.length ? navigator.languages : typeof navigator !== "undefined" && navigator.language ? [navigator.language] : [];
  for (const lang of languages) {
    const parts = String(lang).split("-");
    if (parts.length > 1) {
      const region = parts[parts.length - 1].toUpperCase();
      if (findCountry(region)) {
        return region;
      }
    }
  }
  if (language && language.indexOf("ar") === 0) {
    return "IQ";
  }
  return "GB";
};
const toAsciiDigits = (input) => {
  return input.replace(/[٠-٩۰-۹]/g, (ch) => {
    const code = ch.charCodeAt(0);
    if (code >= 1632 && code <= 1641) {
      return String(code - 1632);
    }
    return String(code - 1776);
  });
};
const composeInternationalPhone = (dial, nationalNumber) => {
  const normalized = toAsciiDigits(nationalNumber).trim();
  let international = "";
  if (normalized.indexOf("+") === 0) {
    international = normalized.substring(1).replace(/\D/g, "");
  } else if (normalized.indexOf("00") === 0) {
    international = normalized.substring(2).replace(/\D/g, "");
  }
  if (international && international.indexOf(dial) === 0) {
    const national = international.substring(dial.length).replace(/^0+/, "");
    return `+${dial}${national}`;
  }
  const digits = normalized.replace(/\D/g, "").replace(/^0+/, "");
  return `+${dial}${digits}`;
};

const falsyToString = (value)=>typeof value === "boolean" ? `${value}` : value === 0 ? "0" : value;
const cx = clsx;
const cva = (base, config)=>(props)=>{
        var _config_compoundVariants;
        if ((config === null || config === void 0 ? void 0 : config.variants) == null) return cx(base, props === null || props === void 0 ? void 0 : props.class, props === null || props === void 0 ? void 0 : props.className);
        const { variants, defaultVariants } = config;
        const getVariantClassNames = Object.keys(variants).map((variant)=>{
            const variantProp = props === null || props === void 0 ? void 0 : props[variant];
            const defaultVariantProp = defaultVariants === null || defaultVariants === void 0 ? void 0 : defaultVariants[variant];
            if (variantProp === null) return null;
            const variantKey = falsyToString(variantProp) || falsyToString(defaultVariantProp);
            return variants[variant][variantKey];
        });
        const propsWithoutUndefined = props && Object.entries(props).reduce((acc, param)=>{
            let [key, value] = param;
            if (value === undefined) {
                return acc;
            }
            acc[key] = value;
            return acc;
        }, {});
        const getCompoundVariantClassNames = config === null || config === void 0 ? void 0 : (_config_compoundVariants = config.compoundVariants) === null || _config_compoundVariants === void 0 ? void 0 : _config_compoundVariants.reduce((acc, param)=>{
            let { class: cvClass, className: cvClassName, ...compoundVariantOptions } = param;
            return Object.entries(compoundVariantOptions).every((param)=>{
                let [key, value] = param;
                return Array.isArray(value) ? value.includes({
                    ...defaultVariants,
                    ...propsWithoutUndefined
                }[key]) : ({
                    ...defaultVariants,
                    ...propsWithoutUndefined
                })[key] === value;
            }) ? [
                ...acc,
                cvClass,
                cvClassName
            ] : acc;
        }, []);
        return cx(base, getVariantClassNames, getCompoundVariantClassNames, props === null || props === void 0 ? void 0 : props.class, props === null || props === void 0 ? void 0 : props.className);
    };

const spinnerVariants = cva("animate-spin", {
  variants: {
    size: {
      xs: "h-3 w-3",
      sm: "h-4 w-4",
      md: "h-5 w-5",
      lg: "h-6 w-6",
      xl: "h-8 w-8"
    },
    colorScheme: {
      current: "text-current",
      white: "text-white",
      green: "text-green-500",
      gray: "text-gray-500",
      blue: "text-blue-500",
      red: "text-red-500"
    }
  },
  defaultVariants: {
    size: "md",
    colorScheme: "current"
  }
});
const Spinner = ({
  className,
  size,
  colorScheme,
  ...props
}) => {
  return /* @__PURE__ */ jsxs("svg", {
    className: spinnerVariants({
      size,
      colorScheme,
      className
    }),
    xmlns: "http://www.w3.org/2000/svg",
    fill: "none",
    viewBox: "0 0 24 24",
    ...props,
    children: [/* @__PURE__ */ jsx("circle", {
      className: "opacity-25",
      cx: "12",
      cy: "12",
      r: "10",
      stroke: "currentColor",
      strokeWidth: "4"
    }), /* @__PURE__ */ jsx("path", {
      className: "opacity-75",
      fill: "currentColor",
      d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    })]
  });
};

const FloatingPortal = ({
  children,
  id = "floating-portal-root",
  root = null
}) => {
  const [portalElement, setPortalElement] = useState(null);
  useEffect(() => {
    let element;
    if (root) {
      element = root;
    } else {
      element = document.getElementById(id);
      if (!element) {
        element = document.createElement("div");
        element.id = id;
        element.style.position = "fixed";
        element.style.top = "0";
        element.style.left = "0";
        element.style.width = "100%";
        element.style.height = "100%";
        element.style.pointerEvents = "none";
        element.style.zIndex = "10000";
        document.body.appendChild(element);
      }
    }
    setPortalElement(element);
    return () => {
      if (!root && element && element.parentNode) {
        const hasOtherChildren = element.children.length > 0;
        if (!hasOtherChildren) {
          element.parentNode.removeChild(element);
        }
      }
    };
  }, [id, root]);
  if (!portalElement) {
    return null;
  }
  return createPortal(children, portalElement);
};

const tooltipVariants = cva("px-2 py-1 text-sm rounded shadow-lg z-50 max-w-xs break-words", {
  variants: {
    variant: {
      default: "bg-gray-900 text-white",
      light: "bg-white text-gray-900 border border-gray-200",
      error: "bg-red-600 text-white",
      success: "bg-green-600 text-white"
    }
  },
  defaultVariants: {
    variant: "default"
  }
});
const Tooltip = ({
  content,
  children,
  placement = "top",
  variant,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef();
  const {
    refs,
    x,
    y,
    strategy
  } = useFloating({
    placement,
    middleware: [offset(8), flip(), shift({
      padding: 8
    })],
    whileElementsMounted: isOpen ? autoUpdate : void 0
  });
  const handleMouseEnter = () => {
    if (disabled || !content)
      return;
    timeoutRef.current = setTimeout(() => setIsOpen(true), 200);
  };
  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsOpen(false);
  };
  if (disabled || !content) {
    return /* @__PURE__ */ jsx(Fragment, {
      children
    });
  }
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [/* @__PURE__ */ jsx("span", {
      ref: refs.setReference,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onFocus: handleMouseEnter,
      onBlur: handleMouseLeave,
      className: "inline-flex",
      children
    }), isOpen && /* @__PURE__ */ jsx(FloatingPortal, {
      children: /* @__PURE__ */ jsx("div", {
        ref: refs.setFloating,
        style: {
          position: strategy,
          top: y || 0,
          left: x || 0,
          pointerEvents: "none"
        },
        className: tooltipVariants({
          variant
        }),
        children: content
      })
    })]
  });
};

const useRipple = () => {
  const [ripples, setRipples] = useState([]);
  const nextKey = useRef(0);
  const createRipple = useMemoizedFn((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;
    const key = nextKey.current;
    nextKey.current = nextKey.current + 1;
    setRipples((prev) => [...prev, { key, x, y, size }]);
  });
  const removeRipple = useMemoizedFn((key) => {
    setRipples((prev) => prev.filter((r) => r.key !== key));
  });
  return { ripples, createRipple, removeRipple };
};

const actionButtonVariants = cva("overflow-hidden relative inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium transition-colors focus:outline-none disabled:opacity-50 disabled:pointer-events-none", {
  variants: {
    intent: {
      primary: "bg-blue-600 text-white hover:bg-blue-700",
      secondary: "bg-gray-200 text-gray-900 hover:bg-gray-300",
      danger: "bg-red-600 text-white hover:bg-red-700",
      amber: "bg-amber-500 text-white hover:bg-amber-600",
      green: "bg-green-600 text-white hover:bg-green-700",
      white: "bg-white text-black border border-gray-200 hover:bg-gray-50",
      black: "bg-black text-white hover:bg-gray-900",
      whiteOutline: "bg-white text-black border-2 border-black border-solid hover:bg-gray-50",
      blackOutline: "bg-transparent text-black border-2 border-solid border-black hover:bg-black hover:text-white",
      gray: "bg-[#B3B3B3] text-white hover:bg-[#4D4D4D]",
      blue: "bg-[#4a90ce] text-white hover:bg-[#3a80be]",
      red: "bg-[#ef4444] text-white hover:bg-[#dc2626]",
      cairPrimary: "bg-[var(--cair-accent)] text-[var(--cair-accent-foreground)] hover:bg-[var(--cair-accent-hover)] hover:text-white border border-[var(--cair-accent)]",
      cairDefault: "bg-[var(--cair-surface-elevated)] text-[var(--cair-text-secondary)] border border-[var(--cair-border)] hover:bg-[var(--cair-surface-elevated-hover)]",
      cairSubtle: "bg-[var(--cair-accent-subtle)] text-[var(--cair-text-secondary)] border border-[var(--cair-border)] hover:bg-[var(--cair-border)]"
    },
    size: {
      xs: "h-[26px] px-[10px] text-xs",
      sm: "h-9 px-3 text-sm",
      md: "h-10 px-4",
      lg: "h-11 px-8"
    },
    rounded: {
      default: "rounded-md",
      minimal: "rounded-[3px]",
      sm: "rounded-[4px]"
    },
    margin: {
      none: "",
      vertical: "my-2"
    }
  },
  defaultVariants: {
    intent: "primary",
    size: "md",
    rounded: "default",
    margin: "none"
  }
});
const ActionButton = forwardRef(({
  className,
  intent,
  size,
  rounded,
  margin,
  isLoading,
  loadingText,
  leftIcon,
  rightIcon,
  tooltip,
  tooltipPlacement,
  children,
  ...props
}, ref) => {
  const {
    ripples,
    createRipple,
    removeRipple
  } = useRipple();
  const handleClick = useMemoizedFn((e) => {
    if (isLoading) {
      return;
    }
    if (props.onClick) {
      props.onClick(e);
    }
    createRipple(e);
  });
  const button = /* @__PURE__ */ jsxs("button", {
    ref,
    className: actionButtonVariants({
      intent,
      size,
      rounded,
      margin,
      className
    }),
    disabled: isLoading || props.disabled,
    ...props,
    onClick: handleClick,
    children: [ripples.map((ripple) => /* @__PURE__ */ jsx("span", {
      className: "absolute rounded-full bg-white/30 animate-ripple pointer-events-none",
      style: {
        left: ripple.x,
        top: ripple.y,
        width: ripple.size,
        height: ripple.size
      },
      onAnimationEnd: () => removeRipple(ripple.key)
    }, ripple.key)), isLoading ? /* @__PURE__ */ jsx(Spinner, {
      size: size === "sm" ? "xs" : "sm",
      className: "mr-2 shrink-0"
    }) : leftIcon ? /* @__PURE__ */ jsx("span", {
      className: "me-2 inline-flex shrink-0",
      children: leftIcon
    }) : null, isLoading && loadingText ? loadingText : children, rightIcon && !isLoading && /* @__PURE__ */ jsx("span", {
      className: "ms-2 inline-flex shrink-0",
      children: rightIcon
    })]
  });
  if (!tooltip) {
    return button;
  }
  return /* @__PURE__ */ jsx(Tooltip, {
    content: tooltip,
    placement: tooltipPlacement || "top",
    children: button
  });
});
ActionButton.displayName = "ActionButton";

const LoadingButtonTW = ({
  loading,
  disabled,
  component: CustomComponent,
  hidden,
  children,
  background,
  ...props
}) => {
  if (hidden) {
    return null;
  }
  const intent = background || "gray";
  if (CustomComponent) {
    return /* @__PURE__ */ jsx(CustomComponent, {
      disabled: disabled || loading,
      isLoading: loading,
      ...props,
      children
    });
  }
  return /* @__PURE__ */ jsx(ActionButton, {
    intent,
    size: "md",
    rounded: "minimal",
    margin: "vertical",
    isLoading: loading,
    disabled: disabled || loading,
    ...props,
    children
  });
};

const textInputVariants = cva("w-full px-3 py-2 border rounded-md text-sm transition-colors focus:outline-none focus:ring-2 focus:border-transparent", {
  variants: {
    variant: {
      default: "border-gray-300 focus:ring-blue-500",
      error: "border-red-300 focus:ring-red-500",
      success: "border-green-300 focus:ring-green-500"
    },
    size: {
      sm: "px-2 py-1 text-xs",
      md: "px-3 py-2 text-sm",
      lg: "px-4 py-3 text-base"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "md"
  }
});
const TextInput = forwardRef(({
  className,
  variant,
  size,
  label,
  helperText,
  error,
  ...props
}, ref) => {
  const inputVariant = error ? "error" : variant;
  return /* @__PURE__ */ jsxs("div", {
    className: "flex flex-col gap-1 w-full",
    children: [label && /* @__PURE__ */ jsx("label", {
      className: "text-sm font-medium text-gray-700",
      children: label
    }), /* @__PURE__ */ jsx("input", {
      ref,
      className: textInputVariants({
        variant: inputVariant,
        size,
        className
      }),
      ...props
    }), (helperText || error) && /* @__PURE__ */ jsx("span", {
      className: `text-xs ${error ? "text-red-500" : "text-gray-500"}`,
      children: error || helperText
    })]
  });
});
TextInput.displayName = "TextInput";

const RESEND_COOLDOWN_SECONDS = 60;
const OTP_ERROR_KEYS = {
  otp_invalid: "auth.otpInvalid",
  otp_expired: "auth.otpExpired",
  otp_too_many_attempts: "auth.otpTooManyAttempts",
  otp_cooldown: "auth.otpCooldown",
  otp_rate_limited: "auth.otpRateLimited",
  sms_send_failed: "auth.smsSendFailed",
  otp_country_not_supported: "auth.otpCountryNotSupported",
  invalid_phone: "validation_error.invalid_phone_number"
};
const errorKey$2 = (error) => error && OTP_ERROR_KEYS[error.response] || "auth.otpFailed";
const inputClassName = "w-full px-3 py-2 border border-gray-300 rounded-md text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";
const PhoneOtpFields = () => {
  const api = useApi();
  const intl = useIntl();
  const onLoginSuccess = useOnLoginSuccess();
  const [country, setCountry] = useState(() => guessDefaultCountry(intl.locale));
  const [nationalNumber, setNationalNumber] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  useEffect(() => {
    if (!cooldown) {
      return;
    }
    const timer = setTimeout(() => setCooldown(cooldown - 1), 1e3);
    return () => clearTimeout(timer);
  }, [cooldown]);
  const countries = useMemo(() => {
    let displayNames = null;
    try {
      displayNames = new Intl.DisplayNames([intl.locale], {
        type: "region"
      });
    } catch (e) {
    }
    const localized = COUNTRY_DIAL_CODES.map((c) => {
      let name = c.name;
      if (displayNames) {
        try {
          const translated = displayNames.of(c.iso);
          if (translated && translated !== c.iso) {
            name = translated;
          }
        } catch (e) {
        }
      }
      return {
        ...c,
        name
      };
    });
    return localized.sort((a, b) => a.name.localeCompare(b.name, intl.locale));
  }, [intl.locale]);
  const selected = findCountry(country) || COUNTRY_DIAL_CODES[0];
  const phone = composeInternationalPhone(selected.dial, nationalNumber);
  const nationalDigits = phone.substring(1 + selected.dial.length);
  const [minDigits, maxDigits] = nationalLengthRange(selected);
  const phoneComplete = nationalDigits.length >= minDigits && nationalDigits.length <= maxDigits;
  const turnstile = useTurnstile(Boolean(nationalNumber));
  const captchaReady = !turnstile.enabled || Boolean(turnstile.token) || turnstile.blocked;
  const request = useAsyncOperation(async (channel) => {
    try {
      await api.requestPhoneOtp(phone, channel === "whatsapp" ? "whatsapp" : "sms", turnstile.token);
    } finally {
      turnstile.reset();
    }
    setCodeSent(true);
    setCooldown(RESEND_COOLDOWN_SECONDS);
  }, {
    showErrorToast: false
  });
  const verify = useAsyncOperation(async () => {
    const mobile = await mobilePhoneData2Save();
    const user = await api.verifyPhoneOtp({
      phone,
      code: toAsciiDigits(code).trim(),
      mobiles: mobile ? [mobile] : null,
      dial: selected.dial
    });
    await enableBiometricForSession(user.refreshToken);
    await onLoginSuccess({
      result: user
    });
  }, {
    showErrorToast: false
  });
  const error = verify.error || request.error;
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [/* @__PURE__ */ jsxs("div", {
      className: "flex flex-col gap-1 w-full",
      children: [/* @__PURE__ */ jsx("label", {
        className: "text-sm font-medium text-gray-700",
        children: /* @__PURE__ */ jsx(FormattedMessage, {
          id: "auth.country"
        })
      }), /* @__PURE__ */ jsx("select", {
        className: `${inputClassName} bg-white`,
        value: country,
        onChange: (e) => setCountry(e.target.value),
        disabled: codeSent,
        children: countries.map((c) => /* @__PURE__ */ jsxs("option", {
          value: c.iso,
          children: [flagEmoji(c.iso), " ", c.name, " (+", c.dial, ")"]
        }, c.iso))
      })]
    }), /* @__PURE__ */ jsxs("div", {
      className: "flex flex-col gap-1 w-full",
      children: [/* @__PURE__ */ jsx("label", {
        className: "text-sm font-medium text-gray-700",
        children: /* @__PURE__ */ jsx(FormattedMessage, {
          id: "auth.phone"
        })
      }), /* @__PURE__ */ jsxs("div", {
        className: "flex w-full",
        dir: "ltr",
        children: [/* @__PURE__ */ jsxs("span", {
          className: "px-3 py-2 border border-gray-300 border-r-0 rounded-md rounded-r-none bg-gray-50 text-sm text-gray-600 whitespace-nowrap select-none",
          children: ["+", selected.dial]
        }), /* @__PURE__ */ jsx("input", {
          type: "tel",
          className: `${inputClassName} rounded-l-none`,
          value: nationalNumber,
          onChange: (e) => setNationalNumber(e.target.value),
          disabled: codeSent
        })]
      })]
    }), nationalNumber && !phoneComplete && /* @__PURE__ */ jsx("div", {
      className: "text-red-500 text-sm",
      children: minDigits === maxDigits ? /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.phoneLengthExact",
        values: {
          n: minDigits
        }
      }) : /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.phoneLengthRange",
        values: {
          min: minDigits,
          max: maxDigits
        }
      })
    }), !codeSent && phoneComplete && /* @__PURE__ */ jsx("div", {
      className: "text-xs text-gray-500 w-full",
      dir: "ltr",
      children: phone
    }), codeSent && /* @__PURE__ */ jsxs(Fragment, {
      children: [/* @__PURE__ */ jsxs("div", {
        className: "text-sm text-gray-600",
        children: [/* @__PURE__ */ jsx(FormattedMessage, {
          id: "auth.codeSent"
        }), " ", /* @__PURE__ */ jsx("span", {
          dir: "ltr",
          children: phone
        })]
      }), /* @__PURE__ */ jsx(TextInput, {
        label: /* @__PURE__ */ jsx(FormattedMessage, {
          id: "auth.enterCode"
        }),
        type: "text",
        value: code,
        onChange: (e) => setCode(e.target.value)
      })]
    }), turnstile.enabled && /* @__PURE__ */ jsxs("div", {
      className: "w-full flex flex-col items-center gap-1",
      children: [/* @__PURE__ */ jsx("div", {
        ref: turnstile.containerRef
      }), turnstile.pending && /* @__PURE__ */ jsx("div", {
        className: "text-xs text-gray-500",
        children: /* @__PURE__ */ jsx(FormattedMessage, {
          id: "auth.otpCaptchaPending"
        })
      })]
    }), error && /* @__PURE__ */ jsx("div", {
      className: "text-red-500 text-sm",
      children: /* @__PURE__ */ jsx(FormattedMessage, {
        id: errorKey$2(error)
      })
    }), !codeSent ? /* @__PURE__ */ jsxs(Fragment, {
      children: [/* @__PURE__ */ jsx(LoadingButtonTW, {
        background: "primary",
        loading: request.isLoading,
        disabled: !phoneComplete || !captchaReady,
        onClick: () => request.execute(),
        className: "w-full",
        children: /* @__PURE__ */ jsx(FormattedMessage, {
          id: request.isLoading ? "auth.sendingCode" : "auth.sendCode"
        })
      }), FEATURE_WHATSAPP_OTP ]
    }) : /* @__PURE__ */ jsxs(Fragment, {
      children: [/* @__PURE__ */ jsx(LoadingButtonTW, {
        background: "primary",
        loading: verify.isLoading,
        disabled: !code,
        onClick: verify.execute,
        className: "w-full",
        children: /* @__PURE__ */ jsx(FormattedMessage, {
          id: verify.isLoading ? "auth.verify2" : "auth.verify"
        })
      }), /* @__PURE__ */ jsx("button", {
        className: "bg-transparent border-none p-0 m-0 text-blue-500 underline hover:text-blue-700 cursor-pointer text-sm disabled:text-gray-400 disabled:no-underline disabled:cursor-default",
        disabled: cooldown > 0 || request.isLoading || !captchaReady,
        onClick: () => {
          verify.reset();
          request.execute();
        },
        children: cooldown > 0 ? /* @__PURE__ */ jsx(FormattedMessage, {
          id: "auth.resendCodeIn",
          values: {
            seconds: cooldown
          }
        }) : /* @__PURE__ */ jsx(FormattedMessage, {
          id: "auth.resendCode"
        })
      }), FEATURE_WHATSAPP_OTP , /* @__PURE__ */ jsx("button", {
        className: "bg-transparent border-none p-0 m-0 text-blue-500 underline hover:text-blue-700 cursor-pointer text-sm",
        onClick: () => {
          setCodeSent(false);
          setCode("");
        },
        children: /* @__PURE__ */ jsx(FormattedMessage, {
          id: "auth.changePhone"
        })
      })]
    })]
  });
};
const PhoneOtpForm = () => {
  const {
    state,
    setState
  } = useRegistrationContext();
  return /* @__PURE__ */ jsxs("div", {
    className: `flex-col items-center p-6 space-y-4 bg-white shadow-md rounded-lg w-full max-w-md mx-auto ${state === "phone-otp" ? "flex" : "hidden"}`,
    children: [/* @__PURE__ */ jsx("h1", {
      className: "text-xl font-bold",
      children: /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.continueWithPhone"
      })
    }), /* @__PURE__ */ jsx(PhoneOtpFields, {}), /* @__PURE__ */ jsx("button", {
      className: "bg-transparent border-none p-0 m-0 text-blue-500 underline hover:text-blue-700 cursor-pointer",
      onClick: () => setState("login"),
      children: /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.alreadyHaveAccount"
      })
    })]
  });
};

const BiometricLoginButton = () => {
  const {
    available,
    hasCredential,
    loading,
    loginWithBiometric
  } = useBiometricLogin();
  const [failed, setFailed] = useState(false);
  if (!available || !hasCredential) {
    return null;
  }
  const onClick = async () => {
    setFailed(false);
    try {
      await loginWithBiometric();
    } catch (e) {
      setFailed(true);
    }
  };
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [/* @__PURE__ */ jsx(LoadingButtonTW, {
      background: "secondary",
      loading,
      onClick,
      className: "w-full",
      children: /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.biometricLogin"
      })
    }), failed && /* @__PURE__ */ jsx("div", {
      className: "text-red-500 text-sm w-full",
      children: /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.biometricFailed"
      })
    })]
  });
};

function base64urlToBuffer(b64url) {
  var b64 = b64url.replace(/-/g, "+").replace(/_/g, "/");
  var pad = b64.length % 4 ? 4 - b64.length % 4 : 0;
  for (var p = 0; p < pad; p++) {
    b64 += "=";
  }
  var str = atob(b64);
  var buf = new ArrayBuffer(str.length);
  var view = new Uint8Array(buf);
  for (var i = 0; i < str.length; i++) {
    view[i] = str.charCodeAt(i);
  }
  return buf;
}
function bufferToBase64url(buf) {
  var view = new Uint8Array(buf);
  var str = "";
  for (var i = 0; i < view.length; i++) {
    str += String.fromCharCode(view[i]);
  }
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
async function startAuthentication(options) {
  var publicKey = Object.assign({}, options);
  publicKey.challenge = base64urlToBuffer(options.challenge);
  if (options.allowCredentials) {
    publicKey.allowCredentials = options.allowCredentials.map(function(c) {
      return Object.assign({}, c, { id: base64urlToBuffer(c.id) });
    });
  }
  var cred = await navigator.credentials.get({ publicKey });
  var response = cred.response;
  var userHandle = response.userHandle ? bufferToBase64url(response.userHandle) : void 0;
  return {
    id: cred.id,
    rawId: bufferToBase64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      authenticatorData: bufferToBase64url(response.authenticatorData),
      signature: bufferToBase64url(response.signature),
      userHandle
    },
    clientExtensionResults: typeof cred.getClientExtensionResults === "function" ? cred.getClientExtensionResults() : {}
  };
}

const PasskeyLoginButton = () => {
  const api = useApi();
  const onLoginSuccess = useOnLoginSuccess();
  const [show, setShow] = useState(false);
  useEffect(() => {
    {
      return;
    }
  }, []);
  const login = useAsyncOperation(async () => {
    try {
      const {
        options,
        challengeId
      } = await api.webauthnLoginOptions();
      const assertion = await startAuthentication(options);
      const user = await api.webauthnLoginVerify({
        challengeId,
        response: assertion
      });
      await onLoginSuccess({
        result: user
      });
    } catch (e) {
      console.error("passkey login failed:", e && e.name, e && e.message, e);
      throw e;
    }
  }, {
    showErrorToast: false
  });
  if (!show) {
    return null;
  }
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [/* @__PURE__ */ jsx(LoadingButtonTW, {
      background: "secondary",
      loading: login.isLoading,
      onClick: () => login.execute(),
      className: "w-full",
      children: /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.passkeyLogin"
      })
    }), login.error && /* @__PURE__ */ jsx("div", {
      className: "text-red-500 text-sm w-full",
      children: /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.passkeyFailed"
      })
    })]
  });
};

const PasswordInput = ({
  label,
  value,
  onChange,
  error,
  className
}) => {
  const [show, setShow] = useState(false);
  const handleToggle = () => setShow(!show);
  return /* @__PURE__ */ jsxs("div", {
    className: `flex flex-col gap-1 w-full ${className || ""}`,
    children: [label && /* @__PURE__ */ jsx("label", {
      className: "text-sm text-gray-600",
      children: label
    }), /* @__PURE__ */ jsxs("div", {
      className: "relative",
      children: [/* @__PURE__ */ jsx("input", {
        type: show ? "text" : "password",
        value,
        onChange,
        className: ["w-full px-3 py-2 pr-10 rounded border bg-gray-50 text-sm", "focus:outline-none focus:ring-2 focus:ring-blue-300", error ? "border-red-400" : "border-gray-300"].join(" ")
      }), /* @__PURE__ */ jsx("button", {
        type: "button",
        tabIndex: -1,
        onClick: handleToggle,
        className: "absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600 focus:outline-none",
        children: show ? /* @__PURE__ */ jsx(MdVisibilityOff, {
          size: 18
        }) : /* @__PURE__ */ jsx(MdVisibility, {
          size: 18
        })
      })]
    }), error && /* @__PURE__ */ jsx("span", {
      className: "text-red-500 text-xs",
      children: error
    })]
  });
};

const toggleClassName$1 = (active) => `flex-1 py-2 text-sm font-medium border-none cursor-pointer transition-colors ${active ? "bg-blue-600 text-white" : "bg-white text-gray-700 hover:bg-gray-100"}`;
const LoginForm = () => {
  const {
    state,
    setState
  } = useRegistrationContext();
  const api = useApi();
  const onLoginSuccess = useOnLoginSuccess();
  const [method, setMethod] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };
  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };
  const submit = useAsyncOperation(async () => {
    const user = await api.loginWithEmail({
      email,
      password
    });
    await onLoginSuccess({
      result: user
    });
  }, {
    showErrorToast: false
  });
  return /* @__PURE__ */ jsxs("div", {
    className: `flex-col items-center p-6 space-y-4 bg-white shadow-md rounded-lg w-full max-w-md mx-auto ${state === "login" || state === "main" ? "flex" : "hidden"}`,
    children: [/* @__PURE__ */ jsx("h1", {
      className: "text-xl font-bold",
      children: /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.login2"
      })
    }), /* @__PURE__ */ jsx(BiometricLoginButton, {}), /* @__PURE__ */ jsx(PasskeyLoginButton, {}), /* @__PURE__ */ jsxs("div", {
      className: "flex w-full rounded-md border border-gray-300 overflow-hidden",
      role: "tablist",
      children: [/* @__PURE__ */ jsx("button", {
        role: "tab",
        "aria-selected": method === "email",
        className: toggleClassName$1(method === "email"),
        onClick: () => setMethod("email"),
        children: /* @__PURE__ */ jsx(FormattedMessage, {
          id: "auth.email"
        })
      }), /* @__PURE__ */ jsx("button", {
        role: "tab",
        "aria-selected": method === "phone",
        className: toggleClassName$1(method === "phone"),
        onClick: () => setMethod("phone"),
        children: /* @__PURE__ */ jsx(FormattedMessage, {
          id: "auth.phone2"
        })
      })]
    }), method === "email" ? /* @__PURE__ */ jsxs(Fragment, {
      children: [/* @__PURE__ */ jsx(TextInput, {
        label: /* @__PURE__ */ jsx(FormattedMessage, {
          id: "auth.email"
        }),
        type: "email",
        value: email,
        onChange: handleEmailChange
      }), /* @__PURE__ */ jsx(PasswordInput, {
        label: /* @__PURE__ */ jsx(FormattedMessage, {
          id: "auth.password"
        }),
        value: password,
        onChange: handlePasswordChange
      }), /* @__PURE__ */ jsx(LoadingButtonTW, {
        background: "primary",
        loading: submit.isLoading,
        onClick: submit.execute,
        className: "w-full",
        children: /* @__PURE__ */ jsx(FormattedMessage, {
          id: submit.isLoading ? "auth.login3" : "auth.login2"
        })
      }), submit.error && /* @__PURE__ */ jsx("div", {
        className: "text-red-500 text-sm w-full",
        children: submit.error.message || /* @__PURE__ */ jsx(FormattedMessage, {
          id: "auth.otpFailed"
        })
      }), /* @__PURE__ */ jsx("div", {
        className: "text-blue-500 underline hover:text-blue-700 cursor-pointer",
        onClick: () => setState("forgot"),
        children: /* @__PURE__ */ jsx(FormattedMessage, {
          id: "auth.forgot"
        })
      })]
    }) : /* @__PURE__ */ jsx(PhoneOtpFields, {}), /* @__PURE__ */ jsx("button", {
      className: "bg-transparent border-none p-0 m-0 text-blue-500 underline hover:text-blue-700 cursor-pointer",
      onClick: () => setState("register"),
      children: /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.register2"
      })
    }), /* @__PURE__ */ jsx(OrRuler, {}), /* @__PURE__ */ jsx(NewGoogleButton, {})]
  });
};

const EMAIL_REGEX = /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?$/i;
const isValidateEmail = (email) => {
  if (!EMAIL_REGEX.test(email) || /\s/.test(email)) {
    return "validation_error.invalid_email";
  }
};

const CONTAINS_NUMBER = /[\d]+/;
const isValidPassword = (password) => {
  if (password === void 0 || password === null || password.length < 8) {
    return "validation_error.password_too_short";
  }
  if (!CONTAINS_NUMBER.test(password)) {
    return "validation_error.password_must_include_digits";
  }
};

const isValidPhoneNumber = (phone) => {
  const regex = /^\+?[\d]+$/;
  if (!regex.test(stripAllWhitespaces(phone))) {
    return "Phone number may contain only digits (0-9) and + sign";
  }
  return null;
};
const stripAllWhitespaces = (input) => {
  return input.replace(/\s+/g, "");
};

const privacy_policy_en = `Privacy Policy
Based on an original by Creative Commons \u2013 adapted with thanks.

1. Preamble
This Privacy Policy explains the collection, use, processing, transferring and disclosure of personal information by NewsSocial Ltd.
This Privacy Policy is incorporated into and made part of NewsSocial\u2019s Terms of Use (\u201CMaster Terms\u201D) located at https://www.mushtarek.org/terms.
Unless otherwise noted on a particular website or service hosted by NewsSocial, this Privacy Policy applies to your use of all websites that NewsSocial operates. These include https://newssocial.news, https://mushtarek.org, https://argon.today, together with all other subdomains thereof, (collectively, the \u201CWebsites\u201D). This Privacy Policy also applies to all products, information, and services provided through the Websites.
By accessing or using any of the Services, you are accepting and agreeing to the practices described in this Privacy Policy.

2. Our Principles
NewsSocial has designed this policy to be consistent with the following principles:
Privacy policies should be human readable and easy to find.
Data collection, storage, and processing should be simplified as much as possible to enhance security, ensure consistency, and make the practices easy for users to understand. 
Data practices should always meet the reasonable expectations of users. 

3. Personal Information NewsSocial Collects and How it is Used
As used in this policy, \u201Cpersonal information\u201D means information that would allow someone to identify you, including your name, email address, IP address, or other information from which someone could deduce your identity.
NewsSocial collects and uses personal information in the following ways: 
Website and Fundraising Analytics: When you visit our Websites and use our Services, NewsSocial collects some information about your activities through tools such as Google Analytics. The type of information that we collect focuses on general information such as country or city where you are located, pages visited, time spent on pages, heat-map of visitors\u2019 activity on the site, information about the browser you are using, etc. NewsSocial collects and uses this information pursuant to our legitimate interest in enhancing the security and utility of our Services. The information we gather and process is used in the aggregate to spot trends without deliberately identifying individuals, except in cases where you engage in a transaction with NewsSocial by donating money, purchasing merchandise, or buying a ticket for an event or program. In those cases, NewsSocial retains certain information about your visit to the Services pursuant to its legitimate interest in understanding its community of supporters for fundraising purposes, and this information is stored in connection with other personal information you provide to NewsSocial. 
Note that you can learn about Google\u2019s practices in connection with its analytics services and how to opt out of it by downloading the Google Analytics opt-out browser add-on, available at https://tools.google.com/dlpage/gaoptout. If you would like to opt out of NewsSocial\u2019s fundraising analytics, please contact info@newssocial.co.uk with your request. 
Information from Cookies: We and our service providers (for example, Google Analytics as described above) may collect information using cookies or similar technologies for the purposes described above and below. Cookies are pieces of information that are stored by your browser on the hard drive or memory of your computer or other Internet access device.  Cookies may enable us to personalize your experience on the Services, maintain a persistent session, passively collect demographic information about your computer, and monitor advertisements and other activities. The Websites may use different kinds of cookies and other types of local storage (such as browser-based or plugin-based local storage).
Log-In Services:  When you register to obtain a user account on any of the Services, you will be asked to provide personal information to create your account and establish a password and profile. NewsSocial collects and uses this personal information pursuant to its legitimate interest in establishing and maintaining your account providing you with the features we provide Registered Users. We may use your email address to contact you regarding changes to this policy or other applicable policies. The name or nickname you provide in connection with your account may be used to attribute you in connection with any content you submit to any Service. 
User-Uploaded Pictures and Files Privacy: We recognize the importance of protecting the privacy and security of our users' uploaded pictures and files. As part of our commitment to safeguarding your information, we maintain strict security measures to prevent unauthorized access, disclosure, alteration, or destruction of the pictures and files you upload to our website. Access to your uploaded pictures and files is restricted to authorized personnel and service providers who require access to perform their job functions or provide essential services, and they are bound by confidentiality agreements.
We do not sell, rent, or share your uploaded pictures and files with third parties without your explicit consent, except when required by law or as necessary to protect our rights or comply with legal processes. It is important to note that by using our website and uploading pictures and files, you grant us a license to use, store, and display these materials in accordance with our Terms of Service. We encourage you to review our Terms of Service for further information on how we handle user-generated content. We continuously review and update our security practices to ensure the ongoing protection of your uploaded pictures and files. However, please be aware that no security measures can guarantee absolute protection, and we cannot ensure or warrant the security of any information you transmit to us. By using our website and uploading pictures and files, you acknowledge and accept these risks.
Events: When you sign up for an event hosted or supported by NewsSocial, you will be asked to provide some personal information, including your name, email address, and other information as needed. NewsSocial collects and uses this personal information pursuant to its legitimate interest in organizing and running the relevant event. We may share your personal information with vendors, third party contractors, partner organizations, and volunteers for the purpose of organizing and running the event and related activities.
Emails and Newsletters: When you sign up to receive updates from NewsSocial or otherwise subscribe to one of our mailing lists, you may be asked to provide some personal information. NewsSocial collects and uses this personal information pursuant to its legitimate interest in providing news and updates to, and collaborating with, its supporters and volunteers. 
Email Analytics: When you receive communications from NewsSocial after signing up for the NewsSocial newsletter, campaign updates, or other ongoing email communications from NewsSocial, we use analytics to track whether you open the mail, click on the links, and otherwise interact with what we send. You may opt out of this tracking by choosing to get plain-text emails from NewsSocial. NewsSocial collects and uses this personal information pursuant to its legitimate interest in understanding the interests of its community of supporters and volunteers in order to provide more relevant news and updates. 
Donations:  When you donate money to NewsSocial, pay for using some of NewsSocial services, or pay for attendance at an event or participation in the NewsSocial Certificate program, you will be asked to provide personal information, including payment information. NewsSocial collects and uses this personal information pursuant to its legitimate interest in raising funds to ensure the sustainability of our nonprofit organization and, where applicable, to provide you with the merchandise, event, or program you purchased. 
Other Voluntarily Provided Information: 
When you provide feedback to NewsSocial, sign a petition distributed by NewsSocial, or otherwise submit personal information to NewsSocial, NewsSocial collects and uses this personal information pursuant to its legitimate interest in better understanding our community of supporters and volunteers and in furtherance of the particular program or activity to which you provided feedback or other input. 

4. Retention of Personal Information
The majority of the personal information collected and used as explained in Section 3 above is aggregated and stored in a central database provided by a third party service provider. NewsSocial aggregates this data pursuant to its legitimate interest in having information stored in a single location to minimize complexity, increase consistency in internal practices, better understand its community of supporters and volunteers, and enhance the security of the data.  
NewsSocial erases the web browser logs described above on a regular, rolling basis.
5. Access to Your Personal Information
You are generally entitled to access personal information that NewsSocial holds and to have inaccurate data corrected or removed to the extent NewsSocial still maintains it. In certain circumstances, you also may have the right to object for legitimate reasons to the processing or transfer of personal information. If you wish to exercise any of these rights, please write to info@mushtarek.org explaining your request.
6. Disclosure of Your Personal Information
NewsSocial does not disclose personal information to third parties except as specified elsewhere in this policy and in the following instances:
NewsSocial may share personal information with our contractors and service providers in order to undertake the activities described in Section 3.  
We may disclose your personal information to third parties in a good faith belief that such disclosure is reasonably necessary to (a) take action regarding suspected illegal activities; (b) enforce or apply our Master Terms and this Privacy Policy; (c) enforce our Charter, including the Code of Conduct and policies contained and incorporated therein, or (d) comply with legal process, such as a search warrant, subpoena, statute, or court order.
7. Security of Your Personal Information
NewsSocial has implemented reasonable physical, technical, and organizational security measures for personal information that NewsSocial processes against accidental or unlawful destruction, or accidental loss, alteration, unauthorized disclosure or access, in compliance with applicable law. However, no website can fully eliminate security risks. Third parties may circumvent our security measures to unlawfully intercept or access transmissions or private communications. If any data breach occurs, we will post a reasonably prominent notice to the Websites and comply with all other applicable data privacy requirements including, when required, personal notice to you if you have provided and we have maintained an email address for you.
The NewsSocial Login Services account systems have security risks in addition to those described above. Among other things, they are vulnerable to DNS attacks, and using any NewsSocial Login Service may increase the risk of phishing. 

8. Children
The Services are not directed at children under the age of 13. Consistent with the U.S. federal Children\u2019s Online Privacy Protection Act of 1998 (COPPA), we will never knowingly request personal information from anyone under the age of 13 without requiring parental consent. Our Master Terms specifically prohibit anyone using our Services from submitting any personally identifiable information about persons under 13 years of age. Any person who provides their personal information to NewsSocial through the Services represents that they are 13 years of age or older.

9. Third Party Service Providers
NewsSocial uses many third party service providers in connection with the Services, including website hosting services, database management, credit card processing, and many more. Some of these service providers may place session cookies on your computer, and they may collect and store your personal information on our behalf in accordance with the data practices and purposes explained above in Section 3. 
10. Third Party Sites
The Services may provide links to a wide variety of third party websites. You should consult the respective privacy policies of these third-party websites. This Privacy Policy does not apply to, and we cannot control the activities of, such other websites.

11. Transferring Data to Other Countries
If you are accessing or using the Services in regions with laws governing data collection, processing, transfer and use, please note that when we use and share your data as specified in this policy, we may transfer your information to recipients in countries other than the country in which the information was originally collected. Those countries may not have the same data protection laws as the country in which you initially provided the information. 
The Services are currently hosted in the United States and the United Kingdom, which means your personal information may be located on servers in the United States and/or the United Kingdom. The majority of contractors that NewsSocial is using as of the effective date of this Privacy Policy are located in the United Kingdom and Republic of Ireland, but this may change from time to time. 
Data transferred from the European Union to the United States or outside the European Union will be made on the grounds of a certification to the E.U./U.S. Privacy Shield regime and/or a data transfer agreement based on the Standard Contractual Clauses approved of by the European Commission respectively, consistent with applicable data privacy requirements.
12. Changes to this Privacy Policy
We may occasionally update this Privacy Policy. When we do, we will provide you with notice of such update through (at a minimum) a reasonably prominent notice on the Websites and Services, and will revise the Effective Date below. We encourage you to periodically review this Privacy Policy to stay informed about how we are protecting, using, processing and transferring the personal information we collect.
Effective Date: 25 July 2021.`;

const terms_and_conditions_en = `Terms of Use
NewsSocial terms of use
Based on an original by Creative Commons \u2013 adapted with thanks.

Effective as of 25 May 2022

1. General Information Regarding These Terms of Use
Thank you for your interest in NewsSocial.
NewsSocial is dedicated to building inclusive, democratic, evidenced-based, rational, and moderated discourse.  The process employed by NewsSocial and partners, commonly known as \u201CGroup Ideation\u201D (or \u201CCrowd Sourcing of Ideas and Designs\u201D; for short Ideation), with its associated methods, are designed to accelerate change via engagement and mobilization of a lot of people. The collaborative ideation of those engaged in the process is directed at developing the adequate and feasible solution to pressing social, economic, organizational, and educational problems.
To balance the needs and interests of all stakeholders, NewsSocial protects expressions that meet the Terms of Use outlined on this page.
Please read these terms. They will help you understand what type of expression is acceptable, and what type of content may be reported and removed.
Unless otherwise noted on a particular site or service, these master terms of use (\u201CMaster Terms\u201D) apply to your use of all of the websites that NewsSocial operates. These include https://newssocial.news, https://mushtarek.org, https://argon.today, together with all other subdomains thereof, (collectively, the \u201CWebsites\u201D). The Master Terms also apply to all products, information, and services provided through the Websites.
Additional terms: In addition to the Master Terms, your use of any Services may also be subject to specific terms applicable to a particular Service (\u201CAdditional Terms\u201D). If there is any conflict between the Additional Terms and the Master Terms, then the Additional Terms apply in relation to the relevant Service.
Collectively, the Terms: The Master Terms, together with any Additional Terms, form a binding legal agreement between you and NewsSocial in relation to your use of the Services. Collectively, this legal agreement is referred to below as the \u201CTerms.\u201D
Human-readable summary of Sec 1: These terms, together with any special terms for particular websites, create a contract between you and NewsSocial. The contract governs your use of all websites operated by NewsSocial, unless a particular website indicates otherwise. These human-readable summaries of each section are not part of the contract, but are intended to help you understand its terms.

2. Your Agreement to the Terms
BY CLICKING \u201CI ACCEPT\u201D OR OTHERWISE ACCESSING OR USING ANY OF THE SERVICES (INCLUDING THE LICENSES, PUBLIC DOMAIN TOOLS, AND CHOOSERS), YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREED TO BE BOUND BY THE TERMS. By clicking \u201CI ACCEPT\u201D or otherwise accessing or using any Services you also represent that you have the legal authority to accept the Terms on behalf of yourself and any party you represent in connection with your use of any Services. If you do not agree to the Terms, you are not authorized to use any Services. If you are an individual who is entering into these Terms on behalf of an entity, you represent and warrant that you have the power to bind that entity, and you hereby agree on that entity\u2019s behalf to be bound by these Terms, with the terms \u201Cyou,\u201D and \u201Cyour\u201D applying to you, that entity, and other users accessing the Services on behalf of that entity.
Human-readable summary of Sec 2: Please read these terms and only use our sites and services if you agree to them.

3. Changes to the Terms
From time to time, NewsSocial may change, remove, or add to the Terms, and reserves the right to do so in its discretion. In that case, we will post updated Terms and indicate the date of revision. If we feel the modifications are material, we will make reasonable efforts to post a prominent notice on the relevant Website(s) and notify those of you with a current NewsSocial Login Service account via email. All new and/or revised Terms take effect immediately and apply to your use of the Services from that date on, except that material changes will take effect 30 days after the change is made and identified as material. Your continued use of any Services after new and/or revised Terms are effective indicates that you have read, understood, and agreed to those Terms.
Human-readable summary of Sec 3: These terms may change. When the changes are important, we will put a notice on the website. If you continue to use the sites after the changes are made, you agree to the changes.

4. No Legal Advice
NewsSocial is not a law firm, does not provide legal advice, and is not a substitute for a law firm. Sending us an email or using any of the Services, including the licenses, public domain tools, and choosers, does not constitute legal advice or create an attorney-client relationship.
Human-readable summary of Sec 4: Some of us are lawyers, but we aren\u2019t your lawyer. Please consult your own attorney if you need legal advice.

5. Content Available through the Services
Provided as-is: You acknowledge that NewsSocial does not make any representations or warranties about the material, data, and information, such as data files, text, computer software, code, music, audio files or other sounds, photographs, videos, or other images (collectively, the \u201CContent\u201D) which you may have access to as part of, or through your use of, the Services. Under no circumstances is NewsSocial liable in any way for any Content, including, but not limited to: any infringing Content, any errors or omissions in Content, or for any loss or damage of any kind incurred as a result of the use of any Content posted, transmitted, linked from, or otherwise accessible through or made available via the Services. You understand that by using the Services, you may be exposed to Content that is offensive, indecent, or objectionable.
You agree that you are solely responsible for your reuse of Content made available through the Services, including providing proper attribution. You should review the terms of the applicable license before you use the Content so that you know what you can and cannot do.
Licensing: NewsSocial-owned public content: Other than the text of NewsSocial licenses.
Search Tools: On some of its Websites, NewsSocial provides website search tools which return Content based on any license information our search tools are able to locate and interpret. Those search tools may return Content that is not NewsSocial licensed, and you should independently verify the terms of the license attached to any Content you intend to use.
Human-readable summary of Sec 5: We try our best to have useful information on our sites, but we cannot promise that everything is accurate or appropriate for your situation. Public Content on the site is licensed under CC BY 4.0 unless it says it is available under different terms. If you find content through a link on our websites, be sure to check the license terms before using it.

6. Content Supplied by You
Your responsibility: You represent, warrant, and agree that no Content posted or otherwise shared by you on or through any of the Services (\u201CYour Content\u201D), violates or infringes upon the rights of any third party, including copyright, trademark, privacy, publicity, or other personal or proprietary rights, breaches or conflicts with any obligation, such as a confidentiality obligation, or contains libelous, defamatory, or otherwise unlawful material.
Licensing Your Content: You retain any copyright that you may have in Your Content. You hereby agree that Your Public Content: (a) is hereby licensed under the Creative Commons Attribution 4.0 License and may be used under the terms of that license or any later version of a Creative Commons Attribution License, or (b) is in the public domain (such as Content that is not copyrightable or Content you make available under CC0), or (c) if not owned by you, (i) is available under a Creative Commons Attribution 4.0 License or (ii) is a media file that is available under any Creative Commons license or that you are authorized by law to post or share through any of the Services, such as under the fair use doctrine, and that is prominently marked as being subject to third party copyright. All of Your Content must be appropriately marked with licensing (or other permission status such as fair use) and attribution information.
Removal: NewsSocial may, but is not obligated to, review Your Content and may delete or remove Your Content (without notice) from any of the Services in its sole discretion. Removal of any of Your Content from the Services (by you or NewsSocial) does not impact any rights you granted in Your Content under the terms of a NewsSocial license.
Human-readable summary of Sec 6: We do not take any ownership of your content when you post it on our sites. If you post Public content you own, you agree it can be used under the terms of CC BY 4.0 or any future version of that license. If you do not own the content, then you should not post it unless it is in the public domain or licensed CC BY 4.0, except that you may also post pictures and videos if you are authorized to use them under law (e.g., fair use) or if they are available under any CC license. You must note that information on the file when you upload it. You are responsible for any content you upload to our sites.

7. Joining in NewsSocial Circles or Groups: Registered Users
By registering for an account through any of the Services, including securing a NewsSocial Login Service account you represent and warrant that you are the age of majority in your jurisdiction (typically age 18). Services offered to registered users are provided subject to these Master Terms, the NewsSocial Privacy Policy, and any Additional Terms specified on the relevant Website(s), all of which are hereby incorporated by reference into these Terms.
Registration: You agree to (a) only provide accurate and current information about yourself (though use of an alias or nickname in lieu of your legal name is encouraged in connection with the NewsSocial Login Service), (b) maintain the security of your passwords and identification, (c) promptly update the email address listed in connection with your account to keep it accurate so that we can contact you, and (d) be fully responsible for all uses of your account. You must not set up an account on behalf of another individual or entity unless you are authorized to do so.
No Membership in NewsSocial: Creating a NewsSocial Login Service account or using any of the related Websites or Services does not and shall not be deemed to make you a member, shareholder or affiliate of NewsSocial for any purposes whatsoever, nor shall you have any of the rights of statutory members as defined under UK law or any other law.
Termination: NewsSocial reserves the right to modify or discontinue your account or your membership at any time for any reason or no reason at all.
Human-readable summary of Sec 7: Please do not register for an account on our sites unless you are 18 years old. NewsSocial has the right to end your account at any time. You are responsible for use of your account. And of course, please do not set up an account for someone else unless you have permission to do so. Setting up an account doesn\u2019t make you a member of NewsSocial.

8. Prohibited Conduct
You agree not to engage in any of the following activities:
8.1 Violating laws and rights:
You may not (a) use any Service for any illegal purpose or in violation of any local, state, national, or international laws, (b) violate or encourage others to violate any right of or obligation to a third party, including by infringing, misappropriating, or violating intellectual property, confidentiality, or privacy rights.
8.2 Solicitation:
You may not use the Services or any information provided through the Services for the transmission of advertising or promotional materials, including junk mail, spam, chain letters, pyramid schemes, or any other form of unsolicited or unwelcome solicitation.
8.3 Disruption:
You may not use the Services in any manner that could disable, overburden, damage, or impair the Services, or interfere with any other party\u2019s use and enjoyment of the Services; including by (a) uploading or otherwise disseminating any virus, adware, spyware, worm or other malicious code, or (b) interfering with or disrupting any network, equipment, or server connected to or used to provide any of the Services, or violating any regulation, policy, or procedure of any network, equipment, or server.
8.4 Harming others:
You may not post or transmit Content on or through the Services that is harmful, offensive, obscene, abusive, invasive of privacy, defamatory, hateful or otherwise discriminatory, false or misleading, or incites an illegal act;
You may not intimidate or harass another through the Services; and, you may not post or transmit any personally identifiable information about persons under 13 years of age on or through the Services.
8.5 Impersonation or unauthorized access:
You may not impersonate another person or entity, or misrepresent your affiliation with a person or entity when using the Services;
You may not use or attempt to use another\u2019s account or personal information without authorization; and
You may not attempt to gain unauthorized access to the Services, or the computer systems or networks connected to the Services, through hacking password mining or any other means.
Human-readable summary of Sec 8: Play nice. Be yourself. Don\u2019t break the law or be disruptive.

9. Participating in Circles: Discussing, Moderating and Reporting
As a registered user you will hopefully find that engagement with others in NewsSocial reflects cooperative values and is respectful, tolerant, engaged and committed.
Apps like NewsSocial with user-generated content must take precautions to moderate content and prevent abusive behaviour. These terms are clear that there is no tolerance for objectionable content or abusive users.
Any registered user may apply the provided mechanism to flag and report objectionable content to the Circle Administrator.
A filtering mechanism is provided to search and check inappropriate content.
NewsSocial is obliged to act on objectionable content reports within 24 hours.
Circle Administrators are empowered to remove content and to block users if necessary.
Human-readable summary of Sec 9: Engage as intended, report as necessary.

10. DISCLAIMER OF WARRANTIES
TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, NEWSSOCIAL OFFERS THE SERVICES (INCLUDING ALL CONTENT AVAILABLE ON OR THROUGH THE SERVICES) AS-IS AND MAKES NO REPRESENTATIONS OR WARRANTIES OF ANY KIND CONCERNING THE SERVICES, EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING WITHOUT LIMITATION, WARRANTIES OF TITLE, MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. NEWSSOCIAL DOES NOT WARRANT THAT THE FUNCTIONS OF THE SERVICES WILL BE UNINTERRUPTED OR ERROR-FREE, THAT CONTENT MADE AVAILABLE ON OR THROUGH THE SERVICES WILL BE ERROR-FREE, THAT DEFECTS WILL BE CORRECTED, OR THAT ANY SERVERS USED BY NEWSSOCIAL ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS. NEWSSOCIAL DOES NOT WARRANT OR MAKE ANY REPRESENTATION REGARDING USE OF THE CONTENT AVAILABLE THROUGH THE SERVICES IN TERMS OF ACCURACY, RELIABILITY, OR OTHERWISE.
Human-readable summary of Sec 10: NewsSocial does not make any guarantees about the sites, services, or content available on the sites.

11. LIMITATION OF LIABILITY
TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT WILL NEWSSOCIAL BE LIABLE TO YOU ON ANY LEGAL THEORY FOR ANY INCIDENTAL, DIRECT, INDIRECT, PUNITIVE, ACTUAL, CONSEQUENTIAL, SPECIAL, EXEMPLARY, OR OTHER DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF REVENUE OR INCOME, LOST PROFITS, PAIN AND SUFFERING, EMOTIONAL DISTRESS, COST OF SUBSTITUTE GOODS OR SERVICES, OR SIMILAR DAMAGES SUFFERED OR INCURRED BY YOU OR ANY THIRD PARTY THAT ARISE IN CONNECTION WITH THE SERVICES (OR THE TERMINATION THEREOF FOR ANY REASON), EVEN IF NEWSSOCIAL HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, NEWSSOCIAL IS NOT RESPONSIBLE OR LIABLE WHATSOEVER IN ANY MANNER FOR ANY CONTENT POSTED ON OR AVAILABLE THROUGH THE SERVICES (INCLUDING CLAIMS OF INFRINGEMENT RELATING TO THAT CONTENT), FOR YOUR USE OF THE SERVICES, OR FOR THE CONDUCT OF THIRD PARTIES ON OR THROUGH THE SERVICES.
Certain jurisdictions do not permit the exclusion of certain warranties or limitation of liability for incidental or consequential damages, which means that some of the above limitations may not apply to you. IN THESE JURISDICTIONS, THE FOREGOING EXCLUSIONS AND LIMITATIONS WILL BE ENFORCED TO THE GREATEST EXTENT PERMITTED BY APPLICABLE LAW.
Human-readable summary of Sec 11: NEWSSOCIAL is not responsible for the content on the sites, your use of our services, or for the conduct of others on our sites.

12. Indemnification
To the extent authorized by law, you agree to indemnify and hold harmless NewsSocial, their employees, officers, directors, affiliates, and agents from and against any and all claims, losses, expenses, damages, and costs, including reasonable attorneys\u2019 fees, resulting directly or indirectly from or arising out of (a) your violation of the Terms, (b) your use of any of the Services, and/or (c) the Content you make available on any of the Services.
Human-readable summary of Sec 12: If something happens because you violate these terms, because of your use of the services, or because of the content you post on the sites, you agree to repay NewsSocial for the damage it causes.

13. Privacy Policy
NewsSocial is committed to responsibly handling the information and data we collect through our Services in compliance with our Privacy Policy, which is incorporated by reference into these Master Terms. Please review the Privacy Policy so you are aware of how we collect and use your personal information.
Human-readable summary of Sec 13: Please read our Privacy Policy. It is part of these terms, too.

14. Trademark Policy
NewsSocial\u2019s name, logos, icons, and other trademarks may only be used in accordance with our Trademark Policy, which is incorporated by reference into these Master Terms. Please review the Trademark Policy so you understand how NewsSocial\u2019s trademarks may be used.
Human-readable summary of Sec 14: Please read our Trademark Policy. It is part of these terms, too.

15. Copyright Complaints
NewsSocial respects copyright, and we prohibit users of the Services from submitting, uploading, posting, or otherwise transmitting any Content on the Services that violates another person\u2019s proprietary rights.
To report allegedly infringing Content hosted on a website owned or controlled by NewsSocial, send a Notice of Infringing Materials to info@newssocial.co.uk
Human-readable summary of Sec 15: Please let us know if you find infringing content on our websites.

16. Termination
By NewsSocial: NewsSocial may modify, suspend, or terminate the operation of, or access to, all or any portion of the Services at any time for any reason. Additionally, your individual access to, and use of, the Services may be terminated by NewsSocial at any time and for any reason.
By you: If you wish to terminate this agreement, you may immediately stop accessing or using the Services at any time.
Automatic upon breach: Your right to access and use the Services (including use of your NewsSocial Login Service account) automatically ceases upon your breach of any of the Terms.
Survival: The disclaimer of warranties, the limitation of liability, and the jurisdiction and applicable law provisions will survive any termination. The license grants applicable to Your Content are not impacted by the termination of the Terms and shall continue in effect subject to the terms of the applicable license. Your warranties and indemnification obligations will survive for one year after termination.
Human-readable summary of Sec 16: If you violate these terms, you may no longer use our sites.
17. Miscellaneous Terms
Choice of law: The Terms are governed by and construed by the laws of the United Kingdom.
Dispute resolution: The parties agree that any disputes between NewsSocial and you concerning these Terms, and/or any of the Services may only be brought in a UK court.
No waiver: Either party\u2019s failure to insist on or enforce strict performance of any of the Terms will not be construed as a waiver of any provision or right.
Severability: If any part of the Terms is held to be invalid or unenforceable by any law or regulation or final determination of a competent court or tribunal, that provision will be deemed severable and will not affect the validity and enforceability of the remaining provisions.
No agency relationship: The parties agree that no joint venture, partnership, employment, or agency relationship exists between you and NewsSocial as a result of the Terms or from your use of any of the Services.
Integration: These Master Terms and any applicable Additional Terms constitute the entire agreement between you and NewsSocial relating to this subject matter and supersede any and all prior communications and/or agreements between you and NewsSocial relating to access and use of the Services.
Human-readable summary of Sec 17: If there is a lawsuit arising from these terms, it should be in UK law. We are glad you use our sites, but this agreement does not mean we are partners.`;

const make_readable = (text) => text.split(/(\r|\n)/);
const PrivacyText = () => /* @__PURE__ */ jsx("div", {
  className: "font-mono text-xs leading-relaxed",
  children: make_readable(privacy_policy_en).map((t, i) => /* @__PURE__ */ jsx("p", {
    className: "mt-3",
    children: t
  }, `_${i}`))
});
const TermsText = () => /* @__PURE__ */ jsx("div", {
  className: "font-mono text-xs leading-relaxed",
  children: make_readable(terms_and_conditions_en).map((t, i) => /* @__PURE__ */ jsx("p", {
    className: "mt-3",
    children: t
  }, `_${i}`))
});
const PolicyDialog = ({
  title,
  onClose,
  children
}) => {
  return createPortal(/* @__PURE__ */ jsxs("div", {
    className: "fixed inset-0 z-50 flex items-center justify-center",
    children: [/* @__PURE__ */ jsx("div", {
      className: "absolute inset-0 bg-black/50",
      onClick: onClose
    }), /* @__PURE__ */ jsxs("div", {
      className: "relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 flex flex-col max-h-[80vh]",
      children: [/* @__PURE__ */ jsxs("div", {
        className: "flex items-center justify-between px-6 py-4 border-b border-gray-200",
        children: [/* @__PURE__ */ jsx("h2", {
          className: "text-base font-semibold",
          children: title
        }), /* @__PURE__ */ jsx("button", {
          onClick: onClose,
          className: "text-gray-500 hover:text-gray-800 text-xl leading-none",
          "aria-label": "Close",
          children: "\xD7"
        })]
      }), /* @__PURE__ */ jsx("div", {
        className: "overflow-y-auto px-6 py-4 flex-1",
        children
      })]
    })]
  }), document.body);
};
const PrivacyPolicyDialog = ({
  onClose
}) => /* @__PURE__ */ jsx(PolicyDialog, {
  title: /* @__PURE__ */ jsx(FormattedMessage, {
    id: "general.privacy_policy"
  }),
  onClose,
  children: /* @__PURE__ */ jsx(PrivacyText, {})
});
const TermsAndConditionsDialog = ({
  onClose
}) => /* @__PURE__ */ jsx(PolicyDialog, {
  title: /* @__PURE__ */ jsx(FormattedMessage, {
    id: "general.terms_and_conditions"
  }),
  onClose,
  children: /* @__PURE__ */ jsx(TermsText, {})
});

const REREGISTER_FLAG = false;
const REGISTRATION_ERROR_KEYS = {
  email_exists: "validation_error.email_exists",
  username_exists: "validation_error.username_exists"
};
const toggleClassName = (active) => `flex-1 py-2 text-sm font-medium border-none cursor-pointer transition-colors ${active ? "bg-blue-600 text-white" : "bg-white text-gray-700 hover:bg-gray-100"}`;
const RegistrationForm = ({
  postId
}) => {
  const {
    state,
    setUserId,
    setState
  } = useRegistrationContext();
  const api = useApi();
  const [method, setMethod] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreeToTerms, setAgreeToTerms] = useBoolean(false);
  const [alreadyRegistered, setAlreadyRegistered] = useBoolean(false);
  const [validEmailError, setValidEmailError] = useState("");
  const [validPasswordError, setValidPasswordError] = useState("");
  const [passwordsDoNotMatchError, setPasswordsDoNotMatchError] = useState("");
  const [showPrivacy, setShowPrivacy] = useBoolean();
  const [showTerms, setShowTerms] = useBoolean();
  const isValueAvailable = async (value) => {
    try {
      const user = await api.loadUserByEmail(value);
      return !user;
    } catch (err) {
      if (err.code === 404) {
        return true;
      } else {
        return false;
      }
    }
  };
  const passwordsMatch = () => {
    if (password !== confirmPassword) {
      setPasswordsDoNotMatchError("validation_error.passwords_must_match");
    } else {
      setPasswordsDoNotMatchError("");
    }
  };
  useEffect(() => {
    passwordsMatch();
  }, [password, confirmPassword]);
  const handleEmailChange = async (e) => {
    const value = e.target.value;
    setEmail(value);
    setValidEmailError(isValidateEmail(value));
    const available = await isValueAvailable(value);
    setAlreadyRegistered.set(!available);
  };
  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setValidPasswordError(isValidPassword(value));
    passwordsMatch();
  };
  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    passwordsMatch();
  };
  const handleAgreeToTermsChange = () => {
    setAgreeToTerms.toggle();
  };
  const submit = useAsyncOperation(async () => {
    const parts = location.href.split("#");
    const mobile = await mobilePhoneData2Save();
    const result = await api.registerWithEmail({
      mobiles: mobile ? [mobile] : null,
      password: stripAllWhitespaces(password),
      email: stripAllWhitespaces(email),
      postId,
      tags: [],
      url: parts[0] + "#?state=verify&pin=<pin>&userId=<userId>"
    });
    if (result.email || result.phone) {
      setUserId(result._id);
    } else {
      throw new Error(typeof result === "string" ? result : result.error || "Registration failed");
    }
  }, {
    showErrorToast: false
  });
  const thereAreErrors = !password || !email || alreadyRegistered || validEmailError || validPasswordError || passwordsDoNotMatchError;
  return /* @__PURE__ */ jsxs("div", {
    className: `flex-col items-center p-6 space-y-4 bg-white shadow-md rounded-lg w-full max-w-md mx-auto overflow-y-auto max-h-full ${state === "register" ? "flex" : "hidden"}`,
    children: [/* @__PURE__ */ jsx("h1", {
      className: "text-xl font-bold",
      children: /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.register"
      })
    }), /* @__PURE__ */ jsxs("div", {
      className: "flex w-full rounded-md border border-gray-300 overflow-hidden",
      role: "tablist",
      children: [/* @__PURE__ */ jsx("button", {
        role: "tab",
        "aria-selected": method === "email",
        className: toggleClassName(method === "email"),
        onClick: () => setMethod("email"),
        children: /* @__PURE__ */ jsx(FormattedMessage, {
          id: "auth.email"
        })
      }), /* @__PURE__ */ jsx("button", {
        role: "tab",
        "aria-selected": method === "phone",
        className: toggleClassName(method === "phone"),
        onClick: () => setMethod("phone"),
        children: /* @__PURE__ */ jsx(FormattedMessage, {
          id: "auth.phone2"
        })
      })]
    }), method === "email" ? /* @__PURE__ */ jsxs(Fragment, {
      children: [/* @__PURE__ */ jsx(TextInput, {
        label: /* @__PURE__ */ jsx(FormattedMessage, {
          id: "auth.email"
        }),
        type: "email",
        value: email,
        onChange: handleEmailChange
      }), alreadyRegistered && /* @__PURE__ */ jsx("div", {
        className: "text-red-500 text-sm",
        children: /* @__PURE__ */ jsx(FormattedMessage, {
          id: "registration_form.already_registered2"
        })
      }), validEmailError && /* @__PURE__ */ jsx("div", {
        className: "text-red-500 text-sm",
        children: /* @__PURE__ */ jsx(FormattedMessage, {
          id: validEmailError
        })
      }), /* @__PURE__ */ jsx(PasswordInput, {
        label: /* @__PURE__ */ jsx(FormattedMessage, {
          id: "auth.password"
        }),
        value: password,
        onChange: handlePasswordChange,
        error: validPasswordError ? /* @__PURE__ */ jsx(FormattedMessage, {
          id: validPasswordError
        }) : void 0
      }), /* @__PURE__ */ jsx(PasswordInput, {
        label: /* @__PURE__ */ jsx(FormattedMessage, {
          id: "auth.confirm"
        }),
        value: confirmPassword,
        onChange: handleConfirmPasswordChange,
        error: passwordsDoNotMatchError ? /* @__PURE__ */ jsx(FormattedMessage, {
          id: passwordsDoNotMatchError
        }) : void 0
      }), /* @__PURE__ */ jsxs("label", {
        className: "flex items-center gap-2 cursor-pointer text-sm self-start",
        children: [/* @__PURE__ */ jsx("input", {
          type: "checkbox",
          checked: agreeToTerms,
          onChange: handleAgreeToTermsChange,
          className: "w-4 h-4"
        }), /* @__PURE__ */ jsx(FormattedMessage, {
          id: "auth.agree"
        })]
      }), /* @__PURE__ */ jsx(LoadingButtonTW, {
        background: "primary",
        loading: submit.isLoading,
        disabled: (!agreeToTerms || !!thereAreErrors) && !REREGISTER_FLAG,
        onClick: submit.execute,
        className: "w-full",
        children: /* @__PURE__ */ jsx(FormattedMessage, {
          id: submit.isLoading ? "auth.registering" : "auth.register"
        })
      }), submit.error && /* @__PURE__ */ jsx("div", {
        className: "text-red-500 text-sm w-full",
        children: REGISTRATION_ERROR_KEYS[submit.error.message] ? /* @__PURE__ */ jsx(FormattedMessage, {
          id: REGISTRATION_ERROR_KEYS[submit.error.message]
        }) : submit.error.message || /* @__PURE__ */ jsx(FormattedMessage, {
          id: "auth.otpFailed"
        })
      })]
    }) : /* @__PURE__ */ jsx(PhoneOtpFields, {}), /* @__PURE__ */ jsx("button", {
      className: "bg-transparent border-none p-0 m-0 text-blue-500 underline hover:text-blue-700 cursor-pointer",
      onClick: () => setState("login"),
      children: /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.alreadyHaveAccount"
      })
    }), /* @__PURE__ */ jsx(OrRuler, {}), /* @__PURE__ */ jsx(NewGoogleButton, {}), /* @__PURE__ */ jsxs("div", {
      className: "flex gap-2 items-center justify-center text-xs",
      children: [/* @__PURE__ */ jsx("button", {
        onClick: setShowPrivacy.toggle,
        className: "bg-transparent border-none p-0 m-0 cursor-pointer text-blue-500 underline hover:text-blue-700",
        children: /* @__PURE__ */ jsx(FormattedMessage, {
          id: "registration.privacy_policy"
        })
      }), /* @__PURE__ */ jsx("button", {
        onClick: setShowTerms.toggle,
        className: "bg-transparent border-none p-0 m-0 cursor-pointer text-blue-500 underline hover:text-blue-700",
        children: /* @__PURE__ */ jsx(FormattedMessage, {
          id: "registration.terms"
        })
      })]
    }), showPrivacy && /* @__PURE__ */ jsx(PrivacyPolicyDialog, {
      onClose: setShowPrivacy.toggle
    }), showTerms && /* @__PURE__ */ jsx(TermsAndConditionsDialog, {
      onClose: setShowTerms.toggle
    })]
  });
};

const CONFIRM_ERROR_KEYS = {
  invalid_pin: "auth.otpInvalid",
  no_such_user: "auth.otpInvalid",
  invalid_link: "auth.otpInvalid"
};
const ConfirmationCode = () => {
  const {
    state,
    setState,
    userId,
    pin
  } = useRegistrationContext();
  const api = useApi();
  const onLoginSuccess = useOnLoginSuccess();
  const [code, setCode] = useState("");
  const trimmedCode = code.trim();
  const handleCodeChange = (e) => {
    setCode(e.target.value);
  };
  useEffect(() => {
    setCode(pin || "");
  }, [pin]);
  const submit = useAsyncOperation(async () => {
    if (!trimmedCode) {
      return;
    }
    const user = await api.confirmEmailWithPin({
      userId,
      pin: trimmedCode
    });
    if (user && user.verified && user._id === userId) {
      await onLoginSuccess({
        result: user
      });
    } else {
      throw Error("Verification failed, try again!");
    }
  }, {
    showErrorToast: false
  });
  useEffect(() => {
    if (pin === code && userId && pin) {
      submit.execute();
    }
  }, [code, userId, pin]);
  return /* @__PURE__ */ jsxs("div", {
    className: `flex-col items-center p-6 space-y-4 bg-white shadow-md rounded-lg w-full max-w-md mx-auto ${state === "verify" ? "flex" : "hidden"}`,
    children: [/* @__PURE__ */ jsx("h1", {
      className: "text-xl font-bold",
      children: /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.verificationCode"
      })
    }), /* @__PURE__ */ jsx(TextInput, {
      label: /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.confirmationCode"
      }),
      type: "text",
      value: code,
      onChange: handleCodeChange
    }), /* @__PURE__ */ jsx(LoadingButtonTW, {
      background: "primary",
      loading: submit.isLoading,
      disabled: !trimmedCode,
      onClick: submit.execute,
      className: "w-full",
      children: /* @__PURE__ */ jsx(FormattedMessage, {
        id: submit.isLoading ? "auth.verify2" : "auth.verify"
      })
    }), submit.error && /* @__PURE__ */ jsx("div", {
      className: "text-red-500 text-sm w-full",
      children: CONFIRM_ERROR_KEYS[submit.error.response] ? /* @__PURE__ */ jsx(FormattedMessage, {
        id: CONFIRM_ERROR_KEYS[submit.error.response]
      }) : submit.error.message || /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.otpFailed"
      })
    }), /* @__PURE__ */ jsx("button", {
      className: "bg-transparent border-none p-0 m-0 text-blue-500 underline hover:text-blue-700 cursor-pointer",
      onClick: () => setState("login"),
      children: /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.alreadyHaveAccount"
      })
    })]
  });
};

const FORGOT_PASSWORD_ERROR_KEYS = {
  "no_such_user": "auth.noSuchAccount",
  "email_address_hard_bounced": "auth.emailBounced"
};
const errorKey$1 = (error) => error && FORGOT_PASSWORD_ERROR_KEYS[error.response] || "auth.resetFailed";
const looksLikePhoneNumber = (value) => !!value.trim() && isValidPhoneNumber(value.trim()) === null;
const ForgotPassword = () => {
  const {
    state,
    setState
  } = useRegistrationContext();
  const api = useApi();
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const handleChange = (e) => {
    setEmail(e.target.value);
  };
  const isPhoneNumber = looksLikePhoneNumber(email);
  const submit = useAsyncOperation(async () => {
    setEmailSent(false);
    const parts = location.href.split("#");
    await api.post({
      path: "/public/users/forgot-password",
      body: {
        email: email.trim(),
        url: parts[0] + "#?state=change-password&token=<token>"
      }
    });
    setEmailSent(true);
  }, {
    showErrorToast: false
  });
  return /* @__PURE__ */ jsxs("div", {
    className: `flex-col items-center p-6 space-y-4 bg-white shadow-md rounded-lg w-full max-w-md mx-auto overflow-y-auto max-h-full ${state === "forgot" ? "flex" : "hidden"}`,
    children: [/* @__PURE__ */ jsx("h1", {
      className: "text-xl font-bold",
      children: /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.reset"
      })
    }), /* @__PURE__ */ jsx(TextInput, {
      label: /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.email"
      }),
      type: "text",
      value: email,
      onChange: handleChange
    }), /* @__PURE__ */ jsx("div", {
      className: `${isPhoneNumber ? "text-red-500" : "text-gray-500"} text-sm w-full`,
      children: /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.phoneNoPassword"
      })
    }), /* @__PURE__ */ jsx(LoadingButtonTW, {
      background: "primary",
      loading: submit.isLoading,
      disabled: isPhoneNumber,
      onClick: submit.execute,
      className: "w-full",
      children: /* @__PURE__ */ jsx(FormattedMessage, {
        id: submit.isLoading ? "auth.resetting" : "auth.reset"
      })
    }), /* @__PURE__ */ jsx("button", {
      className: "bg-transparent border-none p-0 m-0 text-blue-500 underline hover:text-blue-700 cursor-pointer",
      onClick: () => setState("register"),
      children: /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.register2"
      })
    }), submit.error && /* @__PURE__ */ jsx("div", {
      className: "text-red-500 text-sm w-full",
      children: /* @__PURE__ */ jsx(FormattedMessage, {
        id: errorKey$1(submit.error)
      })
    }), emailSent && /* @__PURE__ */ jsx("div", {
      children: /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.sentEmail"
      })
    })]
  });
};

const CHANGE_PASSWORD_ERROR_KEYS = {
  "Token expired": "auth.resetLinkExpired",
  "Signature verification failed": "auth.resetLinkExpired",
  "Not enough or too many segments": "auth.resetLinkExpired",
  "No token supplied": "auth.resetLinkExpired",
  "Token not yet active": "auth.resetLinkExpired",
  "no_such_user": "auth.resetLinkExpired",
  "Bad Request": "validation_error.password_too_short"
};
const errorKey = (error) => error && CHANGE_PASSWORD_ERROR_KEYS[error.response] || "auth.changeFailed";
const ChangePassword = () => {
  const {
    state,
    token
  } = useRegistrationContext();
  const api = useApi();
  const onLoginSuccess = useOnLoginSuccess();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validPasswordError, setValidPasswordError] = useState("");
  const [passwordsDoNotMatchError, setPasswordsDoNotMatchError] = useState("");
  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setValidPasswordError(isValidPassword(value));
  };
  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
  };
  useEffect(() => {
    setPasswordsDoNotMatchError(password && confirmPassword && password !== confirmPassword ? "validation_error.passwords_must_match" : "");
  }, [password, confirmPassword]);
  const thereAreErrors = !password || !confirmPassword || !!validPasswordError || !!passwordsDoNotMatchError;
  const submit = useAsyncOperation(async () => {
    const user = await api.loginWithJwt(token);
    await api.resetPassword({
      password
    });
    await onLoginSuccess({
      result: user
    });
  }, {
    showErrorToast: false
  });
  return /* @__PURE__ */ jsxs("div", {
    className: `flex-col items-center p-6 space-y-4 bg-white shadow-md rounded-lg w-full max-w-md mx-auto overflow-y-auto max-h-full ${state === "change-password" ? "flex" : "hidden"}`,
    children: [/* @__PURE__ */ jsx("h1", {
      className: "text-xl font-bold",
      children: /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.change"
      })
    }), /* @__PURE__ */ jsx(PasswordInput, {
      label: /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.newPassword"
      }),
      value: password,
      onChange: handlePasswordChange,
      error: validPasswordError ? /* @__PURE__ */ jsx(FormattedMessage, {
        id: validPasswordError
      }) : void 0
    }), /* @__PURE__ */ jsx(PasswordInput, {
      label: /* @__PURE__ */ jsx(FormattedMessage, {
        id: "auth.confirm"
      }),
      value: confirmPassword,
      onChange: handleConfirmPasswordChange,
      error: passwordsDoNotMatchError ? /* @__PURE__ */ jsx(FormattedMessage, {
        id: passwordsDoNotMatchError
      }) : void 0
    }), /* @__PURE__ */ jsx(LoadingButtonTW, {
      background: "primary",
      loading: submit.isLoading,
      disabled: !!thereAreErrors,
      onClick: submit.execute,
      className: "w-full",
      children: /* @__PURE__ */ jsx(FormattedMessage, {
        id: submit.isLoading ? "auth.change2" : "auth.change"
      })
    }), submit.error && /* @__PURE__ */ jsx("div", {
      className: "text-red-500 text-sm w-full",
      children: /* @__PURE__ */ jsx(FormattedMessage, {
        id: errorKey(submit.error)
      })
    })]
  });
};

const BookAuthGateInner = ({
  postId,
  onClose
}) => {
  useSetOnLoginUrl();
  return /* @__PURE__ */ jsxs("div", {
    className: "fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-8",
    onClick: onClose ? (e) => {
      if (e.target === e.currentTarget)
        onClose();
    } : void 0,
    children: [onClose && /* @__PURE__ */ jsx("button", {
      type: "button",
      "aria-label": "Close",
      onClick: onClose,
      className: "fixed top-4 right-4 z-[51] w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-md hover:bg-gray-100 text-[20px] leading-none text-[color:var(--cair-text,#020203)]",
      children: "\u2715"
    }), /* @__PURE__ */ jsx(LoginForm, {}), /* @__PURE__ */ jsx(RegistrationForm, {
      postId
    }), /* @__PURE__ */ jsx(PhoneOtpForm, {}), /* @__PURE__ */ jsx(ConfirmationCode, {}), /* @__PURE__ */ jsx(ForgotPassword, {}), /* @__PURE__ */ jsx(ChangePassword, {})]
  });
};
const BookAuthGate = ({
  postId,
  onClose
}) => /* @__PURE__ */ jsx(RegistrationContextProvider, {
  children: /* @__PURE__ */ jsx(BookAuthGateInner, {
    postId,
    onClose
  })
});

const ARABIC = "ar";
const DEFAULT_LANGUAGE = "en";
const AVAILABLE_LANGUAGES = [ARABIC, DEFAULT_LANGUAGE];
const getBrowserLanguage = () => {
  return (typeof localStorage !== "undefined" && localStorage && localStorage.language || typeof navigator !== "undefined" && (navigator.language || navigator["userLanguage"] || navigator["browserLanguage"]) || DEFAULT_LANGUAGE).split("-")[0];
};
const rtlLanguages = ["ar", "ku", "ur", "fa"];
const getDirection = (language) => {
  if (rtlLanguages.includes(language)) {
    return "rtl";
  }
  return "ltr";
};
const useLanguage = (browserLanguage, options) => {
  const {
    loadPolyfills
  } = options;
  const [data, setData] = useState(setupLanguage(browserLanguage, options));
  const {
    language,
    loading
  } = data;
  translations = data.translations;
  useEffect(() => {
    if (loading) {
      loadPolyfills({
        language
      }).then(() => setData((data2) => ({
        ...data2,
        loading: false
      })));
    }
  }, [language, loading]);
  return data;
};
const chooseLanguage = (browserLanguage) => {
  let language = DEFAULT_LANGUAGE;
  if (browserLanguage) {
    if (AVAILABLE_LANGUAGES.find((lang) => lang === browserLanguage)) {
      language = browserLanguage;
    }
  }
  return language;
};
const setupLanguage = (browserLanguage, options) => {
  const {
    shouldPolyfill = () => false,
    getTranslation
  } = options;
  const language = chooseLanguage(browserLanguage);
  const translations2 = getTranslation(language);
  return {
    language,
    translations: translations2,
    loading: shouldPolyfill()
  };
};
let translations;
if (typeof window !== "undefined") {
  window["translations"] = () => translations;
}

const flatten = (json) => {
  let result = {};
  Object.getOwnPropertyNames(json).forEach((name) => {
    let v = json[name];
    if (typeof v === "string") {
      result[name] = v;
    } else {
      let nested = flatten(v);
      Object.getOwnPropertyNames(nested).forEach((n) => {
        result[`${name}.${n}`] = nested[n];
      });
    }
  });
  return result;
};

const plurals$1 = {
  and_one_other: 'وواحد آخر',
  and_n_others: 'و {n} آخرون',
  view: 'مشاهدة',
  one_response: 'استجابة واحدة',
  n_responses: '{responseCount} استجابات',
  one_elided_post: 'مشاركة واحدة',
  n_elided_posts: '{length} مشاركات',
  one_member: 'عضو واحد',
  n_members: '{memberCount} أعضاء}',
  one_post: 'مشاركة واحدة',
  n_posts: '{postCount} مشاركات',
  one_pending_request: 'مشاركة واحدة معلقة',
  n_pending_requests: '{access_request_count} طلبات معلقة}'
};

var ar = {
  chat: {
    pin: "تثبيت الرسالة",
    unpin: "إلغاء تثبيت الرسالة",
    edit: "يحرر",
    loadMore: "تحميل المزيد...",
    search1: "أدخل أي سؤال في نافذة البحث، مثل 'الخدمات الصحية في بريستون'، ثم انقر فوق زر تحليل الذكاء الاصطناعي، أو اضغط على 'رجوع'",
    search2: "للبحث بالذكاء الاصطناعي، انقر على أيقونة CAIR، ثم انقر على زر البحث بالذكاء الاصطناعي، أو اضغط على Return",
    search3: "للبحث عن الكلمات الرئيسية، انقر على أيقونة العدسة المكبرة ثم أدخل الكلمة الرئيسية ثم اضغط على 'رجوع'"
  },
  auth: {
    or: "أو",
    newPassword: "كلمة المرور الجديدة",
    change2: "يتغير...",
    change: "تغيير كلمة المرور",
    forgot: "هل نسيت كلمة السر؟",
    verify2: "يؤكد...",
    verify: "تأكيد",
    verificationCode: "رمز التحقق",
    confirmationCode: "رمز التأكيد",
    emailOrPhone: "البريد الإلكتروني أو رقم الهاتف",
    resetting: "إعادة الضبط...",
    reset: "إعادة تعيين كلمة المرور",
    sentEmail: "لقد أرسلنا إليك رابطًا لإعادة تعيين كلمة المرور. يرجى التحقق من بريدك الإلكتروني.",
    phoneNoPassword: "هل سجلت باستخدام هاتفك؟ لا تحتاج إلى كلمة مرور \u2014 سجل الدخول برقم هاتفك وسنرسل إليك رمزًا في رسالة نصية.",
    resetLinkExpired: "انتهت صلاحية رابط إعادة تعيين كلمة المرور. يرجى طلب رابط جديد.",
    changeFailed: "لم نتمكن من تغيير كلمة المرور، يرجى المحاولة مرة أخرى.",
    noSuchAccount: "لم نتمكن من العثور على حساب بهذا البريد الإلكتروني.",
    emailBounced: "لا يمكننا إرسال بريد إلكتروني إلى هذا العنوان. يرجى التواصل مع الدعم.",
    resetFailed: "لم نتمكن من إرسال بريد إعادة التعيين، يرجى المحاولة مرة أخرى.",
    register2: "مستخدم جديد؟ قم بالتسجيل!",
    registerWithPhone: "سجل باستخدام هاتفك",
    phone: "رقم التليفون",
    email: "بريد إلكتروني",
    password: "كلمة المرور",
    confirm: "تأكيد كلمة المرور",
    register: "يسجل",
    registering: "جاري إنشاء الحساب...",
    agree: "أوافق على شروط الخدمة",
    alreadyHaveAccount: "هل لديك حساب بالفعل؟ قم بتسجيل الدخول!",
    registerWithPhone: "سجل برقم الهاتف",
    login2access: "الرجاء تسجيل الدخول للوصول إلى هذه الصفحة",
    authRequired: "مطلوب المصادقة",
    register: "إنشاء حساب",
    login: "المستخدم الحالي: تسجيل الدخول",
    login2: "تسجيل الدخول",
    login3: "جاري تسجيل الدخول...",
    continueWithPhone: "المتابعة باستخدام الهاتف",
    phone2: "الهاتف",
    country: "الدولة",
    sendCode: "إرسال الرمز",
    sendingCode: "جاري إرسال الرمز...",
    codeSent: "لقد أرسلنا رمز التحقق إلى هاتفك عبر رسالة نصية.",
    phoneLengthExact: "يجب أن يتكون رقم الهاتف من {n} أرقام (بدون الصفر في البداية).",
    phoneLengthRange: "يجب أن يتكون رقم الهاتف من {min} إلى {max} رقمًا (بدون الصفر في البداية).",
    enterCode: "رمز التحقق",
    resendCode: "إعادة إرسال الرمز",
    resendCodeIn: "إعادة إرسال الرمز خلال {seconds} ثانية",
    sendCodeWhatsApp: "استلام الرمز عبر واتساب",
    changePhone: "استخدام رقم هاتف آخر",
    otpInvalid: "الرمز غير صحيح، يرجى المحاولة مرة أخرى.",
    otpExpired: "انتهت صلاحية الرمز، يرجى طلب رمز جديد.",
    otpTooManyAttempts: "محاولات كثيرة جدًا، يرجى طلب رمز جديد.",
    otpCooldown: "يرجى الانتظار دقيقة قبل طلب رمز آخر.",
    otpRateLimited: "طلبات كثيرة جدًا، يرجى المحاولة لاحقًا.",
    smsSendFailed: "تعذر إرسال الرسالة النصية، يرجى المحاولة مرة أخرى.",
    otpCountryNotSupported: "لا يمكننا إرسال رموز إلى هذا البلد. يرجى التسجيل بالبريد الإلكتروني.",
    otpCaptchaPending: "جارٍ التحقق من متصفحك\u2026",
    otpFailed: "حدث خطأ ما، يرجى المحاولة مرة أخرى.",
    biometricLogin: "تسجيل الدخول بالبصمة",
    biometricFailed: "فشل تسجيل الدخول بالبصمة، يرجى تسجيل الدخول مرة أخرى.",
    passkeyLogin: "تسجيل الدخول بمفتاح المرور",
    passkeyFailed: "فشل تسجيل الدخول بمفتاح المرور، يرجى المحاولة مرة أخرى.",
    passkeyAdd: "إضافة مفتاح مرور",
    passkeyAdded: "تمت إضافة مفتاح المرور",
    passkeyEnrollFailed: "تعذر إضافة مفتاح المرور، يرجى المحاولة مرة أخرى."
  },
  CloseDiscussion: 'اغلق النقاش مع هذه كمحصلة',
  reopen: 'أعد فتح المناقشة',
  toggleReponse: 'تبديل الردود',
  CreateZoomMeeting: "إنشاء اجتماع التكبير",
  close: "أغلق",
  people: "اشخاص",
  outcome: 'المحصلة',
  StartaDiscussion: "ابدأ مناقشة",

  authentication_required: 'يستلزم التوثيق',
  statusShort: {
    event: {
      comeLater: "انتظار",
      reportAvailable: "تم الانتهاء من",
      aboutToStart: "ابتداء",
      joinMeeting: "حي",
    }
  },
  ai: {
    details: "تفاصيل مساعد الذكاء الاصطناعي",
    name: "اسم المساعد",
    description: "وصف مساعد",
    selectAvatar: "حدد الصورة الرمزية للمساعد",
    selectIcon: "انقر لتحديد الرمز",
    operation: "عملية مساعد الذكاء الاصطناعي",
    selectMainPrompt: "حدد الموجه الرئيسي",
    freq: "تكرار تنفيذ الموجه (بالثواني)",
    manual: "يدوي",
    schedule: "جدول",
    off: "عن",
    hideUI: "إخفاء العمليات التي يدعمها المستخدم",
    showUI: "إظهار العمليات التي يدعمها المستخدم",
    promptSelection: "اختيار سريع",
    add: "يضيف",
    selectPrompt: "حدد مطالبة",
    thread: "موضوع المناقشة",
    editor: "قائمة المحرر",
    circle: "قائمة الدائرة",
    alias: "الاسم المستعار للقائمة",
    delete: "يمسح",
    cancel: "يلغي",
    save: "يحفظ",
    clickOn: "انقر فوق ثم أدخل التعليمات الخاصة بمساعدة الذكاء الاصطناعي في نافذة المحرر",
    ask: "رسالة",
    disable: "تعطيل الذكاء الاصطناعي",
    enable: "تمكين الذكاء الاصطناعي",
    addAI: "إضافة مساعد الذكاء الاصطناعي",
    active: "نشيط",
    notActive: "غير فعال",
    edit: "يحرر",
    delete: "يمسح",
    createdBy: "انشأ من قبل",
    refresh: "ينعش",
    running: "جري",
    notRunning: "لا يعمل",
    lastExecuted: "تم تنفيذه مؤخرًا",
    service: "خدمة",
    update: "تحديث",
    success: "نجاح",
    logs: "السجلات",
    stop: "قف",
    start: "يبدأ",
    continue: "يكمل",
    pause: "يوقف",
  },
  common: {
    create: "إنشاء حلقة",
  },
  dashboard: {
    breadcrumb: "لوحة التحكم",
    goToBookCair: "الذهاب إلى BOOK CAIR",
    tooltip: {
      profile: "عالمي: ملفي الشخصي والمحتوى الخاص بي",
      allPosts: "مساهمات للعلامات التي أتابعها من جميع الحلقات التي يمكنني الوصول إليها",
      circles: "الحلقات التي انضممت إليها",
      discussions: "المناقشات التي أنا عضو فيها",
      meetings: "الاجتماعات التي أنا مدعو إليها",
      subCircles: "انقر لمشاهدة الدوائر الفرعية",
      parentCircle: "انقر لرؤية الدائرة الرئيسية",
      postDetails: "انقر للحصول على مزيد من التفاصيل",
    },
    circles: "حلقاتي",
    discussions: "نقاشاتي",
    meetings: "اجتماعاتي",
    allPosts: "جميع المشاركات",
    showMore: "أظهار المزيد",
    discover: "اكتشاف",
    completeProfile: "الرجاء إكمال الملف الشخصي",
  },
  event: {
    types: {
      Music: "موسيقى",
      Culture: "ثقافة",
      Training: "تمرين",
      Meeting: "اجتماع",
      Sport: "رياضة",
      Information: "معلومة"
    },
    responded: "استجاب",
    maybe: "ربما",
    no: "لا",
    yes: "نعم شخصيا",
    yes_v: "نعم، انضم افتراضيًا",
    yes1: "نعم",
    going: "ذاهب",
    show_more: "أظهر المزيد",
    respondents: "قائمة المستجيبين",
    organizer: "منظم",
    address: "عنوان",
    start: "بداية الحدث",
    end: "نهاية الحدث",
    addTags: "اضف اشارة",
    name: "اسم",
    address: "عنوان",
    priority: "أولوية الحدث",
    moreInfo: "إضافة المزيد من المعلومات/نموذج التمديد",
    collapse: "طي النموذج",
    cancel: "يلغي",
    save: "يحفظ",
    roles: "تحديد الأدوار لاجتماع أو حدث تدريب",
    location: "موقع الحدث",
    startAndEnd: "بداية ونهاية الحدث",
    eventType: "نوع الحدث",
    describe: "قم بوصف الحدث الخاص بك",
    sponsor: "المنظمة الراعية (أصول البحث)",
    noData: "لا توجد أحداث لهذه الحلقة، لماذا لا تبدأ بحدث جديد للحلقة!",
    finishMeeting: "انتهى الاجتماع",
    listing: "قائمة",
    upcoming: "القادمة",
    recent: "مؤخرا",
    hosting: "الاستضافة",
    speaking: "مشاركة",
    andOthers: "و {count} آخرين",
    startsPrefix: "يبدأ:",
    endsPrefix: "ينتهي:",
    comeLater: "انضم إلى الحدث هنا:",
    reportAvailable: "تقارير الاجتماع متوفرة أدناه ، نرحب بالتعليقات",
    reportAvailableAdmin: "يرجى كتابة نتيجة الاجتماع أدناه عندما تكون جاهزًا",
    aboutToStart: "الاجتماع على وشك أن يبدأ",
    joinMeeting: "انضم للاجتماع",
    openZoomApp: "افتح باستخدام تطبيق Zoom",
    openZoomAppBefore: "يفتح قبل 30 دقيقة من البدء",
    webClient: "عميل الويب ({op})",
    Host: "مضيف",
    Join: "ينضم",
    Assistant: "مساعد",
    more: "أكثر",
    see: "نرى",
    sendInvitation: "إرسال رسائل البريد الإلكتروني للدعوة",
    all_party: "جميع أعضاء حلقة النقاش",
    templates: {
      created: "تم إنشاء الحدث",
      eventsReminder24: "تذكير قبل 24 ساعة",
      eventsReminder1: "تذكير قبل ساعة واحدة",
      eventsFinished: "انتهى الحدث",
    },
  },
  general: {
    ok: 'موافق',
    MeetingTime: "وقت الاجتماع:",
    cancel: 'إلغاء',
    prev: 'سابق',
    next: 'تالى',
    or: 'أو',
    requires_registration: 'التسجيل مطلوب',
    access_denied: "تم الرفض",
    request_access_to_circle: "طلب الوصول إلى الدائرة",
    request_access_to_circle2: "عذرًا، ليس لديك حقوق الوصول إلى هذه الدائرة. انقر أعلاه للانضمام",
    or_click: "أو أضغط هنا.",
    join2read: "انضم إلى الدائرة لقراءة المشاركة",
    automatically_redirect2circle: "سنقوم بإعادة توجيهك تلقائيًا إلى الدائرة للانضمام.",
    no_rights2view_post: "عذرًا، ليس لديك حقوق الوصول لعرض هذه المشاركة.",
    requires_registration_details:
      'سجّل حساب لتمكين مجموعة من الوظائف الإضافية.',
    already_a_member: 'هل أنت عضوا؟',
    sign_up: 'إشترك',
    sign_in: 'تسجيل الدخول',
    untitled: 'مقالة بدون عنوان',
    loading: 'جاري التحميل الرجاءالانتظار ...',
    please_wait: 'الرجاء الانتظار ...',
    error: 'شيء ما حدث بشكل خاطئ',
    view_profile: 'عرض الصفحة الشخصية',
    more: 'اظهار الكل',
    coming_soon: 'نحن لسنا جاهزين بعد - هذه الميزة ستصل قريبًا!',
    generating_art: 'توليد صورة مرفقة',
    type_to_find_person: 'ابدأ في الكتابة للعثور على أشخاص ...',
    type_to_find_group: 'ابدأ الكتابة للعثور على حلقات النقاش ...',
    type_to_find_tag: 'ابدأ في الكتابة للعثور على العلامات ...',
    type_to_find_post: 'جارٍ البدء في الكتابة للعثور على مشاركات ...',
    no_people: 'لاشئ للعرض',
    no_groups: 'لاشئ للعرض',
    cannot_be_undone: 'تحذير: لا يمكن التراجع عن هذا الإجراء!',
    privacy_policy: 'سياسة الخصوصية',
    terms_and_conditions: 'الأحكام والشروط'
  },
  search: {
    placeholder: 'ابدأ في الكتابة للبحث'
  },
  embed: {
    loading: 'تحميل المحتوى المضمن ...'
  },
  validation_error: {
    username_exists: 'اسم المستخدم مستخدم بالفعل',
    email_exists: 'البريد الإلكتروني مسجل بالفعل',
    invalid_url: 'URL غير صالح',
    required_field: 'الحقل مطلوب',
    required: '{field} مطلوب',
    invalid_username: 'اسم المستخدم قصير جدًا',
    invalid_email: 'عنوان البريد الإلكتروني غير صالح',
    invalid_phone_number: 'رقم الهاتف غير صالح',
    password_must_include_digits: 'يجب أن تحتوي كلمة المرور على رقم واحد على الأقل',
    password_too_short: 'يجب أن تتكون كلمة المرور من 8 أحرف على الأقل',
    passwords_must_match: 'كلمتا المرور غير متطابقتين',
    invalid_domain_name: 'اسم نطاق صالح مطلوب على سبيل المثال. www.google.com'
  },
  navigation: {
    login: 'تسجيل الدخول',
    logout: 'تسجيل الخروج',
    '/home': 'الصفحة الرئيسية',
    'home/activity': 'نشاط',
    'home/news': 'مقالات',
    'home/events': 'الأحداث',
    'home/news/tag/posts': 'مقالات',
    'home/news/tag/circles': 'حلقات نقاش',
    'home/people': 'أشخاص',
    '/notifications': 'إشعارات',
    'notifications/notifications': 'إشعارات',
    create: 'إنشاء',
    createEvent: "قم بإنشاء حدث اجتماع",
    groups: 'حلقات نقاش',
    'groups/home': 'حلقات نقاش',
    'groups/discover': 'اكتشف',
    'groups/group/activity': 'نشاط',
    'groups/group/outcome': 'نتيجة',
    'groups/group/posts': 'مشاركات',
    'groups/group/reference': 'مراجع',
    'groups/group/groups': 'حلقات نقاش',
    'groups/group/events': 'الأحداث',
    'profile/public': 'الملف الشخصي',
    'profile/posts': 'الملف الشخصي',
    'profile/drafts': 'مسودات',
    'profile/bookmarks': 'إشارات مرجعية',
    'profile/public/posts': 'مشاركات',
    'profile/public/events': 'احداث',
    'profile/public/responses': 'الردود',
    'profile/public/recommends': 'توصي',
    'profile/public/bio': 'السيرة الذاتية',
    'home/member': ' ',
    'home/member/posts': 'مشاركات',
    'home/member/events': 'الأحداث',
    'home/member/responses': 'الردود',
    'home/member/recommends': 'توصي',
    'home/member/bio': 'السيرة الذاتية',
    'groups/group/membership/access_requests': 'طلبات',
    'groups/group/membership/invites': 'دعوات',
    'groups/group/membership/members': 'أعضاء',
    search: 'بحث'
  },
  login: {
    title: 'تسجيل الدخول',
    email: 'عنوان البريد الإلكتروني',
    password: 'كلمة السر',
    login: 'قدم',
    account_not_verified: 'لم يتم التحقق من عنوان البريد الإلكتروني',
    invalid_credentials: 'بيانات الاعتماد غير صالحة',
    login_with_email: 'تسجيل الدخول باستخدام البريد الإلكتروني وكلمة المرور',
    login_with_facebook: 'تسجيل الدخول باستخدام الفيسبوك',
    no_such_user: 'هذا الحساب غير مسجل',
    reset_password: 'إعادة تعيين كلمة السر',
    sent_password_reset_mail:
      'تم إرسال البريد الإلكتروني لإعادة تعيين كلمة السر لك، يرجى التحقق من بريدك الإلكتروني.'
  },
  logout: {
    signing_out: 'تسجيل الخروج ...'
  },
  landing: {
    line_1: 'التفكير الجماعي في حلقات النقاش الميسرة لإيجاد الحلول',
    line_2:
      'انضم إلى حلقة نقاش ، أنشئ حلقاتك العامة أو الخاصة ، انشر المقالات وتواصل مع الآخرين',
    register_button: 'البدء',
    browse_as_a_guest: 'أو تصفح كضيف'
  },
  registration: {
    register_with_email: 'سجل ببريدك الالكتروني',
    welcome: 'تم تأكيد عنوان بريدك الإلكتروني الآن',
    continue_to_the_app: 'استمر',
    confirmation_required_title: 'أكد عنوان بريدك الألكتروني',
    confirmation_required_text:
      'لقد أرسلنا بريدًا إلكترونيًا إلى {email} يحتوي على رقم التعريف الشخصي لإكمال تسجيلك.',
    confirmation_pin_field: 'رقم التعريف الشخصي',
    confirm_registration: 'قدم رقم التعريف الشخصي للتحقق',
    continue_with_facebook: 'تواصل مع الفيسبوك',
    continue_with_twitter: 'تواصل مع Twitter',
    continue_with_google: 'تواصل مع Google',
    waiting_for_facebook: 'في انتظار المصادقة',
    completing_registration: 'استكمال اشتراكك',
    completed_registration: 'اكتمل الاشتراك ، وإعادة التوجيه ...'
  },
  registration_form: {
    register: 'اشترك',
    username_field: 'اسم المستخدم',
    email_field: 'عنوان البريد الإلكتروني',
    password_field: 'كلمة السر',
    password_again_field: 'أعد إدخال كلمة السر',
    already_registered: '{field}مسجل مسبقا ',
    already_registered2: 'تم تسجيله بالفعل',
    password_reset_button: 'إعادة تعيين',
    confirm_password_button: 'يتأكد',
  },
  password_reset: {
    success: 'نجاح إعادة التعيين‫!‬',
    failed: 'فشل إعادة التعيين!',
    linkLabel: 'استمر',
    signing_in: 'تسجيلك‫...‬'
  },
  onboarding: {
    choose_n_tags: 'اختر ٣ علامات على الأقل لتخصيص المحتوى الخاص بك'
  },
  profile: {
    view: 'عرض الصفحة الشخصية',
    new_post: 'مشاركة جديدة',
    new_event: "حدث جديد",
    edit_button: "تحرير المعلومات الشخصية",
    my_short_bio_default: 'إضافة سيرة ذاتية مختصرة',
    their_short_bio_default: 'يفضل هذا المستخدم الحفاظ على جو من الخصوصية',
    reputation_label: 'سمعة',
    followers_label: 'متابعون',
    following_label: 'يتبع',
    title: 'تعديل الملف الشخصي',
    firstname_field: 'الاسم الاول',
    lastname_field: 'اللقب',
    gender_field: 'الجنس',
    gender_male: 'ذكر',
    gender_female: 'أنثى',
    bio_field: 'السيرة الذاتية',
    profile_photo: 'الصوره الشخصيه',
    save: 'حفظ',
    edit_profile_title: 'تعديل الملف الشخصي',
    view_profile_title: 'الملف الشخصي',
    load_profile: 'تحميل الملف الشخصي',
    follow: {
      is: 'متابعة',
      is_not: 'يتبع',
      checking: 'يتبع'
    },
    choose_avatar_label: 'اختر صورة شخصية',
    drag_image_label: 'انقر أو اسحب الصورة',
    error_user_404: 'المستخدم غير موجود',
    logout: 'تسجيل خروج {username}'
  },
  people: {
    search_for_people:
      'استخدم البحث عن الكلمات الرئيسية أعلاه للبحث عن الأسماء والسير الذاتية.',
    interesting_people:
      'إليك بعض الأشخاص المثيرون للاهتمام الذين قد تودون متابعتهم.',
    no_results: 'لم نعثر على أي شخص يطابق هذا الاستعلام'
  },
  import: {
    drop_here:
      'إسقاط Microsoft Word (.docx) أو ملف PDF هنا ، أو انقر للاستعراض',
    not_a_word_doc: 'يمكن استيراد ملفات PDF أو Microsoft Word .docx فقط.',
    try_again: 'حاول مرة أخري',
    uploading: 'استيراد وثيقة',
    redirecting_you: 'إعادة توجيه'
  },
  compose: {
    unknown_type: 'نوع مشاركة غير معروفة `{type}`',
    new: {
      chat_placeholder: "ندخل النص لـ تحسين أو ندخل سؤالاً للحصول على الـ مساعدة، ثم ننقر على الزر المناسب. ننقر على “نشر” لتقديم تعليقنا.",
      page_title: 'مقالة جديدة',
      new_public_post: 'إنشاء مشاركة جديدة',
      page_event: "حدث جديد",
      event_title: "عنوان الحدث",
      page_evt_title: "إنشاء الحدث",
      start_date: "تاريخ البدء",
      date: "تاريخ",
      evt: "أدخل عنوانًا وتاريخًا لاجتماعك",
      end_date: "تاريخ الانتهاء",
      category: "الفئة",
      time: "زمن",
      submiting: "تقديم",
      CreateMeeting: "إنشاء اجتماع",
      Duration: "المدة الزمنية",
      new_public_post: 'إنشاء مشاركة جديدة',
      create_event: "انشاء حدث",
      event_description: "وصف الحدث",
      add_participant: "إضافة مشارك",
      choose_moderator: "اختر وسيط",
      warning:
        'ستكون مقالتك متوفرة للجميع مبدئياً. ولكن يمكنك التحكم في الخصوصية من خلال اختيار حلقة في خطوة النشر النهائية.',
      what: 'ماذا تريد أن تنشأ؟',
      post_an_article: 'نشر مقالة',
      import_a_document: 'استيراد مستند',
      announce_an_event: 'الإعلان عن حدث',
      thirtyMins: "ثلاثون دقيقة",
      SixtyMins: "ستين دقيقة",
      NinetyMins: "تسعون دقيقة",
      OneTwentyMins: "مائة وعشرون دقيقة",
      minutes150: "150 دقيقة",
      minutes180: "180 دقيقة",
      minutes240: "240 دقيقة",
    },
    tag: {
      page_title: 'أضف علامة على مقالتك',
      page_title_Event: 'ضع علامة على الحدث الخاص بك',
      choose_n_tags: 'علم مقالتك لـ{n} علامات',
      choose_n_tags_events: "العلامات{n}ضع علامة على الحدث الخاص بك مع ما يصل إلى",
      atLeastOneTag: "الرجاء إضافة علامة واحدة على الأقل!",
      tag: 'علامة'
    },
    event: {
      title_schedule_event: "جدولة الحدث الخاص بك",
      title_schedule_discussion: "حدد موعدًا لمناقشتك",
      role: "دور",
      timezone: "وحدة زمنية",
      meeting_info: "قدم معلومات الاجتماع",
      suggested: "مقترح",
      title_roles: "الجمهور والأدوار",
      roles_header: "حدد دورًا وأضف الأشخاص إليه",
      by_name_and_email: "البحث بالاسم أو البريد الإلكتروني",
      schedule_header_event: "حدد التاريخ والوقت ونوع الحدث",
      schedule_header_discussion: "جدولة بداية ونهاية المناقشة",
      event_type: "نوع الحدث",
      label: 'معلومات إضافية (مثل "الانضمام إلى اجتماع Zoom"، وما إلى ذلك)',
      url: "URL لمعلومات إضافية",
      label2: "مزيد من المعلومات الإضافية، على سبيل المثال رابط البث على YouTube",
      url2: "رابط لمزيد من المعلومات الإضافية",
      streamingInfo: "معلومات إضافية حول خدمة البث المستخدمة",
      streamingUrl: "URL لعرض خدمة البث المستخدمة",
      all_party: 'جميع أعضاء حلقة النقاش',
      types: {
        google: "Google",
        jitsi: "Jitsi",
        zoom: "Zoom",
        external: "خارجي",
      },
      roles: {
        expert: "خبير",
        host: "مضيف",
        moderator: "مشرف",
        speaker: "مكبر الصوت",
        guest_expert: "ضيف خبير",
        panelist: "المتحدث",
        participant: "مشارك",
        trainer: "مدرب",
      },
      all_members_invited: "جميع أعضاء الدائرة مدعوون إلى هذا الاجتماع كمشاركين",
    },
    audience: {
      page_title: 'جمهورك',
      publish: 'نشر',
      publishing: 'النشر',
      control_visibility:
        'قرر من يمكنه مشاهدة مقالتك عن طريق النشر بشكل عام أو إلى حلقة تشارك فيها',
      public_title: 'نشر عام',
      public_description: 'يمكن لأي شخص مشاهدة هذه المقالة',
      group_title: 'حلقة نقاش',
      group_description: 'يتم التحكم في مستوى الإطلاع من خلال إعدادات الحلقة',
      choose_group: 'اختر حلقة النقاش',
      change_group: 'تغيير'
    },
    share: {
      title: 'شارك ما أنشئت',
      published_successfully: 'تم نشر مقالتك بنجاح',
      share_post: 'تم',
      share_article: 'حدد وانسخ الرابط أدناه للمشاركة'
    },
    next: 'التالي',
    external_link: 'رابط خارجي',
    link_post: 'رابط للمشاركة',
    embed_post: 'ضمّن مشاركة',
    link_field: 'رابط',
    link_placeholder: 'أدخل عنوان URL (عناوين) URL صالح',
    description_field: 'وصف',
    description_placeholder: 'الوصف (يظهر في قسم الروابط)',
    uploading: 'تحميل {name}',
    link: {
      manager_title: 'تعديل الرابط',
      update: 'تحديث الرابط'
    },
    embed: {
      manager_title: 'تحرير التضمين',
      update: 'تحديث التضمين'
    }
  },
  attachments: {
    manager_title: 'إدارة المرفقات',
    no_attachments: 'لم ترفق أي ملفات حتى الآن.',
    drop_here: 'أفلت الملفات هنا أو انقر لفتح منتقي الملفات.',
    describe: 'وصّف هذا المرفق'
  },
  activity: {
    publish: 'نشر في',
    publish_publicly: 'نشر علنا',
    publish_to_all: 'نشر علنا',
    follow: 'يتبع',
    reply: 'رد على مشاركتك',
    react: 'رد على المقالة',
    'create-group': 'بدأ الحلقة {name}',
    'created-group': 'بدأ الحلقة {name}', // deprecate
    'join-group': 'انضم إلى حلقة {name}',
    'joined-group': 'انضم إلى حلقة {name}', // deprecated
    'close-group': 'أنهيت الحلقة {name}',
    'reopen-group': 'إعادة فتح الحلقة {name}',
    'invite-to-group': 'دعاك للانضمام إلى حلقة نقاش',
    'request-access': 'طلبت الوصول إلى الحلقة "{name}"',
    'removed-post': 'تم إزالة مشاركة',
    you: 'أنت',
    in_group: 'في',
    verbs: {
      publish: 'نشرت {activity_count} مقالات', // todo: plurals
      follow: 'تابعت {activity_count} أشخاص', // todo: plurals
      reply: 'أجبت على مقالتك',
      react: 'تم الرد على مقالتك',
      'join-group': 'شاركت بحلقة',
      'create-group': 'شاركت بحلقة نقاش جديدة',
      'invite-to-group': 'دعاك للانضمام إلى حلقة',
      'reopen-group': 'أعادة فتح حلقة',
      'close-group': 'اختتم الحلقة',
      'request-access': 'طلب الوصول إلى الحلقات',
      'removed-post': 'إزالة منشور'
    },
    name_comma: ', ',
    name_and: ' و ',
    mentioned_you: 'قام بذكرك',
    and_n_others: `{n, plural, one {${plurals$1.and_one_other}} other {${plurals$1.and_n_others}}}`,
    n_people: '{n} أشخاص',
    tap_groups: 'انقر على "الحلقات"',
    join_some_groups: 'لاكتشاف الحلقات التي تتوافق مع اهتماماتك',
    tap_create: 'انقر على "إضافة"',
    post_some_content: 'لإنشاء مشاركاتك الخاصة',
    view_posts_matching_your_tags:
      'اضغط على "مقالات" لمشاهدة أحدث المقالات والأخبار التي تطابق العلامات الخاصة بك',
    view_events_matching_your_tags:
      'اضغط على "الأحداث" لمشاهدة أحدث الأحداث التي تطابق العلامات الخاصة بك',
    follow_some_people:
      "أو تجد أشخاصًا مثيرين للاهتمام لمتابعة من القائمة أدناه",
  },
  drafts: {
    delete: 'احذف هذه المسودة',
    count: '{count, plural, zero {لا مسودات} one {مسودة واحدة} two {مسودتان} few {# مسودات} many {# مسودة} other {# مسودة}}'
  },
  posts: {
    send_reminder: "إرسال بريد إلكتروني للتذكير",
    open_thread: "افتح الموضوع في نافذة جديدة",
    empty_list_placeholder: 'لا يوجد شيء يمكن رؤيته هنا حتى الآن.',
    n_minute_read: '{value} للقراءة',
    unpublish: 'ألغي نشر هذه المشاركة',
    edit: 'تحرير هذه المشاركة',
    edit_metadata: 'تحرير البيانات الوصفية',
    in_group: ', في {name}',
    pin_and_close_group: 'عرف كمغلق لحلقة النقاش',
    pin_as_group_intro: 'عرف كمقدمة لحلقة النقاش',
    search_placeholder: 'اكتب للبحث عن المشاركات'
  },
  post: {
    continue: "إستمر",
    proceed: "الانتقال إلى صفحة تسجيل الدخول",
    please_login: "هذه المشاركة للأعضاء. يرجى تسجيل الدخول لقراءتها.",
    register_login: "يرجى الاشتراك/تسجيل الدخول أولا من أجل الرد.",
    proceed_join: "الاشتراك في الحلقة",
    join2respond: "يرجى الانضمام إلى الحلقة لكتابة الرد.",
    join: "انضمام",
    cancel: "إلغاء",
    startNewDiscussionThread: "بدء موضوع مناقشة جديد",
    respond2Thread: "الرد على الموضوع",
    closeDiscussionThread: "إغلاق موضوع المناقشة",
    unflag: "إلغاء إبلاغ عن صورة غير ملائمة",
    unpublished: 'المشاركة متوفرة',
    unpublished_description: 'تمت إزالة هذه المشاركة من قبل المؤلف أو المشرف',
    responses: 'استجابات',
    responses_discussion: 'مواضيع المناقشة',
    respond: 'انقر هنا لكتابة إجابتك {name}',
    respond_discussion: "انقر هنا لبدء سلسلة نقاش جديدة",
    conversation_with: 'محادثة مع {name}',
    n_responses: `${plurals$1.view} {responseCount, plural, one {${plurals$1.one_response}} other {${plurals$1.n_responses}}}`,
    maximize_conversation: 'شاهد المحادثة بأكملها',
    minimize_conversation: 'إخفاء هذه المحادثة',
    references: 'المراجع والمصادر',
    response_removed: 'تمت إزالة هذه الاستجابة',
    response_removed_by: "لم يتم نشر التعليق بواسطة",
    responding_to_thread: 'ردا على هذا الموضوع:',
    main_post: 'وثيقة النقاش الرئيسية',
    go_back_to_main_post: 'انقر على العنوان أدناه للذهاب الى وثيقة النقاش الرئيسية',
    owner_thread: 'سلسلة النقاش الأم:',
    go_to_owner_thread: '(انقر أدناه للذهاب لسلسة النقاش الأم)',
    current_thread: 'الرجاء الاجابة للنقاش أدناه:',
    n_other_posts: `{length, plural, one {${plurals$1.one_elided_post}} other {${plurals$1.n_elided_posts}}}`,
    full_screen_composer: 'شاشة كاملة',
    publish: 'نشر',
    discard: 'تجاهل',
    share: 'شارك هذه المقالة',
    reactions: {
      title: 'ردود فعل',
      agreement: 'اتفاقية',
      agreement_desc: 'مقياس لمدى موافقتك على الحجج العامة المقدمة في المشاركة',
      give_feedback: "يرجى تقييم اقتراح المناقشة",
      eloquence: 'بلاغة',
      eloquence_desc:
        'مقياس لنمط ونوعية وإقناع الكتابة في هذا المنشور ، بصرف النظر عن موافقتك',
      relevance: 'ملاءمة',
      relevance_desc:
        'مقياس لمدى ملائمة هذه المشاركة بالمجموعة و / أو علامات المشاركة',
      average: 'متوسط التقييم',
      yours: 'تقيمك',
      cannot_react_to_own_post: 'لا يمكنك تقييم مشاركتك الخاصة!'
    }
  },
  tags: {
    follow: 'إتبع',
    following: 'متابعة',
    minimum_required: 'يرجى تقديم على الأقل {n} علامات',
    tag_field: 'العلامة',
    home_following: 'هنالك متابعة لهذه العلامة',
    home_unfollowing: 'ليس هنالك متابعة لهذه العلامة'
  },
  group: {
    pages: "الصفحات",
    notify_on: "قم بتشغيل إشعارات الهاتف المحمول",
    notify_off: "قم بإيقاف تشغيل إشعارات الهاتف المحمول",
    recentActivities: "أنشطة حالية",
    createPost: "إنشاء مشاركة",
    tabs: {
      all: "كل المشاركات",
      discussions: "المناقشات",
      meetings: "الأحداث",
      articles: "المقالات",
      references: "المراجع",
      subCircles: "حلقات فرعية",
      map: "خريطة",
    },
    actions: {
      startDiscussion: "بدأ مناقشة",
      announceEvent: "أعلن عن الحدث",
      announce: "الإعلان عن اجتماع",
      writeArticle: "كتابة مقالة",
      import: "استيراد مستند",
      record: "تسجيل أصل",
      createInterview: 'إنشاء منشور وسائط',
    },
    tooltips: {
      dashboard: "لوحة التحكم",
      help: "صفحة المساعدة",
      notifications: "أحدث إخطارات",
      members: "أعضاء هذه الحلقة",
    },
    totalResponses: 'إجمالي الردود',
    searchHere: "البحث هنا",
    empty_list_placeholder: 'لا يبدو أنك تشارك في أي حلقات نقاش',
    discover_some_groups:
      'انقر على "اكتشاف" للعثور على الحلقات التي تطابق اهتماماتك',
    tap_add: 'انقر على "إضافة"',
    create_your_own_groups: 'لبدء حلقاتك الخاصة ودعوة أعضاء آخرين للانضمام!',
    no_activity: 'لا يوجد شيء يمكن رؤيته هنا حتى الآن.',
    post_something: 'انشر شيئًا',
    import_a_document: 'استيراد وثيقة',
    edit_group: 'تعديل هذه الدائرة',
    new_sub_group: 'إنشاء حلقة فرعية',
    invite: 'دعوة أعضاء جدد',
    send_invite: 'إرسال',
    pending_invite: 'قد دعي',
    already_a_member: 'هو بالفعل عضو',
    request_access_to_circle: 'طلب الدخول',
    request_access: {
      title: 'طلب الدخول إلى الحلقة',
      request: 'طلب الدخول',
      description:
        'اضغط على الزر أدناه لطلب الدخول إلى هذه الحلقة. سيتم إشعار مديري الحلقة بطلبك.',
      already_requested: 'لديك بالفعل طلب معلق للانضمام إلى هذه الحلقة.',
      already_rejected: 'تم رفض طلبك الحالي للانضمام إلى هذه الحلقة.'
    },
    only_visible_to_members: 'المحتوى مرئي فقط لأعضاء الحلقة',
    join: 'انضم إلى هذه الدائرة',
    login_to_join: 'سجّل الدخول للانضمام إلى هذه الحلقة',
    leave: 'غادر هذه الحلقة',
    reopen: 'أعد فتح هذه الحلقة',
    close: 'اغلق هذه الحلقة',
    close_did_you_know:
      'عند إنهاء الحلقة ، يمكنك اختيار مشاركة واحدة داخل الحلقة من أجل "تثبيت" "نتيجة" للحلقة.',
    close_how_to_pin: 'للإغلاق مع مشاركة مثبتة "النتيجة":',
    close_find_post:
      'في علامة التبويب "المشاركات" ، ابحث عن المشاركة التي تريد تثبيتها',
    close_tap_overflow:
      'افتح قائمة السياقات من خلال النقر على رمز الفائض في المشاركة',
    close_pin_as_outcome:
      'في قائمة السياق ، انقر على "عرف كنتيجة لحلقة النقاش"',
    close_is_better_with_outcome: 'ينصح بشدة بإغلاق الحلقة بنتيجة مثبتة',
    close_without_outcome: 'أغلق بدون تثبيت',
    you_are_closing_this_group:
      'تحذير: أنت على وشك إنهاء هذه الحلقة ، التي ستمنع نشر المشاركات الجديدة في هذه الحلقة.',
    close_and_set_outcome: 'أغلق مع نتيجة',
    outcome_to_be_pinned: 'تثبيت المشاركة‫-‬النتيجة‫:‬',
    increase_privacy_by_one_level: 'زيادة خصوصية هذه الدائرة إلى مستوى أعلى:',
    make_more_private: 'زيادة الخصوصية',
    enable_content_digest: "قم بتضمين ملخص البريد الإلكتروني اليومي",
    disable_content_digest: "استبعاد ملخص البريد الإلكتروني اليومي",
    summary: {
      privacy: {
        PUBLIC: 'عام',
        public: 'عام', // deprecated
        REGISTERED: 'الاعضاء المسجلين فقط',
        registered: 'الاعضاء المسجلين فقط', // deprecated
        PRIVATE: 'المدعوون فقط',
        private: 'المدعوون فقط' // deprecated
      },
      stats: `{memberCount, plural, one {${plurals$1.one_member}} other {${plurals$1.n_members}}, {postCount, plural, one {${plurals$1.one_post}} other {${plurals$1.n_posts}}}`
    },
    info: {
      privacy: {
        PUBLIC: 'متاح للجمهور',
        public: 'متاح للجمهور', // deprecated
        REGISTERED: 'يمكن الوصول إليها من قبل المستخدمين المسجلين',
        registered: 'يمكن الوصول إليها من قبل المستخدمين المسجلين', // deprecated
        PRIVATE: 'الوصول عن طريق الدعوة فقط',
        private: 'الوصول عن طريق الدعوة فقط', // deprecated
        cannot_be_less_private_than_parent:
          'لا يمكن أن تكون الدوائر الفرعية أقل خصوصية من دائرة أصلها.'
      },
      intro: 'اقرأ هذا أولا!',
      closed: '(مغلقة إلى مزيد من النشاط)',
      pending_access_requests: `{access_request_count, plural, one {${plurals$1.one_pending_request}} other {${plurals$1.n_pending_requests}}`,
      membership: {
        ADMIN: {
          true: 'أنت مشرف حلقة',
          false: 'أنت مشرف حلقة'
        },
        MEMBER: {
          true: 'أنت عضو (للقراءة فقط)',
          false: 'أنت عضو'
        },
        undefined: 'انت لست عضو'
      },
      guest: 'المشاهدة كضيف',
      members: `{memberCount, plural, one {${plurals$1.one_member}} other {${plurals$1.n_members}}`,
      posts: `{postCount, plural, one {${plurals$1.one_post}} other {${plurals$1.n_posts}}}`,
      view_members: '(مشاهدة الاعضاء)'
    },
    discover: {
      invitations: 'دعوات',
      suggestions: 'اقترح لك',
      invited_you: 'دعاك‫/‬دعتك للانضمام إلى هذه الدائرة',
      nothing_to_see: 'إنها هادئة قليلاً هنا ، حاول مرة أخرى في وقت لاحق!',
      join: 'انضم إلى الدائرة',
      dont_join: 'تجاهل الدعوة',
      discarded: 'لقد تم تجاهل هذه الدعوة',
      joined: 'لقد انضممت إلى هذه الحلقة',
      go_to_group: 'زيارة الحلقة'
    },
    create: {
      basics_page_title: 'ابدأ بحلقة',
      privacy_page_title: 'إعدادات الخصوصية',
      select_privacy: "الرجاء تحديد إعدادات الخصوصية الخاصة بك",
      privacy_subcircle: "إذا كنت تقوم بإنشاء دائرة فرعية، فلا يمكن أن تكون خصوصيتها أقل من خصوصية الدائرة الرئيسية",
      members_page_title: 'دعوة أعضاء',
      schedule_page_title: 'جدول',
      tags: 'العلامات',
      next: 'التالي',
      finish: 'إنشاء',
      name_field: 'إسم الحلقة',
      name_placeholder: 'أدخل اسمًا لحلقتك',
      members_read_only_field: 'للقراءة فقط لغير المشرفين',
      only_admins_can_post: 'يمكن للمشرفين فقط النشر',
      members_and_admins_can_post: 'يمكن للأعضاء والمشرفين النشر',
      specify_at_least_one_tag: 'حدد علامة واحدة على الأقل تصف دائرتك',
      privacy: 'الخصوصية',
      privacy_public_title: 'عام',
      privacy_public_description:
        'يمكن لأي شخص قراءة المشاركات في هذه الحلقة. يمكن لأي مستخدم مسجل المساهمة بالمحتوى في الحلقة.',
      privacy_registered_title: 'المستخدمون المسجلون',
      privacy_registered_description:
        'يمكن للمستخدمين المسجلين فقط قراءة المحتوى ونشره في هذه الحلقة.',
      privacy_private_title: 'خاصة',
      privacy_private_description:
        'يمكن فقط للأشخاص الذين تدعوهم الانضمام إلى دائرة القراءة والمساهمة.',
      send_invitations_to: 'دعوات',
      invite_some_people: 'لم تقم بدعوة أي شخص حتى الآن',
      sponsor_page_title: 'رعاية',
      sponsor: 'Sponsor',
      associate_sponsor: 'ربط أحد الرعاة لهذه الحلقة ، إذا كان لديك واحد',
      schedule: 'Schedule'
    },
    invitation: {
      page_title: "أنشئ كلمة مرورك الآن",
      invited_header: "أنت مدعو للانضمام إلى الدائرة",
      circle: "الدائرة:",
      invited_by: "بدعوة من:",
      agree_to_terms: "أوافق على سياسة الخصوصية والشروط والأحكام",
      intro: "بعد تأكيد كلمة المرور الخاصة بك ، ستتمكن من الانضمام إلى الأحداث والوصول إلى خدمات النظام الأساسي الأخرى",
    },
    invitations: {
      page_title: "قم بدعوة أعضاء جدد لهذه الدائرة",
      intro: "قم بتوسيع مجتمعك! يمكنك التواصل عبر البريد الإلكتروني مع الأفراد الذين لم يصبحوا جزءًا من المنصة بعد ، وتشجيعهم على التسجيل والانضمام إلى دائرتك. بدلاً من ذلك ، يمكنك أيضًا الاختيار من بين مستخدمي النظام الأساسي الحاليين ودعوتهم لإثراء دائرتك كأعضاء جدد.",
      email_header: "قم بدعوة مستخدمين جدد عن طريق إرسال رسائل بريد إلكتروني إليهم للتسجيل والانضمام إلى الدائرة",
      emails_field: "سرد رسائل البريد الإلكتروني (مفصولة بفواصل) ...",
      send_email_button: "التحقق من صحة وإرسال رسائل البريد الإلكتروني للدعوة",
      registered_toast: "دعوة المستخدمين ...",
      register_msg: "يسجل",
      invite_registered: "قم بدعوة المستخدمين المسجلين بالفعل للانضمام إلى هذه الدائرة",
      person_picker: "ابدأ الكتابة للعثور على أسماء المستخدمين واختر دعوة…",
      invite: "يدعو",
      invited_toast: "المستخدمون مدعوون",
      invite_msg: "دعوة",
    },
    edit: {
      basics_page_title: 'عدل الحلقة',
      finish: 'حفظ'
    },
    members_page: {
      title: 'اعضاء في {name}',
      since: 'انضم',
      role: {
        ADMIN: '(مشرف)',
        MEMBER: '(عضو)'
      },
      invited_by: 'بدعوة من',
      promote_to_admin: 'الترقية إلى مسؤول الحلقة',
      demote_to_member: 'تخفيض إلى عضو في الحلقة'
    },
    access_requests: {
      manage: 'إدارة الحلقة',
      accept: 'منح الدخول',
      reject: 'رفظ الدخول',
      confirmation_required_title: 'التأكيد مطلوب',
      user_will_be_notified_of_rejection:
        'اضغط على الزر أدناه لرفض طلب الوصول هذا. سيتم إشعار المستخدم الذي قدم الطلب بأن طلبه قد تم رفضه.',
      REJECTED: 'مرفوض من طرف {username}',
      ACCEPTED: 'قبلت من قبل {username}'
    }
  },
  buttons: {
    zoom_meeting: 'Join Zoom Meeting',
    ok: 'نعم',
    cancel: 'يلغي'
  },
  post_dialog: {
    pre_welcome: 'مرحبًا بكم في مناقشتنا قم',
    post_welcome: 'هذه المناقشة مفتوحة الآن لمساهمتك في الاقتراحات أدناه.',
    tour_help: 'بجولة لمعرفة المزيد. يمكنك دائمًا الوصول إلى شاشة المساعدة هذه لاحقًا من رمز المساعدة (علامة الاستفهام) في شريط التطبيقات.    ',
    post_closed: 'تم إغلاق هذه المناقشة من قبل المنسق. يرجى قراءة المناقشات ونتائجها أدناه.',
    line_1: 'مساعدتك في الرد على',
    line_2_1: 'يمكنك الرد على مستوى أعلى ، بالنقر فوق الزر',
    line_2_2: 'الردود ، أو يمكنك الرد على تعليق بالضغط على',
    line_2_3: 'بجانب التعليق.',
    line_3: 'المنسق قادر دائمًا على إغلاق المناقشة مع نتيجة أو إعادة فتحها مرة أخرى',
    line_4: 'المشاركة في المناقشات',
    tab_1: 'مقدمة',
    tab_2: 'الاستجابة',
    tab_3: 'حصيلة',
    button: 'جولة',
    discussion_outcome: 'المنسق قادر دائمًا على إغلاق المناقشة مع نتيجة أو إعادة فتحها مرة أخرى من القائمة الكاملة.',
    comment_response: 'التعليق على رد معين. يمكنك دائمًا الصعود في التسلسل الهرمي لسلسلة الردود من خلال النقر على زر أعلى. يمكنك أيضًا بدء سلسلة محادثات جديدة تمامًا من حقل الردود في الأعلى.',
    response_menu_moderator: 'يمكن للمنسق إدخال رد أو اختيار رد موجود وإغلاق المناقشات لتصبح النتيجة. لا مزيد من تحرير المناقشة ممكن ما لم يختار المنسق إعادة فتح المناقشة من القائمة الكاملة للنتيجة.',
    profile: 'في ملف تعريف المستخدم ، يمكنك رؤية منشوراتهم الأخيرة أو متابعة منشوراتهم الجديدة. يتم الاحتفاظ بنسخة من ردودك بأمان في ملف التعريف الخاص بك ، ويمكن الوصول إليها من القائمة الرئيسية. من هناك يمكنك تعديل أو إلغاء نشر ردودك!',
    rate: 'انقر فوق "الرجاء تقييم اقتراح المناقشة" لإعطاء انطباعك الأول عن الاقتراح.',
    online_users: 'يُظهر الرمز عدد الزملاء الذين فتحوا نفس المناقشة وربما يشاركون. اضغط عليها لتجد أسمائهم.',
    Notifications: 'يقوم الرمز بتبديل اللوحة الجانبية لإظهار التنبيهات مثل إعلام وصول الردود الجديدة.',
    respond: 'إذا كنت المستجيب الأول الفخور ، فاكتب الرد الأول ضمن "الردود" وانقر فوق نشر!',
    fullscreen: 'في الردود والتعليقات ، يمكنك تنسيق النص في السطر. للقيام بالمزيد ، مثل إرفاق صورة أو إضافة رابط أو تضمين مرجع مستند أو مقطع فيديو ، انقر على "الشاشة الكاملة" أثناء الرد.',
    response_menu_authour: 'بصفتك مؤلفًا ، يمكنك إلغاء نشر ردك أو تعديله بعد نشره. إذا كنت مشرفًا / مشرفًا في دائرة ، فيمكنك أيضًا تعديل أو إلغاء نشر الرد مع المؤلف الأصلي المخول بتعديله وإعادة نشره.',
    circle: {
      title: 'مرحبًا بك في حلقتك للنقاش',
      line_1: 'يمكنك متابعة المقالات والمناقشات وأحداث الاجتماعات في حلقة النقاش هذه. يمكنك أيضًا كتابة مساهمتك الخاصة أو بدء حدثك الخاص. سنأخذك في جولة سريعة في حلقة النقاش هذه.',
      line_2: 'يمكنك دائمًا زيارة هذه الجولة لاحقًا من رمز المساعدة (علامة الاستفهام) في شريط التطبيقات.',
      button: 'Features tour',
    },
  },
  email_registration: {
    agree: 'أوافق على',
    privacy: 'سياسة الخصوصية',
    post_privacy: 'و',
    terms: 'الشروط والأحكام',
  },
  dialogs: {
    online: 'مستخدمين على الهواء',
    configure_notifications: 'إظهار الإخطارات',
    show_toasts: 'إظهار الإخطارات المنبثقة',
    show_notifications: 'إظهار إخطارات سطح المكتب',
  },
  accordion: {
    recent_notes: 'التصويتات الأخيرة',
    recent_votes: 'التصويت الأول ما رأيك؟',
  },
  placeholders: {
    opinion: 'رأي قصير',
  },
  toolbars: {
    notifications: 'إشعارات',
    report: 'الإبلاغ عن موضوع غير ملائم',
  },
  no_such_post: 'لم نتمكن من العثور على هذه المشاركة!',
  circle_listing: {
    collapse: 'تصغير الدوائر الفرعية',
    expand: 'قم بتوسيع الدوائر الفرعية',
    c_posts: 'مشاركات',
    c_members: 'أعضاء',
    go_to_circle: 'انقر للذهاب إلى الدائرة',
  },
  br: {
    exit: 'خروج',
    share: 'مشاركة',
    viewDetails: 'عرض تفاصيل الكتاب',
    savedText: 'النص المحفوظ',
    selText: 'النص المحدد',
    back: 'رجوع',

    bm: {
      title: 'إشارة مرجعية',
      titleAdd: 'إضافة إشارة مرجعية',
      name: 'الاسم',
      ph: 'اسم الإشارة المرجعية',
      save: 'حفظ الإشارة المرجعية',
      remove: 'حذف الإشارة المرجعية',
    },

    note: {
      title: 'ملاحظة',
      titleAdd: 'إضافة ملاحظة',
      label: 'ملاحظة',
      ph: 'اكتب ملاحظتك\u2026',
      save: 'حفظ الملاحظة',
      remove: 'حذف الملاحظة',
    },

    ai: {
      title: 'اسأل كير',
      ph: 'اسأل أي شيء',
      disclaimer: 'تقتصر إجابات الذكاء الاصطناعي على هذا الكتاب فقط',
      ask: 'اسأل',
      searching: 'جارٍ البحث\u2026',
      sources: '{count, plural, zero {لا مصادر} one {مصدر واحد} two {مصدران} few {# مصادر} many {# مصدراً} other {# مصدر}}',
      readAloud: 'قراءة بصوت عالٍ',
      stopReading: 'إيقاف القراءة',
      newQ: 'سؤال جديد',
    },
    prefs: {
      title: 'تفضيلات القارئ',
      reset: 'إعادة التعيين',
      fontType: 'نوع الخط',
      fontSize: 'حجم الخط',
      lineSpacing: 'تباعد الأسطر',
      theme: 'المظهر',
      font: {
        inter: { label: 'Inter', sub: 'بدون ضريف' },
        times: { label: 'Times New Roman', sub: 'تقليدي' },
        opendyslexic: { label: 'OpenDyslexic', sub: 'سهل القراءة' },
        'arabic-naskh': { label: '\u0646\u0648\u062a\u0648 \u0646\u0633\u062e \u0639\u0631\u0628\u064a', sub: 'عربي مضلع' },
        'arabic-kufi': { label: '\u0643\u0648\u0641\u064a \u0639\u0631\u0628\u064a', sub: 'عربي كوفي' },
        'arabic-muna': { label: 'منى', sub: 'نسخ عربي' },
      },
      themes: {
        light: 'فاتح',
        sepia: 'بني فاتح',
        dark: 'داكن',
      },
    },
    content: {
      title: 'المحتوى',
      close: 'إغلاق',
      toc: 'جدول المحتويات',
      bookmarks: 'الإشارات المرجعية',
      noBm: 'لا توجد إشارات مرجعية',
      notes: 'الملاحظات',
      noNotes: 'لا توجد ملاحظات',
      continue: 'متابعة القراءة',
      search: 'بحث',
    },
    sel: {
      askAi: 'اسأل كير',
      bookmark: 'إشارة مرجعية',
      note: 'ملاحظة',
    },
    dial: {
      search: 'بحث',
      askAi: 'اسأل الذكاء الاصطناعي',
      settings: 'إعدادات القارئ',
      translate: 'ترجمة',
      toc: 'جدول المحتويات',
      save: 'حفظ إشارة مرجعية',
      open: 'فتح قائمة الإجراءات',
      close: 'إغلاق قائمة الإجراءات',
    },
    audio: {
      play: 'تشغيل',
      pause: 'إيقاف مؤقت',
      skipBack: 'تخطي للخلف',
      skipFwd: 'تخطي للأمام',
      speed: 'سرعة التشغيل',
      progress: 'تقدم الصوت',
    },
    hdr: {
      openContents: 'فتح المحتويات',
      chOf: 'الفصل {index} من {total}',
      pageOf: 'ص. {page} من {total}',
    },
    snack: {
      bmAdded: 'تمت إضافة الإشارة المرجعية',
      noteSaved: 'تم حفظ الملاحظة',
    },
  },
  interview: {
    narrator: 'المؤلف / الراوي',
    bornYear: 'من مواليد {year}',
    fieldResearcher: 'الباحثة الميدانية: {name}',
    recordingDate: 'تاريخ التسجيل: {date}',
    recordingDateLabel: 'تاريخ التسجيل',
    summary: 'ملخّص',
    details: 'تفاصيل المقابلة',
    birthPlace: 'مكان الميلاد',
    placeOfOrigin: 'البلد الأصلي والقضاء',
    currentAddress: 'العنوان الحالي',
    recordingPlace: 'مكان التسجيل',
    duration: 'المدة',
    durationMin: '{min} دقيقة',
    fieldResearcherLabel: 'الباحثة الميدانية',
    organization: 'الجهة المشرفة',
    supervisingResearcher: 'الباحثة المشرفة',
    audio: 'تسجيل صوتي',
    video: 'فيديو',
    image: 'صورة',
    prevSlide: 'الشريحة السابقة',
    nextSlide: 'الشريحة التالية',
    transcript: 'النص الكامل',
    transcriptExpand: 'انقر للتوسعة',
    transcriptReadTime: 'قراءة {min} دقيقة تقريباً',
    mediaItem: 'عنصر {index}',
    previewUnavailable: 'المعاينة غير متاحة.',
    openFile: 'فتح الملف',
    openFullSize: 'فتح بالحجم الكامل',
  },
  meta: {
    tab: {
      core: 'عام',
      interview: 'الوسائط المتعددة',
      pricing: 'التسعير والوصول',
    },
    field: {
      narratorName: {
        label: 'اسم الراوي',
        desc: 'الاسم الكامل للراوي.',
      },
      narratorImage: {
        label: 'صورة الراوي',
        desc: 'صورة شخصية تظهر في ترويسة سيرة الراوي.',
      },
      birthYear: {
        label: 'سنة الميلاد',
        desc: 'السنة التي وُلد فيها الراوي.',
      },
      birthPlace: {
        label: 'مكان الميلاد',
        desc: 'المدينة أو القرية التي وُلد فيها الراوي.',
      },
      originalTownDistrict: {
        label: 'البلدة الأصلية / القضاء',
        desc: 'بلدة الراوي الأصلية أو قضاؤه.',
      },
      recordingPlace: {
        label: 'مكان التسجيل',
        desc: 'المكان الذي جرى فيه تسجيل المقابلة.',
      },
      recordingDate: {
        label: 'تاريخ التسجيل',
        desc: 'تاريخ تسجيل المقابلة. يُحفَظ بصيغة سنة-شهر-يوم.',
      },
      addressLocation: {
        label: 'العنوان / الموقع',
        desc: 'العنوان أو الموقع الحالي للراوي.',
      },
      recordingDurationMinutes: {
        label: 'مدة التسجيل (بالدقائق)',
        desc: 'إجمالي مدة التسجيل بالدقائق.',
      },
      fieldResearcher: {
        label: 'الباحث الميداني',
        desc: 'اسم الباحث الذي أجرى المقابلة.',
      },
      supervisingOrganization: {
        label: 'الجهة المشرفة',
        desc: 'الجهة التي أشرفت على البحث أو كلّفت به.',
      },
      supervisingResearcher: {
        label: 'الباحث المشرف',
        desc: 'اسم الباحث الذي أشرف على العمل الميداني.',
      },
      title: {
        label: 'العنوان / الترويسة',
        desc: 'يُدخِله المشرف أو المؤلف. إذا تُرك فارغًا، يُملأ تلقائيًا من نص العنوان الرئيسي أو السطر الأول من المستند.',
      },
      author: {
        label: 'المؤلف / التوقيع',
        desc: 'اسم المؤلف أو المصدر. يُدخِله المشرف أو المؤلف، ويُملأ مبدئيًا باسم المؤلف من نظام النشر أو المستند.',
      },
      authorAvatar: {
        label: 'صورة المؤلف',
        desc: 'صورة المؤلف. ارفع صورة لعرضها بجانب اسم المؤلف.',
      },
      publicationDate: {
        label: 'تاريخ النشر',
        desc: 'التاريخ الأصلي لنشر العنصر. يُدخِله المشرف أو المؤلف.',
      },
      summary: {
        label: 'الملخص / الخلاصة',
        desc: 'ملخص قصير للمحتوى. يمكن كتابته يدويًا أو توليده بالذكاء الاصطناعي. إذا تُرك فارغًا، يُملأ تلقائيًا من الفقرة الأولى بعد العنوان.',
      },
      creationDate: {
        label: 'تاريخ الإنشاء',
        desc: 'التاريخ الذي أنشأ فيه النظام المنشور أو المستند.',
      },
      tags: {
        label: 'الوسوم',
        desc: 'مجموعة من الوسوم أو الكلمات المفتاحية التي تصف المحتوى. يمكن إكمالها شبه يدويًا باستخدام قوائم مفردات مضبوطة. حاليًا، حتى ٨ وسوم للمقالات وغير محدودة للأصول.',
      },
      documentType: {
        label: 'نوع المستند',
        desc: 'يختار المنسّق هذا الحقل من خيارات محددة مسبقًا (مثل: كتاب، مقالة، قصيدة، رسالة، صورة، فيديو، صوت).',
      },
      sourceInstitution: {
        label: 'المؤسسة المصدر / الناشر',
        desc: 'معلومات مُسجَّلة يدويًا أو بواسطة النظام عن المؤسسة المصدر أو الناشر.',
      },
      rights: {
        label: 'الحقوق / الترخيص',
        desc: 'يحددها فريق المشروع أو الفريق القانوني. إذا كان المحتوى محفوظ الحقوق، أضِف رابطًا إلى موقع الناشر.',
      },
    },
  },
};

const plurals = {
  and_one_other: 'and one other',
  and_n_others: 'and {n} others',
  view: 'View',
  one_response: 'One response',
  n_responses: '{responseCount} responses',
  one_elided_post: 'One elided post',
  n_elided_posts: '{length} elided posts',
  one_member: 'One member',
  n_members: '{memberCount} members}',
  one_post: 'One post',
  n_posts: '{postCount} posts',
  one_pending_request: 'One pending request',
  n_pending_requests: '{access_request_count} pending requests}'
};

var en = {
  chat: {
    pin: "Pin message",
    unpin: "Unpin message",
    edit: "Edit",
    loadMore: "Load more...",
    search1: "Enter any question in the search window, such as ‘Health services in Preston’, then click on the AI Analysis Button, or just hit Return",
    search2: "For AI search click on the CAIR, Head, icon then click on AI Search button, or just hit Return",
    search3: "For keyword search click on the magnifying glass icon then enter keyword followed by hitting Return"
  },
  auth: {
    or: "OR",
    newPassword: "New Password",
    change2: "Change...",
    change: "Change Password",
    forgot: "Forgot password?",
    verify2: "Verify...",
    verify: "Verify",
    verificationCode: "Verification Code",
    confirmationCode: "Confirmation Code",
    emailOrPhone: "Email or Phone number",
    resetting: "Resetting...",
    reset: "Reset Password",
    sentEmail: "We have emailed you a link to reset your password. Please check your inbox.",
    phoneNoPassword: "Signed up with your phone? You do not need a password \u2014 sign in with your phone number and we will text you a code.",
    resetLinkExpired: "This password-reset link has expired. Please request a new one.",
    changeFailed: "We could not change your password, please try again.",
    noSuchAccount: "We could not find an account with that email address.",
    emailBounced: "We cannot send email to that address. Please contact support.",
    resetFailed: "We could not send the reset email, please try again.",
    register2: "New user? Register!",
    registerWithPhone: "Register with your phone",
    phone: "Phone Number",
    email: "Email",
    password: "Password",
    confirm: "Confirm Password",
    register: "Register",
    registering: "Creating account...",
    agree: "I agree to the Terms of Service",
    alreadyHaveAccount: "Already have an account? Login!",
    registerWithPhone: "Register with phone number",
    login2access: "Please log in to access this page",
    authRequired: "Authentication required",
    register: "Create account",
    login: "Existing user: Login",
    login2: "Login",
    login3: "Logging in...",
    continueWithPhone: "Continue with phone",
    phone2: "Phone",
    country: "Country",
    sendCode: "Send code",
    sendingCode: "Sending code...",
    codeSent: "We sent a verification code to your phone by SMS.",
    phoneLengthExact: "The phone number should be {n} digits (not counting the leading 0).",
    phoneLengthRange: "The phone number should be {min}–{max} digits (not counting the leading 0).",
    enterCode: "Verification code",
    resendCode: "Resend code",
    resendCodeIn: "Resend code in {seconds}s",
    sendCodeWhatsApp: "Get the code on WhatsApp",
    changePhone: "Use a different phone number",
    otpInvalid: "That code is not correct, please try again.",
    otpExpired: "That code has expired, please request a new one.",
    otpTooManyAttempts: "Too many attempts, please request a new code.",
    otpCooldown: "Please wait a minute before requesting another code.",
    otpRateLimited: "Too many requests, please try again later.",
    smsSendFailed: "We could not send the SMS, please try again.",
    otpCountryNotSupported: "We cannot send codes to that country. Please sign up with your email instead.",
    otpCaptchaPending: "Checking your browser\u2026",
    otpFailed: "Something went wrong, please try again.",
    biometricLogin: "Sign in with biometrics",
    biometricFailed: "Biometric sign-in failed, please sign in again.",
    passkeyLogin: "Sign in with a passkey",
    passkeyFailed: "Passkey sign-in failed, please try again.",
    passkeyAdd: "Add a passkey",
    passkeyAdded: "Passkey added",
    passkeyEnrollFailed: "Could not add a passkey, please try again."
  },
  CloseDiscussion: 'Close with this outcome',
  reopen: 'Reopen Discussion',
  close: "close",
  people: "people",
  toggleReponse: 'toggle reponses',
  CreateZoomMeeting: "Create zoom meeting",
  outcome: 'Outcome',
  StartaDiscussion: "Start a Discussion",
  authentication_required: 'Authentication Required',
  statusShort: {
    event: {
      comeLater: "waiting",
      reportAvailable: "finished",
      aboutToStart: "starting",
      joinMeeting: "live",
    }
  },
  ai: {
    details: "AI assistant details",
    name: "Assistant name",
    description: "Assistant description",
    selectAvatar: "Select assistant avatar image",
    selectIcon: "Click to select icon",
    operation: "AI assistant operation",
    selectMainPrompt: "Select Main Prompt",
    freq: "Frequency of Prompt's execution (in seconds)",
    manual: "Manual",
    schedule: "Schedule",
    off: "Off",
    hideUI: "Hide user-enabled processes",
    showUI: "Show user-enabled processes",
    promptSelection: "Prompt selection",
    add: "Add",
    selectPrompt: "Select Prompt",
    thread: "Discussion thread",
    editor: "Editor Menu",
    circle: "Circle Menu",
    alias: "Menu alias",
    delete: "Delete",
    cancel: "Cancel",
    save: "Save",
    clickOn: "Click-on then enter instruction for the AI assistance in the Editor window",
    ask: "Message",
    disable: "Disable AI",
    enable: "Enable AI",
    addAI: "Add AI assistant",
    active: "Active",
    notActive: "Not active",
    edit: "Edit",
    delete: "Delete",
    createdBy: "Created by",
    refresh: "refresh",
    running: "Running",
    notRunning: "Not Running",
    lastExecuted: "Last executed",
    service: "Service",
    update: "Update",
    success: "success",
    logs: "Logs",
    stop: "Stop",
    start: "Start",
    continue: "Continue",
    pause: "Pause",
  },
  common: {
    create: "Create",
  },
  dashboard: {
    breadcrumb: "Dashboard",
    goToBookCair: "Go to BOOK CAIR",
    tooltip: {
      profile: "My World: My Profile and my content",
      allPosts: "Posts for tags I follow from all Circles that I can access",
      circles: "Circles that I have joined",
      discussions: "Discussions that I am member of",
      meetings: "Meetings that I am invited to",
      subCircles: "Click to see sub-Circles",
      parentCircle: "Click to see Parent Circle",
      postDetails: "Click for more details",
    },
    circles: "My Circles",
    discussions: "My Discussions",
    meetings: "My Meetings",
    allPosts: "All Posts",
    showMore: "Show More",
    discover: "Discover",
    completeProfile: "Click here to complete your profile",
  },
  event: {
    types: {
      Music: "Music",
      Culture: "Culture",
      Training: "Training",
      Meeting: "Meeting",
      Sport: "Sport",
      Information: "Information"
    },
    responded: "responded",
    maybe: "Maybe",
    no: "No",
    yes: "Yes, in Person",
    yes_v: "Yes, join virtually",
    yes1: "Yes",
    going: "Going",
    show_more: "show more",
    respondents: "List of respondents",
    organizer: "Organizer",
    address: "Address",
    start: "Event start",
    end: "Event end",
    addTags: "Add Tags",
    name: "Name",
    address: "Address",
    priority: "Event Priority",
    moreInfo: "Add more info /Extend Form",
    collapse: "Collapse form",
    cancel: "Cancel",
    save: "Save",
    roles: "Define roles for a Meeting or Training Event",
    location: "Event's Location",
    startAndEnd: "Start and end of Event",
    eventType: "Event Type",
    describe: "Describe your event",
    sponsor: "Sponsor organization (search assets)",
    noData: "No Events scheduled for this Circle, why don't you start an Event!",
    finishMeeting: "Meeting is finished",
    listing: "Listing",
    upcoming: "Upcoming",
    recent: "Recent",
    hosting: "hosting",
    speaking: "participating",
    andOthers: "and {count} others",
    startsPrefix: "starts:",
    endsPrefix: "ends:",
    comeLater: "Join the event here:",
    reportAvailable: "Meeting Reports available below, comments welcome",
    reportAvailableAdmin: "Please write meeting Outcome below when ready",
    aboutToStart: "Meeting is about to start",
    joinMeeting: "Join the Meeting",
    openZoomApp: "Open with Zoom App",
    openZoomAppBefore: "Opens 30 minutes before start",
    webClient: "Web client ({op})",
    Host: "Host",
    Join: "Join",
    Assistant: "Assistant",
    more: "more",
    see: "see",
    sendInvitation: "Send invitation emails",
    all_party: "Notify all Circle members",
    templates: {
      created: "Event created",
      eventsReminder24: "Remind 24 hours before",
      eventsReminder1: "Remind 1 hours before",
      eventsFinished: "Event finished",
    },
  },
  general: {
    ok: 'OK',
    MeetingTime: "Meeting time:",
    cancel: 'Cancel',
    prev: 'prev',
    next: 'next',
    or: 'or',
    requires_registration: 'Registration Required',
    access_denied: "Access denied",
    request_access_to_circle: "Request access to the circle",
    request_access_to_circle2: "Sorry, you have no access rights to this Circle. Click above to join",
    or_click: "Or you can click here.",
    join2read: "Join Circle to read Post",
    automatically_redirect2circle: "We will automatically redirect you to the Circle to join.",
    no_rights2view_post: "Sorry, you have no access rights to view this post.",
    requires_registration_details:
      'Register an account to enable a host of additional functionality.',
    already_a_member: 'Already a member?',
    sign_up: 'Sign Up',
    sign_in: 'Sign In',
    untitled: 'Untitled Post',
    loading: 'Loading, please wait ...',
    please_wait: 'Please wait ...',
    error: 'Oops, something went wrong',
    view_profile: 'View Profile',
    more: 'see all',
    coming_soon: "We're not quite ready yet - this feature is arriving soon!",
    generating_art: 'Generating a cover image',
    type_to_find_person: 'Start typing to find people ...',
    type_to_find_group: 'Start typing to find circles ...',
    type_to_find_tag: 'Start typing to find tags ...',
    type_to_find_post: 'Start typing to find posts ...',
    no_people: 'Nothing to show',
    no_groups: 'Nothing to show',
    cannot_be_undone: 'WARNING: This action cannot be undone!',
    privacy_policy: 'Privacy Policy',
    terms_and_conditions: 'Terms and Conditions'
  },
  search: {
    placeholder: 'Type to search'
  },
  embed: {
    loading: 'Loading embedded content ...'
  },
  validation_error: {
    username_exists: "User name already exists",
    email_exists: "Email already registered",
    invalid_url: 'Invalid URL',
    required: '{field} is required',
    required_field: 'Field is required',
    invalid_username: 'Username too short',
    invalid_email: 'Invalid Email Address',
    invalid_phone_number: 'Invalid phone number',
    password_must_include_digits: 'Password must include at least one number',
    password_too_short: 'Password must be at least 8 characters',
    passwords_must_match: 'Passwords do not match',
    invalid_domain_name: 'Valid domain name required e.g. www.google.com'
  },
  navigation: {
    login: 'Sign In',
    logout: 'Sign out',
    '/home': 'Home',
    'home/activity': 'Activity',
    'home/news': 'Posts',
    'home/events': 'Events',
    'home/news/tag/posts': 'Posts',
    'home/news/tag/circles': 'Circles',
    'home/people': 'People',
    '/notifications': 'Notifications',
    'notifications/notifications': 'Notifications',
    create: 'Create',
    createEvent: "Create a Meeting Event",
    groups: 'Circles',
    'groups/home': 'My Circles',
    'groups/discover': 'Discover Circles',
    'groups/group/activity': 'Activity',
    'groups/group/outcome': 'Outcome',
    'groups/group/posts': 'Posts',
    'groups/group/reference': 'Reference',
    'groups/group/groups': 'SubCircles',
    'groups/group/events': 'Events',
    'profile/public': 'Profile',
    'profile/posts': 'Profile',
    'profile/drafts': 'Drafts',
    'profile/bookmarks': 'Bookmarks',
    'profile/public/posts': 'Posts',
    'profile/public/events': 'Events',
    'profile/public/responses': 'Responses',
    'profile/public/recommends': 'Recommends',
    'profile/public/bio': 'Bio',
    'home/member': ' ',
    'home/member/posts': 'Posts',
    'home/member/events': 'Events',
    'home/member/responses': 'Responses',
    'home/member/recommends': 'Recommends',
    'home/member/bio': 'Bio',
    'groups/group/membership/access_requests': 'Requests',
    'groups/group/membership/invites': 'Invites',
    'groups/group/membership/members': 'Members',
    search: 'Search'
  },
  login: {
    title: 'Sign In',
    email: 'Email Address',
    password: 'Password',
    login: 'Submit',
    account_not_verified: 'Email address not verified',
    invalid_credentials: 'Invalid Credentials',
    login_with_email: 'Login with Email & Password',
    login_with_facebook: 'Login with Facebook',
    no_such_user: 'This account is not registered',
    reset_password: 'Reset Password',
    sent_password_reset_mail:
      'A password-reset email has been sent to you, please check your email.'
  },
  logout: {
    signing_out: 'Signing out ...'
  },
  landing: {
    line_1: 'Ideation in circles of facilitated discussions to find solutions',
    line_2:
      'Join a Circle, create your own public or private Circles, publish Posts and follow others',
    register_button: 'Get Started',
    browse_as_a_guest: 'or browse as a guest'
  },
  registration: {
    terms: "terms",
    privacy_policy: "privacy policy",
    register_with_email: 'Register with Email',
    welcome: 'Your email address is now confirmed',
    continue_to_the_app: 'Continue',
    confirmation_required_title: 'Confirm your Email',
    confirmation_required_text:
      'We sent an email to {email} containing a PIN to complete your registration.',
    confirmation_pin_field: 'Verification PIN',
    confirm_registration: 'Submit verification PIN',
    continue_with_facebook: 'Continue with Facebook',
    continue_with_twitter: 'Continue with Twitter',
    continue_with_google: 'Continue with Google',
    waiting_for_facebook: 'Waiting for authentication',
    completing_registration: 'Completing your registration',
    completed_registration: 'Registration complete, redirecting ...'
  },
  registration_form: {
    register: 'Register',
    username_field: 'Username',
    email_field: 'Email Address',
    password_field: 'Password',
    password_again_field: 'Re-type Password',
    already_registered: '{field} is already registered',
    already_registered2: 'already registered',
    password_reset_button: 'Reset',
    confirm_password_button: "Confirm"
  },
  password_reset: {
    success: 'Reset Success!',
    failed: 'Password Reset Failed!',
    linkLabel: 'continue',
    signing_in: 'Signing you in ...'
  },
  onboarding: {
    choose_n_tags: 'Choose at least 3 tags to personalize your news feed'
  },
  profile: {
    view: 'View Profile',
    new_post: 'New Post',
    new_event: "New Event",
    edit_button: 'Edit personal information',
    my_short_bio_default: 'Add a short bio',
    their_short_bio_default:
      'This user prefers to keep an air of mystery about them',
    reputation_label: 'reputation',
    followers_label: 'followers',
    following_label: 'following',
    title: 'Edit Profile',
    firstname_field: 'First Name',
    lastname_field: 'Last Name',
    gender_field: 'Gender',
    gender_male: 'Male',
    gender_female: 'Female',
    bio_field: 'Bio',
    profile_photo: 'Profile Pic',
    save: 'save',
    edit_profile_title: 'Edit Profile',
    view_profile_title: 'Profile',
    load_profile: 'Loading Profile',
    follow: {
      is: 'Following',
      is_not: 'Follow',
      checking: 'Follow'
    },
    choose_avatar_label: 'Choose an avatar image',
    drag_image_label: 'Click or Drag Image',
    error_user_404: 'User Does Not Exist',
    logout: 'Logout {username}'
  },
  people: {
    search_for_people:
      'Use keyword search (above right) to search names and biographies.',
    interesting_people:
      'Here are some interesting people you might like to follow.',
    no_results: "We didn't find anyone matching that query"
  },
  import: {
    drop_here:
      'Drop a Microsoft Word (.docx) or PDF file here, or click to browse',
    not_a_word_doc: 'Only PDF or Microsoft Word .docx files can be imported.',
    try_again: 'Try Again',
    uploading: 'Importing Document',
    redirecting_you: 'Redirecting ...'
  },
  compose: {
    unknown_type: 'Unknown Post type `{type}`',
    new: {
      page_title: 'New Post',
      page_evt_title: "Create Event",
      new_public_post: 'Create a New Post',
      page_event: "New Event",
      date: "date",
      thirtyMins: "30 minutes",
      SixtyMins: "60 minutes",
      NinetyMins: "90 minutes",
      OneTwentyMins: "120 minutes",
      CreateMeeting: "create meeting",
      submiting: "submiting",
      Duration: "Duration",
      time: "time",
      end_time: "end time",
      chat_placeholder: "Enter text to Enhance or enter a question for Assist then click the appropriate button, click on Publish to submit your comment.",
      event_title: "Event title",
      create_event: "Create event",
      start_date: "Start date",
      end_date: "end date",
      event_description: "Event description",
      category: "category",
      choose_moderator: "Choose moderator",
      add_participant: "add participant",
      warning:
        'Your post will be public by default. You can control privacy by choosing a Circle at the final publishing step.',
      what: 'What do you want to create?',
      post_an_article: 'Write an Article',
      import_a_document: 'Import a Document',
      announce_an_event: 'Announce an Event',
      thirtyMins: "Thirty minutes",
      SixtyMins: "Sixty minutes",
      NinetyMins: "Ninety minutes",
      minutes150: "150 minutes",
      minutes180: "180 minutes",
      minutes240: "240 minutes",
    },
    tag: {
      page_title: 'Tag your Post',
      page_title_Event: 'Tag your event',
      choose_n_tags: 'Tag your post with up to {n} tags',
      choose_n_tags_events: 'Tag your event with up to {n} tags',
      atLeastOneTag: "Please add at least one tag!",
      tag: 'Tag'
    },
    event: {
      title_schedule_event: "Schedule your event",
      title_schedule_discussion: "Schedule your discussion",
      role: "Role",
      suggested: "Suggested",
      title_roles: "Audience and Roles",
      roles_header: "Select a role and add people to it",
      schedule_header_event: "Set date and time and the type of the event",
      schedule_header_discussion: "Schedule the start and end of the discussion",
      by_name_and_email: "Search by name or email",
      timezone: "timezone",
      meeting_info: "Provide meeting information",
      event_type: "Event Type",
      label: "Additional Information (e.g ‘Join Zoom Meeting’, etc.)",
      url: "URL to additional information",
      label2: "More additional information, e.g link to YouTube streaming",
      url2: "Link for more additional information",
      streamingInfo: "Additional information about streaming service used",
      streamingUrl: "URL for viewing the streaming service used",
      types: {
        google: "Google",
        jitsi: "Jitsi",
        zoom: "Zoom",
        external: "External",
      },
      roles: {
        expert: "Expert",
        host: "Host",
        moderator: "Moderator",
        speaker: "Speaker",
        guest_expert: "Guest Expert",
        panelist: "Panelist",
        participant: "Participant",
        trainer: "Trainer",
      },
      all_members_invited: "All Circle members are invited to this meeting as participants",
    },
    audience: {
      page_title: 'Your Audience',
      publish: 'publish',
      publishing: 'publishing',
      control_visibility:
        'Manage who can see your post by posting publicly or to a Circle you are participating in',
      public_title: 'Public',
      public_description: 'Anyone can see this post',
      group_title: 'Circle',
      group_description: 'Visiblity is controlled by Circle settings',
      choose_group: 'Choose Circle',
      change_group: 'Change'
    },
    share: {
      title: 'Share your creation',
      published_successfully: 'Your post was published successfully.',
      share_post: 'done',
      share_article: 'Select and copy the link below to share:'
    },
    next: 'next',
    external_link: 'External Link',
    link_post: 'Link to a Post',
    embed_post: 'Embed a Post',
    link_field: 'URL',
    link_placeholder: 'Enter a valid http(s) URL',
    description_field: 'Description',
    description_placeholder: 'Description (appears in links section)',
    uploading: 'Uploading {name}',
    link: {
      manager_title: 'Edit Link',
      update: 'Update Link'
    },
    embed: {
      manager_title: 'Edit Embed',
      update: 'Update Embed'
    }
  },
  attachments: {
    manager_title: 'Manage Attachments',
    no_attachments: "You haven't attached any files yet.",
    drop_here: 'Drop files here, or tap to open a file-picker.',
    describe: 'Describe this attachment'
  },
  activity: {
    publish: 'posted in',
    publish_publicly: 'posted publicly',
    publish_to_all: 'posted publicly',
    follow: 'followed',
    reply: 'replied to your post',
    react: 'reacted to a post',
    'create-group': 'started Circle {name}',
    'created-group': 'started Circle {name}', // deprecate
    'join-group': 'joined Circle {name}',
    'joined-group': 'joined Circle {name}', // deprecated
    'close-group': 'ended Circle {name}',
    'reopen-group': 'reopened Circle {name}',
    'invite-to-group': 'invited you to join a Circle',
    'request-access': 'requested access to the Circle "{name}"',
    'removed-post': 'removed a post',
    you: 'You',
    in_group: 'in',
    verbs: {
      publish: 'published {activity_count} posts', // todo: plurals
      follow: 'followed {activity_count} people', // todo: plurals
      reply: 'replied to your post',
      react: 'reacted to a post',
      'join-group': 'joined a Circle',
      'create-group': 'started a new Circle',
      'invite-to-group': 'invited you to join a Circle',
      'reopen-group': 'reopened a Circle',
      'close-group': 'concluded a Circle',
      'request-access': 'requested access to Circles',
      'removed-post': 'removed a post'
    },
    name_comma: ', ',
    name_and: ' and ',
    mentioned_you: 'mentioned you',
    and_n_others: `{n, plural, one {${plurals.and_one_other}} other {${plurals.and_n_others}}}`,
    n_people: '{n} people',
    tap_groups: 'Tap "Circles"',
    join_some_groups: 'to discover Circles that match your interests',
    tap_create: 'Tap "Add"',
    post_some_content: 'to create your own posts',
    view_posts_matching_your_tags:
      'Tap "Posts" to see latest posts and news that match your tags',
    view_events_matching_your_tags:
      'Tap "Events" to see latest events that match your tags',
    follow_some_people: 'Or find interesting people to follow from list below'
  },
  drafts: {
    delete: 'Delete this draft',
    count: '{count, plural, one {# draft} other {# drafts}}'
  },
  posts: {
    send_reminder: "Send reminder email",
    open_thread: "Open thread in a new window",
    empty_list_placeholder: "There's nothing to see here yet.",
    n_minute_read: '{value} min read',
    unpublish: 'Unpublish this post',
    edit: 'Edit this post',
    edit_metadata: 'Edit metadata',
    in_group: ', in {name}',
    pin_and_close_group: 'Pin and close Circle',
    pin_as_group_intro: 'Pin as Circle intro',
    search_placeholder: 'Type to find posts'
  },
  post: {
    continue: "Continue",
    proceed: "Proceeding to Login Page",
    please_login: "This post is for members. Please log in to read it.",
    register_login: "Please register/login first in order to respond.",
    proceed_join: "Joining the Circle",
    join2respond: "Please join the Circle to write a response.",
    join: "Join",
    cancel: "Cancel",
    startNewDiscussionThread: "Start a new discussion thread",
    respond2Thread: "Respond to thread",
    closeDiscussionThread: "Close Discussion thread",
    unflag: "Cancel Report as inappropriate",
    unpublished: 'Post Unavailable',
    unpublished_description:
      'This post has been removed by the author or a moderator',
    responses: 'Responses',
    responses_discussion: 'Discussion Threads',
    respond: 'Click here to write a response to {name}',
    respond_discussion: 'Click here to start a new discussion thread',
    conversation_with: 'Conversation with {name}',
    n_responses: `${plurals.view} {responseCount, plural, one {${plurals.one_response}} other {${plurals.n_responses}}}`,
    maximize_conversation: 'See the whole conversation',
    minimize_conversation: 'Minimize this conversation',
    references: 'Resources and References',
    response_removed: 'This response has been removed',
    response_removed_by: "Comment unpublished by",
    main_post: 'Main discussion post',
    go_back_to_main_post: 'Click on the title below to go to the main post',
    owner_thread: 'Owner thread:',
    go_to_owner_thread: '(Click below to go to owner thread)',
    current_thread: 'Please respond to this thread below: ',
    n_other_posts: `{length, plural, one {${plurals.one_elided_post}} other {${plurals.n_elided_posts}}}`,
    full_screen_composer: 'full screen',
    publish: 'Publish',
    discard: 'discard',
    share: 'Share this Post',
    reactions: {
      title: 'Reactions',
      agreement: 'Agreement',
      agreement_desc:
        'A measure of how much you agree with the overall arguments presented in the post',
      give_feedback: "Please rate the discussion proposal",
      eloquence: 'Eloquence',
      eloquence_desc:
        'A measure of the style, quality and persuasiveness of the writing in this post, regardless if you agree',
      relevance: 'Relevance',
      relevance_desc:
        "A measure of how relevant the post is to the group, and/or the post's tags",
      average: 'Average Rating',
      yours: 'Your Rating',
      cannot_react_to_own_post: 'You cannot react to your own post!'
    }
  },
  tags: {
    follow: 'Follow',
    following: 'Following',
    minimum_required: 'Please provide at least {n} tags',
    tag_field: 'Tag',
    home_following: 'You are currently following this tag',
    home_unfollowing: 'You are not following this tag'
  },
  group: {
    pages: "pages",
    notify_on: "Turn on mobile push notifications",
    notify_off: "Turn off mobile push notifications",
    recentActivities: "Recent Activities",
    createPost: "Create a Post",
    tabs: {
      all: "All Posts",
      discussions: "Discussions",
      meetings: "Events",
      articles: "Articles",
      references: "References",
      subCircles: "SubCircles",
      map: "Map",
    },
    actions: {
      startDiscussion: "Start a Discussion",
      announceEvent: "Announce event",
      announce: "Announce a meeting",
      writeArticle: "Write an Article",
      import: "Import a Document",
      record: "Record an Asset",
      createInterview: "Create Media Post",
    },
    tooltips: {
      dashboard: "Your Dashboard",
      help: "Help page",
      notifications: "Latest notification",
      members: "Members of this Circle",
    },
    totalResponses: "Total responses",
    searchHere: "Search here",
    empty_list_placeholder:
      "You don't appear to be participating in any Circles",
    discover_some_groups:
      'Tap "Discover" to find Circles that match your interests',
    tap_add: 'Tap "Add"',
    create_your_own_groups:
      'to start your own Circles and invite other members to join!',
    no_activity: "There's nothing to see here yet.",
    post_something: 'Post something',
    import_a_document: 'import a document',
    edit_group: 'Edit this Circle',
    new_sub_group: 'Create Sub-Circle',
    invite: 'Invite new members',
    send_invite: 'send',
    pending_invite: 'has been invited',
    already_a_member: 'is already a Member',
    request_access_to_circle: 'Request Access',
    request_access: {
      title: 'Request access to Circle',
      request: 'Request Access',
      description:
        'Tap the button below to request access to this Circle. The administrators of the Circle will be notified of your request.',
      already_requested:
        'You already have a pending request to join this Circle.',
      already_rejected:
        'Your existing request to join this Circle was rejected.'
    },
    only_visible_to_members: 'Content is only visible to members of the Circle',
    join: 'Join this Circle',
    login_to_join: 'Login to join this Circle',
    leave: 'Leave this Circle',
    reopen: 'Re-Open this Circle',
    close: 'Close this Circle',
    close_did_you_know:
      'When ending a Circle you can choose one post within the Circle to "pin" as a representative post or "outcome" for the Circle.',
    close_how_to_pin: 'To close with a pinned "outcome" post:',
    close_find_post: 'In the "Posts" tab, find the post you want to pin',
    close_tap_overflow:
      'Open the context menu by tapping the overflow icon on the post',
    close_pin_as_outcome: 'In the context menu, tap "Pin as Circle outcome"',
    close_is_better_with_outcome:
      'Closing with a pinned outcome is highly recommended',
    close_without_outcome: 'Close without pinning',
    you_are_closing_this_group:
      'Warning: You are about to end this Circle, which will prevent new posts being published to this Circle.',
    close_and_set_outcome: 'Close with Outcome',
    outcome_to_be_pinned: 'Outcome post to be pinned:',
    increase_privacy_by_one_level:
      'Increase the privacy of this Circle to the next access-level:',
    make_more_private: 'Increase Privacy',
    enable_content_digest: 'Include daily email digest',
    disable_content_digest: 'Exclude daily email digest',
    summary: {
      privacy: {
        PUBLIC: 'Public',
        public: 'Public', // deprecated
        REGISTERED: 'Registered Users Only',
        registered: 'Registered Users Only', // deprecated
        PRIVATE: 'Invitation Only',
        private: 'Invitation Only', // deprecated
        PERSONAL: 'Personal Circle',
        personal: 'Personal Circle', // deprecated
      },
      stats: `{memberCount, plural, one {${plurals.one_member}} other {${plurals.n_members}}, {postCount, plural, one {${plurals.one_post}} other {${plurals.n_posts}}}`
    },
    info: {
      privacy: {
        PUBLIC: 'Publicly Accessible',
        public: 'Publicly Accessible', // deprecated
        REGISTERED: 'Accessible by registered users',
        registered: 'Accessible by registered users', // deprecated
        PRIVATE: 'Access by invitation only',
        private: 'Access by invitation only', // deprecated
        PERSONAL: 'Personal Circle',
        personal: 'Personal Circle', // deprecated
        cannot_be_less_private_than_parent:
          'Sub-circles cannot be less private than their parent circle.'
      },
      intro: 'Read this first!',
      closed: '(closed to further activity)',
      pending_access_requests: `{access_request_count, plural, one {${plurals.one_pending_request}} other {${plurals.n_pending_requests}}`,
      membership: {
        ADMIN: {
          true: 'You are an administrator',
          false: 'You are an administrator'
        },
        MEMBER: {
          true: 'You are a member (read-only)',
          false: 'You are a member'
        },
        undefined: 'You are not a member'
      },
      guest: 'Viewing as a guest',
      members: `{memberCount, plural, one {${plurals.one_member}} other {${plurals.n_members}}`,
      posts: `{postCount, plural, one {${plurals.one_post}} other {${plurals.n_posts}}}`,
      view_members: '(view members)'
    },
    discover: {
      invitations: 'Invitations',
      suggestions: 'Suggested for you',
      invited_you: 'invited you to join this Circle',
      nothing_to_see: "It's a bit quiet around here, try again later!",
      join: 'Join Circle',
      dont_join: 'Discard Invitation',
      discarded: 'This invitation has been discarded',
      joined: "You've joined this Circle",
      go_to_group: 'Visit Circle'
    },
    create: {
      basics_page_title: 'Start a Circle',
      privacy_page_title: 'Privacy Settings',
      select_privacy: "Please select your privacy settings",
      privacy_subcircle: "If your are creating a sub-circle, its privacy can not be lower than that of its parent",
      members_page_title: 'Invite Members',
      schedule_page_title: 'Schedule',
      tags: 'Tags',
      next: 'next',
      finish: 'create',
      name_field: 'Circle Name',
      name_placeholder: 'Enter a name for your Circle',
      members_read_only_field: 'Read-Only for non-admins',
      only_admins_can_post: 'Only admins can post',
      members_and_admins_can_post: 'Members and admins can post',
      community: 'Community',
      accessible_by_all_users: 'Accessible by all users',
      members_are_invited: 'Members are invited',
      specify_at_least_one_tag:
        'Specify at least one tag that describes your Circle',
      privacy: 'Privacy',
      privacy_public_title: 'Public',
      privacy_public_description:
        'Anyone can read posts in this Circle. Any registered user can contribute content to the Circle.',
      privacy_registered_title: 'Registered Users',
      privacy_registered_description:
        'Only registered users can read and post content in this Circle.',
      privacy_private_title: 'Private',
      privacy_private_description:
        'Only people you invite can join the Circle to read and contribute.',
      privacy_personal_title: 'Personal',
      privacy_personal_description:
        'Only you can join the Circle to read and contribute.',
      send_invitations_to: 'Invitations',
      invite_some_people: "You haven't invited anyone yet",
      sponsor_page_title: 'Sponsorship',
      sponsor: 'Sponsor',
      associate_sponsor: 'Associate a sponsor for this Circle, if you have one',
      schedule: 'Schedule'
    },
    invitation: {
      page_title: "Create your password now",
      invited_header: "You are invited to join the circle",
      circle: "The circle:",
      invited_by: "Invited by:",
      agree_to_terms: "I agree to the Privacy Policy and the Terms and Conditions",
      intro: "After you confirm your password, you will be able to join events and get access to other platform services",
    },
    invitations: {
      page_title: "Invite new members to this circle",
      intro: "Expand your community! You can reach out via email to individuals who aren't yet part of the platform, encouraging them to register and join your Circle. Alternatively, you can also choose from existing platform users and invite them to enrich your Circle as new members.",
      email_header: "Invite new users by sending them emails to register and join the circle",
      emails_field: "List emails (comma-separated) …",
      send_email_button: "Validate and Send emails to invite",
      registered_toast: "inviting users …",
      register_msg: "Register",
      invite_registered: "Invite already registered users to join this circle",
      person_picker: "Start typing to find usernames and pick to invite …",
      invite: "Invite",
      invited_toast: "Users are invited",
      invite_msg: "Inviting",
    },
    edit: {
      basics_page_title: 'Edit Circle',
      finish: 'save'
    },
    members_page: {
      title: 'Members of {name}',
      since: 'joined',
      role: {
        ADMIN: '(Administrator)',
        MEMBER: '(Member)'
      },
      invited_by: 'Invited by',
      promote_to_admin: 'Promote to Circle Administrator',
      demote_to_member: 'Demote to Circle Member'
    },
    access_requests: {
      manage: 'Manage Circle',
      accept: 'Grant Access',
      reject: 'Deny Access',
      confirmation_required_title: 'Confirmation Required',
      user_will_be_notified_of_rejection:
        'Tap the button below to deny this access request. The user who made the request will be notified that their request has been rejected.',
      REJECTED: 'Rejected by {username}',
      ACCEPTED: 'Accepted by {username}'
    },
    drafts: {

    }
  },
  buttons: {
    zoom_meeting: 'Join Zoom Meeting',
    ok: 'Ok',
    cancel: 'Cancel',
  },
  post_dialog: {
    pre_welcome: 'Welcome to our discussion',
    post_welcome: 'This discussion is now open for your contribution to the proposals below.',
    tour_help: 'Take the tour to learn more. You can always access this help screen later from the help (questionmark) icon in the appbar.',
    post_closed: 'This discussion has been closed by the Moderator. Please read the discussions and their outcome below.',
    line_1: 'Helping you with responding to',
    line_2_1: 'You can respond at a higher level, clicking in the',
    line_2_2: 'Responses field, or you can respond to a Comment by clicking on the',
    line_2_3: 'next to the comment.',
    line_3: 'Moderator is always able to close discussion with Outcome or re-open it again.',
    line_4: 'Taking Part in Discussions',
    tab_1: 'Tour',
    tab_2: 'Responding',
    tab_3: 'Outcome',
    button: 'tour',
    discussion_outcome: 'Moderator is always able to close discussion with Outcome or re-open it again from overflow menu.',
    comment_response: 'Comment on a particular response. You can always go up the thread’s hierarchy by clicking on a button higher up. You may also start a completely new thread from the Responses field at the top.',
    response_menu_moderator: 'The Moderator can enter a response or choose an existing response and close the discussions with it becoming the Outcome. No more editing of the discussion is possible unless the Moderator chooses to reopen the discussion from the outcome’s overflow menu.',
    profile: 'In the author profile you can see their recent posts or follow their new posts. A copy of your responses are kept safe in your own Profile, accessible from the main menu. From there you can edit or unpublish your responses!',
    rate: 'Click on ‘Please rate the discussion’s proposal’ to give your first impression of the proposal.',
    online_users: 'The icon shows the  number of colleagues that have opened the same discussion and may be taking part. Click on it to find their names.',
    Notifications: 'The icon toggles the side panel to show alerts such as notifying new Responses arriving.',
    respond: 'If you are the proud first responder, write the first response under "Responses" and click Publish!',
    fullscreen: 'In responses and comments you can format text inline. To do more, such as attaching an image, adding a link or embedding a document reference or a video, click on ‘full screen’ while responding.',
    response_menu_authour: 'As author, you can unpublish or edit your response after you have published it. If you are a Circle Admin / Moderator you can also edit or unpublish the response with the original author empowered to edit and republish it. ',
    circle: {
      title: 'Welcome to your Circle',
      line_1: 'You can follow articles, discussions and meeting events in this circle. You can also author your own contribution or start your own event. We will take you through a quick tour of this Circle.',
      line_2: 'You can always revisit this tour later from the help (question mark) icon in the appbar.',
      button: 'Features tour',
    },
  },
  email_registration: {
    agree: 'I agree to the',
    privacy: 'Privacy Policy',
    post_privacy: 'and the',
    terms: 'Terms and Conditions',
  },
  dialogs: {
    online: 'Online users',
    configure_notifications: 'Configure notifications',
    show_toasts: 'Show toasts (pop-up notifications)',
    show_notifications: 'Show desktop notifications',
  },
  accordion: {
    recent_notes: 'Recent notes',
    recent_votes: 'Recent votes',
  },
  placeholders: {
    opinion: 'Short opinion',
  },
  toolbars: {
    notifications: 'Notifications',
    report: 'Report as inappropriate',
  },
  no_such_post: "We couldn't find that post!",
  circle_listing: {
    collapse: 'Collapse sub circles',
    expand: 'Expand sub circles',
    c_posts: 'posts',
    c_members: 'members',
    go_to_circle: 'Click to go to circle'
  },
  br: {
    exit: 'Exit',
    share: 'Share',
    viewDetails: 'View book details',
    savedText: 'Saved text',
    selText: 'Selected text',
    back: 'Back',

    bm: {
      title: 'Bookmark',
      titleAdd: 'Add Bookmark',
      name: 'Name',
      ph: 'Bookmark name',
      save: 'Save bookmark',
      remove: 'Remove bookmark',
    },

    note: {
      title: 'Note',
      titleAdd: 'Add Note',
      label: 'Note',
      ph: 'Write your note\u2026',
      save: 'Save note',
      remove: 'Remove note',
    },
    ai: {
      title: 'Ask CAIR',
      ph: 'Ask anything',
      disclaimer: 'AI responses are scoped to this document only',
      ask: 'Ask',
      searching: 'Searching\u2026',
      sources: '{count, plural, one {1 Source} other {# Sources}}',
      readAloud: 'Read aloud',
      stopReading: 'Stop reading',
      newQ: 'New question',
    },
    prefs: {
      title: 'Reader Preferences',
      reset: 'Reset to defaults',
      fontType: 'Font Type',
      fontSize: 'Font Size',
      lineSpacing: 'Line Spacing',
      theme: 'Theme',
      font: {
        inter: { label: 'Inter', sub: 'Sans-serif' },
        times: { label: 'Times New Roman', sub: 'Traditional' },
        opendyslexic: { label: 'OpenDyslexic', sub: 'Accessibility' },
        'arabic-naskh': { label: '\u0646\u0648\u062a\u0648 \u0646\u0633\u062e \u0639\u0631\u0628\u064a', sub: 'Arabic Serif' },
        'arabic-kufi': { label: '\u0643\u0648\u0641\u064a \u0639\u0631\u0628\u064a', sub: 'Arabic Sans' },
        'arabic-muna': { label: 'منى', sub: 'Arabic Naskh' },
      },
      themes: {
        light: 'Light',
        sepia: 'Sepia',
        dark: 'Dark',
      },
    },
    content: {
      title: 'Content',
      close: 'Close',
      toc: 'Table of content',
      bookmarks: 'Bookmarks',
      noBm: 'No bookmarks yet',
      notes: 'Notes',
      noNotes: 'No notes yet',
      continue: 'Continue Reading',
      search: 'Search',
    },
    sel: {
      askAi: 'Ask CAIR',
      bookmark: 'Bookmark',
      note: 'Note',
    },
    dial: {
      search: 'Search',
      askAi: 'Ask AI',
      settings: 'Reader settings',
      translate: 'Translate',
      toc: 'Table of contents',
      save: 'Save bookmark',
      open: 'Open actions menu',
      close: 'Close actions menu',
    },
    audio: {
      play: 'Play',
      pause: 'Pause',
      skipBack: 'Skip back',
      skipFwd: 'Skip forward',
      speed: 'Playback speed',
      progress: 'Audio progress',
    },
    hdr: {
      openContents: 'Open contents',
      chOf: 'Ch {index} of {total}',
      pageOf: 'p. {page} of {total}',
    },
    snack: {
      bmAdded: 'Bookmark added',
      noteSaved: 'Note saved',
    },
  },
  interview: {
    narrator: 'Author/Narrator',
    bornYear: 'b. {year}',
    fieldResearcher: 'Field researcher: {name}',
    recordingDate: 'Recording date: {date}',
    recordingDateLabel: 'Recording date',
    summary: 'Summary',
    details: 'Interview details',
    birthPlace: 'Birthplace',
    placeOfOrigin: 'Place of origin',
    currentAddress: 'Current address',
    recordingPlace: 'Recording place',
    duration: 'Duration',
    durationMin: '{min} min',
    fieldResearcherLabel: 'Field researcher',
    organization: 'Organization',
    supervisingResearcher: 'Supervising researcher',
    audio: 'Audio',
    video: 'Video',
    image: 'Image',
    prevSlide: 'Previous slide',
    nextSlide: 'Next slide',
    transcript: 'TRANSCRIPT',
    transcriptExpand: 'tap to expand',
    transcriptReadTime: '~{min} min read',
    mediaItem: 'Item {index}',
    previewUnavailable: 'Preview unavailable.',
    openFile: 'Open file',
    openFullSize: 'Open full size',
  },
  meta: {
    tab: {
      core: 'General',
      interview: 'Multimedia',
      pricing: 'Pricing & Access',
    },
    field: {
      narratorName: {
        label: 'Narrator Name',
        desc: 'Full name of the narrator / interviewee.',
      },
      narratorImage: {
        label: 'Narrator Image',
        desc: 'Portrait photo shown in the narrator bio header.',
      },
      birthYear: {
        label: 'Birth Year',
        desc: 'Year the narrator was born.',
      },
      birthPlace: {
        label: 'Birthplace',
        desc: 'City or village where the narrator was born.',
      },
      originalTownDistrict: {
        label: 'Original Town / District',
        desc: "The narrator's town or district of origin.",
      },
      recordingPlace: {
        label: 'Recording Place',
        desc: 'Location where the interview was recorded.',
      },
      recordingDate: {
        label: 'Recording Date',
        desc: 'Date the interview was recorded. Stored as YYYY-MM-DD.',
      },
      addressLocation: {
        label: 'Address / Location',
        desc: 'Current address or location of the narrator.',
      },
      recordingDurationMinutes: {
        label: 'Recording Duration (minutes)',
        desc: 'Total length of the recording in minutes.',
      },
      fieldResearcher: {
        label: 'Field Researcher',
        desc: 'Name of the researcher who conducted the interview.',
      },
      supervisingOrganization: {
        label: 'Supervising Organization',
        desc: 'Organization that supervised or commissioned the research.',
      },
      supervisingResearcher: {
        label: 'Supervising Researcher',
        desc: 'Name of the researcher who supervised the field work.',
      },
      title: {
        label: 'Title / Headline',
        desc: 'Entered by Admin or author. If empty, auto-filled by the heading 1 text or the first line of the document.',
      },
      author: {
        label: 'Author / Byline',
        desc: "The name of the author or source. Entered by Admin or author, initially auto-filled by the author's name from the publishing system or document.",
      },
      authorAvatar: {
        label: 'Author Photo',
        desc: "Photo of the author. Upload an image to display alongside the author's name.",
      },
      publicationDate: {
        label: 'Date of Publication',
        desc: 'The date the item was originally published. Entered by Admin or author.',
      },
      summary: {
        label: 'Summary / Abstract',
        desc: 'A short summary of the content. Can be manually created or generated using an AI/LLM. If empty, auto-filled from the first paragraph after the title.',
      },
      creationDate: {
        label: 'Date of Creation',
        desc: 'The system-generated date when the post or document was created.',
      },
      tags: {
        label: 'Tags',
        desc: 'A set of tags or keywords describing the content. Can be completed semi-manually using controlled vocabulary lists. Currently, up to 8 tags for articles and unlimited for assets.',
      },
      documentType: {
        label: 'Document Type',
        desc: 'The curator selects this field from predefined options (e.g., Book, Article, Poem, Letter, Photo, Video, Audio).',
      },
      sourceInstitution: {
        label: 'Source Institution/Publisher',
        desc: 'Manually recorded or system-logged information about the source institution or publisher.',
      },
      rights: {
        label: 'Rights / License',
        desc: "Determined by the project or legal team. If copyrighted, include a link to the publisher's website.",
      },
    },
  },
};

const getTranslation = (language) => {
  return language === ARABIC ? flatten(ar) : flatten(en);
};

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function getAugmentedNamespace(n) {
	if (n.__esModule) return n;
	var a = Object.defineProperty({}, '__esModule', {value: true});
	Object.keys(n).forEach(function (k) {
		var d = Object.getOwnPropertyDescriptor(n, k);
		Object.defineProperty(a, k, d.get ? d : {
			enumerable: true,
			get: function () {
				return n[k];
			}
		});
	});
	return a;
}

var shouldPolyfill$3 = {};

/**
 * http://ecma-international.org/ecma-402/7.0/index.html#sec-canonicalizelocalelist
 * @param locales
 */
function CanonicalizeLocaleList$1(locales) {
    return Intl.getCanonicalLocales(locales);
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */

var extendStatics$1 = function(d, b) {
    extendStatics$1 = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
    return extendStatics$1(d, b);
};

function __extends$1(d, b) {
    if (typeof b !== "function" && b !== null)
        throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
    extendStatics$1(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

function __spreadArray$1(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var data$1 = {
    supplemental: {
        languageMatching: {
            'written-new': [
                {
                    paradigmLocales: {
                        _locales: 'en en_GB es es_419 pt_BR pt_PT',
                    },
                },
                {
                    $enUS: {
                        _value: 'AS+CA+GU+MH+MP+PH+PR+UM+US+VI',
                    },
                },
                {
                    $cnsar: {
                        _value: 'HK+MO',
                    },
                },
                {
                    $americas: {
                        _value: '019',
                    },
                },
                {
                    $maghreb: {
                        _value: 'MA+DZ+TN+LY+MR+EH',
                    },
                },
                {
                    no: {
                        _desired: 'nb',
                        _distance: '1',
                    },
                },
                {
                    bs: {
                        _desired: 'hr',
                        _distance: '4',
                    },
                },
                {
                    bs: {
                        _desired: 'sh',
                        _distance: '4',
                    },
                },
                {
                    hr: {
                        _desired: 'sh',
                        _distance: '4',
                    },
                },
                {
                    sr: {
                        _desired: 'sh',
                        _distance: '4',
                    },
                },
                {
                    aa: {
                        _desired: 'ssy',
                        _distance: '4',
                    },
                },
                {
                    de: {
                        _desired: 'gsw',
                        _distance: '4',
                        _oneway: 'true',
                    },
                },
                {
                    de: {
                        _desired: 'lb',
                        _distance: '4',
                        _oneway: 'true',
                    },
                },
                {
                    no: {
                        _desired: 'da',
                        _distance: '8',
                    },
                },
                {
                    nb: {
                        _desired: 'da',
                        _distance: '8',
                    },
                },
                {
                    ru: {
                        _desired: 'ab',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ach',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    nl: {
                        _desired: 'af',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ak',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'am',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    es: {
                        _desired: 'ay',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    ru: {
                        _desired: 'az',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    ur: {
                        _desired: 'bal',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    ru: {
                        _desired: 'be',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'bem',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    hi: {
                        _desired: 'bh',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'bn',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'bo',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    fr: {
                        _desired: 'br',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    es: {
                        _desired: 'ca',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    fil: {
                        _desired: 'ceb',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'chr',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'ckb',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    fr: {
                        _desired: 'co',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    fr: {
                        _desired: 'crs',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    sk: {
                        _desired: 'cs',
                        _distance: '20',
                    },
                },
                {
                    en: {
                        _desired: 'cy',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ee',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'eo',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    es: {
                        _desired: 'eu',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    da: {
                        _desired: 'fo',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    nl: {
                        _desired: 'fy',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ga',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'gaa',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'gd',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    es: {
                        _desired: 'gl',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    es: {
                        _desired: 'gn',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    hi: {
                        _desired: 'gu',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ha',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'haw',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    fr: {
                        _desired: 'ht',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    ru: {
                        _desired: 'hy',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ia',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ig',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'is',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    id: {
                        _desired: 'jv',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ka',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    fr: {
                        _desired: 'kg',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    ru: {
                        _desired: 'kk',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'km',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'kn',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'kri',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    tr: {
                        _desired: 'ku',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    ru: {
                        _desired: 'ky',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    it: {
                        _desired: 'la',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'lg',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    fr: {
                        _desired: 'ln',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'lo',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'loz',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    fr: {
                        _desired: 'lua',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    hi: {
                        _desired: 'mai',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'mfe',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    fr: {
                        _desired: 'mg',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'mi',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ml',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    ru: {
                        _desired: 'mn',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    hi: {
                        _desired: 'mr',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    id: {
                        _desired: 'ms',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'mt',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'my',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ne',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    nb: {
                        _desired: 'nn',
                        _distance: '20',
                    },
                },
                {
                    no: {
                        _desired: 'nn',
                        _distance: '20',
                    },
                },
                {
                    en: {
                        _desired: 'nso',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ny',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'nyn',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    fr: {
                        _desired: 'oc',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'om',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'or',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'pa',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'pcm',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ps',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    es: {
                        _desired: 'qu',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    de: {
                        _desired: 'rm',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'rn',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    fr: {
                        _desired: 'rw',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    hi: {
                        _desired: 'sa',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'sd',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'si',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'sn',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'so',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'sq',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'st',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    id: {
                        _desired: 'su',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'sw',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ta',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'te',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    ru: {
                        _desired: 'tg',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ti',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    ru: {
                        _desired: 'tk',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'tlh',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'tn',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'to',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    ru: {
                        _desired: 'tt',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'tum',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'ug',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    ru: {
                        _desired: 'uk',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ur',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    ru: {
                        _desired: 'uz',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    fr: {
                        _desired: 'wo',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'xh',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'yi',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'yo',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'za',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'zu',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'aao',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'abh',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'abv',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'acm',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'acq',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'acw',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'acx',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'acy',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'adf',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'aeb',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'aec',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'afb',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'ajp',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'apc',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'apd',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'arq',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'ars',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'ary',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'arz',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'auz',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'avl',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'ayh',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'ayl',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'ayn',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'ayp',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'bbz',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'pga',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'shu',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'ssh',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    az: {
                        _desired: 'azb',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    et: {
                        _desired: 'vro',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ff: {
                        _desired: 'ffm',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ff: {
                        _desired: 'fub',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ff: {
                        _desired: 'fue',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ff: {
                        _desired: 'fuf',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ff: {
                        _desired: 'fuh',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ff: {
                        _desired: 'fui',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ff: {
                        _desired: 'fuq',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ff: {
                        _desired: 'fuv',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    gn: {
                        _desired: 'gnw',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    gn: {
                        _desired: 'gui',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    gn: {
                        _desired: 'gun',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    gn: {
                        _desired: 'nhd',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    iu: {
                        _desired: 'ikt',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    kln: {
                        _desired: 'enb',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    kln: {
                        _desired: 'eyo',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    kln: {
                        _desired: 'niq',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    kln: {
                        _desired: 'oki',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    kln: {
                        _desired: 'pko',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    kln: {
                        _desired: 'sgc',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    kln: {
                        _desired: 'tec',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    kln: {
                        _desired: 'tuy',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    kok: {
                        _desired: 'gom',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    kpe: {
                        _desired: 'gkp',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'ida',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'lkb',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'lko',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'lks',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'lri',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'lrm',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'lsm',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'lto',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'lts',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'lwg',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'nle',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'nyd',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'rag',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    lv: {
                        _desired: 'ltg',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    mg: {
                        _desired: 'bhr',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    mg: {
                        _desired: 'bjq',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    mg: {
                        _desired: 'bmm',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    mg: {
                        _desired: 'bzc',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    mg: {
                        _desired: 'msh',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    mg: {
                        _desired: 'skg',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    mg: {
                        _desired: 'tdx',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    mg: {
                        _desired: 'tkg',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    mg: {
                        _desired: 'txy',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    mg: {
                        _desired: 'xmv',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    mg: {
                        _desired: 'xmw',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    mn: {
                        _desired: 'mvf',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'bjn',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'btj',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'bve',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'bvu',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'coa',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'dup',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'hji',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'id',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'jak',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'jax',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'kvb',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'kvr',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'kxd',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'lce',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'lcf',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'liw',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'max',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'meo',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'mfa',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'mfb',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'min',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'mqg',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'msi',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'mui',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'orn',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'ors',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'pel',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'pse',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'tmw',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'urk',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'vkk',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'vkt',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'xmm',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'zlm',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'zmi',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ne: {
                        _desired: 'dty',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    om: {
                        _desired: 'gax',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    om: {
                        _desired: 'hae',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    om: {
                        _desired: 'orc',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    or: {
                        _desired: 'spv',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ps: {
                        _desired: 'pbt',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ps: {
                        _desired: 'pst',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qub',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qud',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'quf',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qug',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'quh',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'quk',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qul',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qup',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qur',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qus',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'quw',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qux',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'quy',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qva',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qvc',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qve',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qvh',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qvi',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qvj',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qvl',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qvm',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qvn',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qvo',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qvp',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qvs',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qvw',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qvz',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qwa',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qwc',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qwh',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qws',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qxa',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qxc',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qxh',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qxl',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qxn',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qxo',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qxp',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qxr',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qxt',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qxu',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qxw',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    sc: {
                        _desired: 'sdc',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    sc: {
                        _desired: 'sdn',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    sc: {
                        _desired: 'sro',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    sq: {
                        _desired: 'aae',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    sq: {
                        _desired: 'aat',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    sq: {
                        _desired: 'aln',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    syr: {
                        _desired: 'aii',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    uz: {
                        _desired: 'uzs',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    yi: {
                        _desired: 'yih',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'cdo',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'cjy',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'cpx',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'czh',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'czo',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'gan',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'hak',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'hsn',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'lzh',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'mnp',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'nan',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'wuu',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'yue',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    '*': {
                        _desired: '*',
                        _distance: '80',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'am-Ethi',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'ru-Cyrl': {
                        _desired: 'az-Latn',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'bn-Beng',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'zh-Hans': {
                        _desired: 'bo-Tibt',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'ru-Cyrl': {
                        _desired: 'hy-Armn',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'ka-Geor',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'km-Khmr',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'kn-Knda',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'lo-Laoo',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'ml-Mlym',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'my-Mymr',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'ne-Deva',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'or-Orya',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'pa-Guru',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'ps-Arab',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'sd-Arab',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'si-Sinh',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'ta-Taml',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'te-Telu',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'ti-Ethi',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'ru-Cyrl': {
                        _desired: 'tk-Latn',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'ur-Arab',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'ru-Cyrl': {
                        _desired: 'uz-Latn',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'yi-Hebr',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'sr-Cyrl': {
                        _desired: 'sr-Latn',
                        _distance: '5',
                    },
                },
                {
                    'zh-Hans': {
                        _desired: 'za-Latn',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'zh-Hans': {
                        _desired: 'zh-Hani',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    'zh-Hant': {
                        _desired: 'zh-Hani',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    'ar-Arab': {
                        _desired: 'ar-Latn',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    'bn-Beng': {
                        _desired: 'bn-Latn',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    'gu-Gujr': {
                        _desired: 'gu-Latn',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    'hi-Deva': {
                        _desired: 'hi-Latn',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    'kn-Knda': {
                        _desired: 'kn-Latn',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    'ml-Mlym': {
                        _desired: 'ml-Latn',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    'mr-Deva': {
                        _desired: 'mr-Latn',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    'ta-Taml': {
                        _desired: 'ta-Latn',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    'te-Telu': {
                        _desired: 'te-Latn',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    'zh-Hans': {
                        _desired: 'zh-Latn',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    'ja-Jpan': {
                        _desired: 'ja-Latn',
                        _distance: '5',
                        _oneway: 'true',
                    },
                },
                {
                    'ja-Jpan': {
                        _desired: 'ja-Hani',
                        _distance: '5',
                        _oneway: 'true',
                    },
                },
                {
                    'ja-Jpan': {
                        _desired: 'ja-Hira',
                        _distance: '5',
                        _oneway: 'true',
                    },
                },
                {
                    'ja-Jpan': {
                        _desired: 'ja-Kana',
                        _distance: '5',
                        _oneway: 'true',
                    },
                },
                {
                    'ja-Jpan': {
                        _desired: 'ja-Hrkt',
                        _distance: '5',
                        _oneway: 'true',
                    },
                },
                {
                    'ja-Hrkt': {
                        _desired: 'ja-Hira',
                        _distance: '5',
                        _oneway: 'true',
                    },
                },
                {
                    'ja-Hrkt': {
                        _desired: 'ja-Kana',
                        _distance: '5',
                        _oneway: 'true',
                    },
                },
                {
                    'ko-Kore': {
                        _desired: 'ko-Hani',
                        _distance: '5',
                        _oneway: 'true',
                    },
                },
                {
                    'ko-Kore': {
                        _desired: 'ko-Hang',
                        _distance: '5',
                        _oneway: 'true',
                    },
                },
                {
                    'ko-Kore': {
                        _desired: 'ko-Jamo',
                        _distance: '5',
                        _oneway: 'true',
                    },
                },
                {
                    'ko-Hang': {
                        _desired: 'ko-Jamo',
                        _distance: '5',
                        _oneway: 'true',
                    },
                },
                {
                    '*-*': {
                        _desired: '*-*',
                        _distance: '50',
                    },
                },
                {
                    'ar-*-$maghreb': {
                        _desired: 'ar-*-$maghreb',
                        _distance: '4',
                    },
                },
                {
                    'ar-*-$!maghreb': {
                        _desired: 'ar-*-$!maghreb',
                        _distance: '4',
                    },
                },
                {
                    'ar-*-*': {
                        _desired: 'ar-*-*',
                        _distance: '5',
                    },
                },
                {
                    'en-*-$enUS': {
                        _desired: 'en-*-$enUS',
                        _distance: '4',
                    },
                },
                {
                    'en-*-GB': {
                        _desired: 'en-*-$!enUS',
                        _distance: '3',
                    },
                },
                {
                    'en-*-$!enUS': {
                        _desired: 'en-*-$!enUS',
                        _distance: '4',
                    },
                },
                {
                    'en-*-*': {
                        _desired: 'en-*-*',
                        _distance: '5',
                    },
                },
                {
                    'es-*-$americas': {
                        _desired: 'es-*-$americas',
                        _distance: '4',
                    },
                },
                {
                    'es-*-$!americas': {
                        _desired: 'es-*-$!americas',
                        _distance: '4',
                    },
                },
                {
                    'es-*-*': {
                        _desired: 'es-*-*',
                        _distance: '5',
                    },
                },
                {
                    'pt-*-$americas': {
                        _desired: 'pt-*-$americas',
                        _distance: '4',
                    },
                },
                {
                    'pt-*-$!americas': {
                        _desired: 'pt-*-$!americas',
                        _distance: '4',
                    },
                },
                {
                    'pt-*-*': {
                        _desired: 'pt-*-*',
                        _distance: '5',
                    },
                },
                {
                    'zh-Hant-$cnsar': {
                        _desired: 'zh-Hant-$cnsar',
                        _distance: '4',
                    },
                },
                {
                    'zh-Hant-$!cnsar': {
                        _desired: 'zh-Hant-$!cnsar',
                        _distance: '4',
                    },
                },
                {
                    'zh-Hant-*': {
                        _desired: 'zh-Hant-*',
                        _distance: '5',
                    },
                },
                {
                    '*-*-*': {
                        _desired: '*-*-*',
                        _distance: '4',
                    },
                },
            ],
        },
    },
};

// This file is generated from regions-gen.ts
var regions$1 = {
    "001": [
        "001",
        "001-status-grouping",
        "002",
        "005",
        "009",
        "011",
        "013",
        "014",
        "015",
        "017",
        "018",
        "019",
        "021",
        "029",
        "030",
        "034",
        "035",
        "039",
        "053",
        "054",
        "057",
        "061",
        "142",
        "143",
        "145",
        "150",
        "151",
        "154",
        "155",
        "AC",
        "AD",
        "AE",
        "AF",
        "AG",
        "AI",
        "AL",
        "AM",
        "AO",
        "AQ",
        "AR",
        "AS",
        "AT",
        "AU",
        "AW",
        "AX",
        "AZ",
        "BA",
        "BB",
        "BD",
        "BE",
        "BF",
        "BG",
        "BH",
        "BI",
        "BJ",
        "BL",
        "BM",
        "BN",
        "BO",
        "BQ",
        "BR",
        "BS",
        "BT",
        "BV",
        "BW",
        "BY",
        "BZ",
        "CA",
        "CC",
        "CD",
        "CF",
        "CG",
        "CH",
        "CI",
        "CK",
        "CL",
        "CM",
        "CN",
        "CO",
        "CP",
        "CQ",
        "CR",
        "CU",
        "CV",
        "CW",
        "CX",
        "CY",
        "CZ",
        "DE",
        "DG",
        "DJ",
        "DK",
        "DM",
        "DO",
        "DZ",
        "EA",
        "EC",
        "EE",
        "EG",
        "EH",
        "ER",
        "ES",
        "ET",
        "EU",
        "EZ",
        "FI",
        "FJ",
        "FK",
        "FM",
        "FO",
        "FR",
        "GA",
        "GB",
        "GD",
        "GE",
        "GF",
        "GG",
        "GH",
        "GI",
        "GL",
        "GM",
        "GN",
        "GP",
        "GQ",
        "GR",
        "GS",
        "GT",
        "GU",
        "GW",
        "GY",
        "HK",
        "HM",
        "HN",
        "HR",
        "HT",
        "HU",
        "IC",
        "ID",
        "IE",
        "IL",
        "IM",
        "IN",
        "IO",
        "IQ",
        "IR",
        "IS",
        "IT",
        "JE",
        "JM",
        "JO",
        "JP",
        "KE",
        "KG",
        "KH",
        "KI",
        "KM",
        "KN",
        "KP",
        "KR",
        "KW",
        "KY",
        "KZ",
        "LA",
        "LB",
        "LC",
        "LI",
        "LK",
        "LR",
        "LS",
        "LT",
        "LU",
        "LV",
        "LY",
        "MA",
        "MC",
        "MD",
        "ME",
        "MF",
        "MG",
        "MH",
        "MK",
        "ML",
        "MM",
        "MN",
        "MO",
        "MP",
        "MQ",
        "MR",
        "MS",
        "MT",
        "MU",
        "MV",
        "MW",
        "MX",
        "MY",
        "MZ",
        "NA",
        "NC",
        "NE",
        "NF",
        "NG",
        "NI",
        "NL",
        "NO",
        "NP",
        "NR",
        "NU",
        "NZ",
        "OM",
        "PA",
        "PE",
        "PF",
        "PG",
        "PH",
        "PK",
        "PL",
        "PM",
        "PN",
        "PR",
        "PS",
        "PT",
        "PW",
        "PY",
        "QA",
        "QO",
        "RE",
        "RO",
        "RS",
        "RU",
        "RW",
        "SA",
        "SB",
        "SC",
        "SD",
        "SE",
        "SG",
        "SH",
        "SI",
        "SJ",
        "SK",
        "SL",
        "SM",
        "SN",
        "SO",
        "SR",
        "SS",
        "ST",
        "SV",
        "SX",
        "SY",
        "SZ",
        "TA",
        "TC",
        "TD",
        "TF",
        "TG",
        "TH",
        "TJ",
        "TK",
        "TL",
        "TM",
        "TN",
        "TO",
        "TR",
        "TT",
        "TV",
        "TW",
        "TZ",
        "UA",
        "UG",
        "UM",
        "UN",
        "US",
        "UY",
        "UZ",
        "VA",
        "VC",
        "VE",
        "VG",
        "VI",
        "VN",
        "VU",
        "WF",
        "WS",
        "XK",
        "YE",
        "YT",
        "ZA",
        "ZM",
        "ZW"
    ],
    "002": [
        "002",
        "002-status-grouping",
        "011",
        "014",
        "015",
        "017",
        "018",
        "202",
        "AO",
        "BF",
        "BI",
        "BJ",
        "BW",
        "CD",
        "CF",
        "CG",
        "CI",
        "CM",
        "CV",
        "DJ",
        "DZ",
        "EA",
        "EG",
        "EH",
        "ER",
        "ET",
        "GA",
        "GH",
        "GM",
        "GN",
        "GQ",
        "GW",
        "IC",
        "IO",
        "KE",
        "KM",
        "LR",
        "LS",
        "LY",
        "MA",
        "MG",
        "ML",
        "MR",
        "MU",
        "MW",
        "MZ",
        "NA",
        "NE",
        "NG",
        "RE",
        "RW",
        "SC",
        "SD",
        "SH",
        "SL",
        "SN",
        "SO",
        "SS",
        "ST",
        "SZ",
        "TD",
        "TF",
        "TG",
        "TN",
        "TZ",
        "UG",
        "YT",
        "ZA",
        "ZM",
        "ZW"
    ],
    "003": [
        "003",
        "013",
        "021",
        "029",
        "AG",
        "AI",
        "AW",
        "BB",
        "BL",
        "BM",
        "BQ",
        "BS",
        "BZ",
        "CA",
        "CR",
        "CU",
        "CW",
        "DM",
        "DO",
        "GD",
        "GL",
        "GP",
        "GT",
        "HN",
        "HT",
        "JM",
        "KN",
        "KY",
        "LC",
        "MF",
        "MQ",
        "MS",
        "MX",
        "NI",
        "PA",
        "PM",
        "PR",
        "SV",
        "SX",
        "TC",
        "TT",
        "US",
        "VC",
        "VG",
        "VI"
    ],
    "005": [
        "005",
        "AR",
        "BO",
        "BR",
        "BV",
        "CL",
        "CO",
        "EC",
        "FK",
        "GF",
        "GS",
        "GY",
        "PE",
        "PY",
        "SR",
        "UY",
        "VE"
    ],
    "009": [
        "009",
        "053",
        "054",
        "057",
        "061",
        "AC",
        "AQ",
        "AS",
        "AU",
        "CC",
        "CK",
        "CP",
        "CX",
        "DG",
        "FJ",
        "FM",
        "GU",
        "HM",
        "KI",
        "MH",
        "MP",
        "NC",
        "NF",
        "NR",
        "NU",
        "NZ",
        "PF",
        "PG",
        "PN",
        "PW",
        "QO",
        "SB",
        "TA",
        "TK",
        "TO",
        "TV",
        "UM",
        "VU",
        "WF",
        "WS"
    ],
    "011": [
        "011",
        "BF",
        "BJ",
        "CI",
        "CV",
        "GH",
        "GM",
        "GN",
        "GW",
        "LR",
        "ML",
        "MR",
        "NE",
        "NG",
        "SH",
        "SL",
        "SN",
        "TG"
    ],
    "013": [
        "013",
        "BZ",
        "CR",
        "GT",
        "HN",
        "MX",
        "NI",
        "PA",
        "SV"
    ],
    "014": [
        "014",
        "BI",
        "DJ",
        "ER",
        "ET",
        "IO",
        "KE",
        "KM",
        "MG",
        "MU",
        "MW",
        "MZ",
        "RE",
        "RW",
        "SC",
        "SO",
        "SS",
        "TF",
        "TZ",
        "UG",
        "YT",
        "ZM",
        "ZW"
    ],
    "015": [
        "015",
        "DZ",
        "EA",
        "EG",
        "EH",
        "IC",
        "LY",
        "MA",
        "SD",
        "TN"
    ],
    "017": [
        "017",
        "AO",
        "CD",
        "CF",
        "CG",
        "CM",
        "GA",
        "GQ",
        "ST",
        "TD"
    ],
    "018": [
        "018",
        "BW",
        "LS",
        "NA",
        "SZ",
        "ZA"
    ],
    "019": [
        "003",
        "005",
        "013",
        "019",
        "019-status-grouping",
        "021",
        "029",
        "419",
        "AG",
        "AI",
        "AR",
        "AW",
        "BB",
        "BL",
        "BM",
        "BO",
        "BQ",
        "BR",
        "BS",
        "BV",
        "BZ",
        "CA",
        "CL",
        "CO",
        "CR",
        "CU",
        "CW",
        "DM",
        "DO",
        "EC",
        "FK",
        "GD",
        "GF",
        "GL",
        "GP",
        "GS",
        "GT",
        "GY",
        "HN",
        "HT",
        "JM",
        "KN",
        "KY",
        "LC",
        "MF",
        "MQ",
        "MS",
        "MX",
        "NI",
        "PA",
        "PE",
        "PM",
        "PR",
        "PY",
        "SR",
        "SV",
        "SX",
        "TC",
        "TT",
        "US",
        "UY",
        "VC",
        "VE",
        "VG",
        "VI"
    ],
    "021": [
        "021",
        "BM",
        "CA",
        "GL",
        "PM",
        "US"
    ],
    "029": [
        "029",
        "AG",
        "AI",
        "AW",
        "BB",
        "BL",
        "BQ",
        "BS",
        "CU",
        "CW",
        "DM",
        "DO",
        "GD",
        "GP",
        "HT",
        "JM",
        "KN",
        "KY",
        "LC",
        "MF",
        "MQ",
        "MS",
        "PR",
        "SX",
        "TC",
        "TT",
        "VC",
        "VG",
        "VI"
    ],
    "030": [
        "030",
        "CN",
        "HK",
        "JP",
        "KP",
        "KR",
        "MN",
        "MO",
        "TW"
    ],
    "034": [
        "034",
        "AF",
        "BD",
        "BT",
        "IN",
        "IR",
        "LK",
        "MV",
        "NP",
        "PK"
    ],
    "035": [
        "035",
        "BN",
        "ID",
        "KH",
        "LA",
        "MM",
        "MY",
        "PH",
        "SG",
        "TH",
        "TL",
        "VN"
    ],
    "039": [
        "039",
        "AD",
        "AL",
        "BA",
        "ES",
        "GI",
        "GR",
        "HR",
        "IT",
        "ME",
        "MK",
        "MT",
        "PT",
        "RS",
        "SI",
        "SM",
        "VA",
        "XK"
    ],
    "053": [
        "053",
        "AU",
        "CC",
        "CX",
        "HM",
        "NF",
        "NZ"
    ],
    "054": [
        "054",
        "FJ",
        "NC",
        "PG",
        "SB",
        "VU"
    ],
    "057": [
        "057",
        "FM",
        "GU",
        "KI",
        "MH",
        "MP",
        "NR",
        "PW",
        "UM"
    ],
    "061": [
        "061",
        "AS",
        "CK",
        "NU",
        "PF",
        "PN",
        "TK",
        "TO",
        "TV",
        "WF",
        "WS"
    ],
    "142": [
        "030",
        "034",
        "035",
        "142",
        "143",
        "145",
        "AE",
        "AF",
        "AM",
        "AZ",
        "BD",
        "BH",
        "BN",
        "BT",
        "CN",
        "CY",
        "GE",
        "HK",
        "ID",
        "IL",
        "IN",
        "IQ",
        "IR",
        "JO",
        "JP",
        "KG",
        "KH",
        "KP",
        "KR",
        "KW",
        "KZ",
        "LA",
        "LB",
        "LK",
        "MM",
        "MN",
        "MO",
        "MV",
        "MY",
        "NP",
        "OM",
        "PH",
        "PK",
        "PS",
        "QA",
        "SA",
        "SG",
        "SY",
        "TH",
        "TJ",
        "TL",
        "TM",
        "TR",
        "TW",
        "UZ",
        "VN",
        "YE"
    ],
    "143": [
        "143",
        "KG",
        "KZ",
        "TJ",
        "TM",
        "UZ"
    ],
    "145": [
        "145",
        "AE",
        "AM",
        "AZ",
        "BH",
        "CY",
        "GE",
        "IL",
        "IQ",
        "JO",
        "KW",
        "LB",
        "OM",
        "PS",
        "QA",
        "SA",
        "SY",
        "TR",
        "YE"
    ],
    "150": [
        "039",
        "150",
        "151",
        "154",
        "155",
        "AD",
        "AL",
        "AT",
        "AX",
        "BA",
        "BE",
        "BG",
        "BY",
        "CH",
        "CQ",
        "CZ",
        "DE",
        "DK",
        "EE",
        "ES",
        "FI",
        "FO",
        "FR",
        "GB",
        "GG",
        "GI",
        "GR",
        "HR",
        "HU",
        "IE",
        "IM",
        "IS",
        "IT",
        "JE",
        "LI",
        "LT",
        "LU",
        "LV",
        "MC",
        "MD",
        "ME",
        "MK",
        "MT",
        "NL",
        "NO",
        "PL",
        "PT",
        "RO",
        "RS",
        "RU",
        "SE",
        "SI",
        "SJ",
        "SK",
        "SM",
        "UA",
        "VA",
        "XK"
    ],
    "151": [
        "151",
        "BG",
        "BY",
        "CZ",
        "HU",
        "MD",
        "PL",
        "RO",
        "RU",
        "SK",
        "UA"
    ],
    "154": [
        "154",
        "AX",
        "CQ",
        "DK",
        "EE",
        "FI",
        "FO",
        "GB",
        "GG",
        "IE",
        "IM",
        "IS",
        "JE",
        "LT",
        "LV",
        "NO",
        "SE",
        "SJ"
    ],
    "155": [
        "155",
        "AT",
        "BE",
        "CH",
        "DE",
        "FR",
        "LI",
        "LU",
        "MC",
        "NL"
    ],
    "202": [
        "011",
        "014",
        "017",
        "018",
        "202",
        "AO",
        "BF",
        "BI",
        "BJ",
        "BW",
        "CD",
        "CF",
        "CG",
        "CI",
        "CM",
        "CV",
        "DJ",
        "ER",
        "ET",
        "GA",
        "GH",
        "GM",
        "GN",
        "GQ",
        "GW",
        "IO",
        "KE",
        "KM",
        "LR",
        "LS",
        "MG",
        "ML",
        "MR",
        "MU",
        "MW",
        "MZ",
        "NA",
        "NE",
        "NG",
        "RE",
        "RW",
        "SC",
        "SH",
        "SL",
        "SN",
        "SO",
        "SS",
        "ST",
        "SZ",
        "TD",
        "TF",
        "TG",
        "TZ",
        "UG",
        "YT",
        "ZA",
        "ZM",
        "ZW"
    ],
    "419": [
        "005",
        "013",
        "029",
        "419",
        "AG",
        "AI",
        "AR",
        "AW",
        "BB",
        "BL",
        "BO",
        "BQ",
        "BR",
        "BS",
        "BV",
        "BZ",
        "CL",
        "CO",
        "CR",
        "CU",
        "CW",
        "DM",
        "DO",
        "EC",
        "FK",
        "GD",
        "GF",
        "GP",
        "GS",
        "GT",
        "GY",
        "HN",
        "HT",
        "JM",
        "KN",
        "KY",
        "LC",
        "MF",
        "MQ",
        "MS",
        "MX",
        "NI",
        "PA",
        "PE",
        "PR",
        "PY",
        "SR",
        "SV",
        "SX",
        "TC",
        "TT",
        "UY",
        "VC",
        "VE",
        "VG",
        "VI"
    ],
    "EU": [
        "AT",
        "BE",
        "BG",
        "CY",
        "CZ",
        "DE",
        "DK",
        "EE",
        "ES",
        "EU",
        "FI",
        "FR",
        "GR",
        "HR",
        "HU",
        "IE",
        "IT",
        "LT",
        "LU",
        "LV",
        "MT",
        "NL",
        "PL",
        "PT",
        "RO",
        "SE",
        "SI",
        "SK"
    ],
    "EZ": [
        "AT",
        "BE",
        "CY",
        "DE",
        "EE",
        "ES",
        "EZ",
        "FI",
        "FR",
        "GR",
        "IE",
        "IT",
        "LT",
        "LU",
        "LV",
        "MT",
        "NL",
        "PT",
        "SI",
        "SK"
    ],
    "QO": [
        "AC",
        "AQ",
        "CP",
        "DG",
        "QO",
        "TA"
    ],
    "UN": [
        "AD",
        "AE",
        "AF",
        "AG",
        "AL",
        "AM",
        "AO",
        "AR",
        "AT",
        "AU",
        "AZ",
        "BA",
        "BB",
        "BD",
        "BE",
        "BF",
        "BG",
        "BH",
        "BI",
        "BJ",
        "BN",
        "BO",
        "BR",
        "BS",
        "BT",
        "BW",
        "BY",
        "BZ",
        "CA",
        "CD",
        "CF",
        "CG",
        "CH",
        "CI",
        "CL",
        "CM",
        "CN",
        "CO",
        "CR",
        "CU",
        "CV",
        "CY",
        "CZ",
        "DE",
        "DJ",
        "DK",
        "DM",
        "DO",
        "DZ",
        "EC",
        "EE",
        "EG",
        "ER",
        "ES",
        "ET",
        "FI",
        "FJ",
        "FM",
        "FR",
        "GA",
        "GB",
        "GD",
        "GE",
        "GH",
        "GM",
        "GN",
        "GQ",
        "GR",
        "GT",
        "GW",
        "GY",
        "HN",
        "HR",
        "HT",
        "HU",
        "ID",
        "IE",
        "IL",
        "IN",
        "IQ",
        "IR",
        "IS",
        "IT",
        "JM",
        "JO",
        "JP",
        "KE",
        "KG",
        "KH",
        "KI",
        "KM",
        "KN",
        "KP",
        "KR",
        "KW",
        "KZ",
        "LA",
        "LB",
        "LC",
        "LI",
        "LK",
        "LR",
        "LS",
        "LT",
        "LU",
        "LV",
        "LY",
        "MA",
        "MC",
        "MD",
        "ME",
        "MG",
        "MH",
        "MK",
        "ML",
        "MM",
        "MN",
        "MR",
        "MT",
        "MU",
        "MV",
        "MW",
        "MX",
        "MY",
        "MZ",
        "NA",
        "NE",
        "NG",
        "NI",
        "NL",
        "NO",
        "NP",
        "NR",
        "NZ",
        "OM",
        "PA",
        "PE",
        "PG",
        "PH",
        "PK",
        "PL",
        "PT",
        "PW",
        "PY",
        "QA",
        "RO",
        "RS",
        "RU",
        "RW",
        "SA",
        "SB",
        "SC",
        "SD",
        "SE",
        "SG",
        "SI",
        "SK",
        "SL",
        "SM",
        "SN",
        "SO",
        "SR",
        "SS",
        "ST",
        "SV",
        "SY",
        "SZ",
        "TD",
        "TG",
        "TH",
        "TJ",
        "TL",
        "TM",
        "TN",
        "TO",
        "TR",
        "TT",
        "TV",
        "TZ",
        "UA",
        "UG",
        "UN",
        "US",
        "UY",
        "UZ",
        "VC",
        "VE",
        "VN",
        "VU",
        "WS",
        "YE",
        "ZA",
        "ZM",
        "ZW"
    ]
};

var UNICODE_EXTENSION_SEQUENCE_REGEX$1 = /-u(?:-[0-9a-z]{2,8})+/gi;
function invariant$1(condition, message, Err) {
    if (Err === void 0) { Err = Error; }
    if (!condition) {
        throw new Err(message);
    }
}
// This is effectively 2 languages in 2 different regions in the same cluster
var DEFAULT_MATCHING_THRESHOLD$1 = 838;
var PROCESSED_DATA$1;
function processData$1() {
    var _a, _b;
    if (!PROCESSED_DATA$1) {
        var paradigmLocales = (_b = (_a = data$1.supplemental.languageMatching['written-new'][0]) === null || _a === void 0 ? void 0 : _a.paradigmLocales) === null || _b === void 0 ? void 0 : _b._locales.split(' ');
        var matchVariables = data$1.supplemental.languageMatching['written-new'].slice(1, 5);
        var data = data$1.supplemental.languageMatching['written-new'].slice(5);
        var matches = data.map(function (d) {
            var key = Object.keys(d)[0];
            var value = d[key];
            return {
                supported: key,
                desired: value._desired,
                distance: +value._distance,
                oneway: value.oneway === 'true' ? true : false,
            };
        }, {});
        PROCESSED_DATA$1 = {
            matches: matches,
            matchVariables: matchVariables.reduce(function (all, d) {
                var key = Object.keys(d)[0];
                var value = d[key];
                all[key.slice(1)] = value._value.split('+');
                return all;
            }, {}),
            paradigmLocales: __spreadArray$1(__spreadArray$1([], paradigmLocales, true), paradigmLocales.map(function (l) {
                return new Intl.Locale(l.replace(/_/g, '-')).maximize().toString();
            }), true),
        };
    }
    return PROCESSED_DATA$1;
}
function isMatched$1(locale, languageMatchInfoLocale, matchVariables) {
    var _a = languageMatchInfoLocale.split('-'), language = _a[0], script = _a[1], region = _a[2];
    var matches = true;
    if (region && region[0] === '$') {
        var shouldInclude = region[1] !== '!';
        var matchRegions = shouldInclude
            ? matchVariables[region.slice(1)]
            : matchVariables[region.slice(2)];
        var expandedMatchedRegions = matchRegions
            .map(function (r) { return regions$1[r] || [r]; })
            .reduce(function (all, list) { return __spreadArray$1(__spreadArray$1([], all, true), list, true); }, []);
        matches && (matches = !(expandedMatchedRegions.indexOf(locale.region || '') > -1 !=
            shouldInclude));
    }
    else {
        matches && (matches = locale.region
            ? region === '*' || region === locale.region
            : true);
    }
    matches && (matches = locale.script ? script === '*' || script === locale.script : true);
    matches && (matches = locale.language
        ? language === '*' || language === locale.language
        : true);
    return matches;
}
function serializeLSR$1(lsr) {
    return [lsr.language, lsr.script, lsr.region].filter(Boolean).join('-');
}
function findMatchingDistanceForLSR$1(desired, supported, data) {
    for (var _i = 0, _a = data.matches; _i < _a.length; _i++) {
        var d = _a[_i];
        var matches = isMatched$1(desired, d.desired, data.matchVariables) &&
            isMatched$1(supported, d.supported, data.matchVariables);
        if (!d.oneway && !matches) {
            matches =
                isMatched$1(desired, d.supported, data.matchVariables) &&
                    isMatched$1(supported, d.desired, data.matchVariables);
        }
        if (matches) {
            var distance = d.distance * 10;
            if (data.paradigmLocales.indexOf(serializeLSR$1(desired)) > -1 !=
                data.paradigmLocales.indexOf(serializeLSR$1(supported)) > -1) {
                return distance - 1;
            }
            return distance;
        }
    }
    throw new Error('No matching distance found');
}
function findMatchingDistance$1(desired, supported) {
    var desiredLocale = new Intl.Locale(desired).maximize();
    var supportedLocale = new Intl.Locale(supported).maximize();
    var desiredLSR = {
        language: desiredLocale.language,
        script: desiredLocale.script || '',
        region: desiredLocale.region || '',
    };
    var supportedLSR = {
        language: supportedLocale.language,
        script: supportedLocale.script || '',
        region: supportedLocale.region || '',
    };
    var matchingDistance = 0;
    var data = processData$1();
    if (desiredLSR.language !== supportedLSR.language) {
        matchingDistance += findMatchingDistanceForLSR$1({
            language: desiredLocale.language,
            script: '',
            region: '',
        }, {
            language: supportedLocale.language,
            script: '',
            region: '',
        }, data);
    }
    if (desiredLSR.script !== supportedLSR.script) {
        matchingDistance += findMatchingDistanceForLSR$1({
            language: desiredLocale.language,
            script: desiredLSR.script,
            region: '',
        }, {
            language: supportedLocale.language,
            script: supportedLSR.script,
            region: '',
        }, data);
    }
    if (desiredLSR.region !== supportedLSR.region) {
        matchingDistance += findMatchingDistanceForLSR$1(desiredLSR, supportedLSR, data);
    }
    return matchingDistance;
}
function findBestMatch$1(requestedLocales, supportedLocales, threshold) {
    if (threshold === void 0) { threshold = DEFAULT_MATCHING_THRESHOLD$1; }
    var lowestDistance = Infinity;
    var result = {
        matchedDesiredLocale: '',
        distances: {},
    };
    requestedLocales.forEach(function (desired, i) {
        if (!result.distances[desired]) {
            result.distances[desired] = {};
        }
        supportedLocales.forEach(function (supported) {
            // Add some weight to the distance based on the order of the supported locales
            // Add penalty for the order of the requested locales, which currently is 0 since ECMA-402
            // doesn't really have room for weighted locales like `en; q=0.1`
            var distance = findMatchingDistance$1(desired, supported) + 0 + i * 40;
            result.distances[desired][supported] = distance;
            if (distance < lowestDistance) {
                lowestDistance = distance;
                result.matchedDesiredLocale = desired;
                result.matchedSupportedLocale = supported;
            }
        });
    });
    if (lowestDistance >= threshold) {
        result.matchedDesiredLocale = undefined;
        result.matchedSupportedLocale = undefined;
    }
    return result;
}

/**
 * https://tc39.es/ecma402/#sec-bestfitmatcher
 * @param availableLocales
 * @param requestedLocales
 * @param getDefaultLocale
 */
function BestFitMatcher$1(availableLocales, requestedLocales, getDefaultLocale) {
    var foundLocale;
    var extension;
    var noExtensionLocales = [];
    var noExtensionLocaleMap = requestedLocales.reduce(function (all, l) {
        var noExtensionLocale = l.replace(UNICODE_EXTENSION_SEQUENCE_REGEX$1, '');
        noExtensionLocales.push(noExtensionLocale);
        all[noExtensionLocale] = l;
        return all;
    }, {});
    var result = findBestMatch$1(noExtensionLocales, availableLocales);
    if (result.matchedSupportedLocale && result.matchedDesiredLocale) {
        foundLocale = result.matchedSupportedLocale;
        extension =
            noExtensionLocaleMap[result.matchedDesiredLocale].slice(result.matchedDesiredLocale.length) || undefined;
    }
    if (!foundLocale) {
        return { locale: getDefaultLocale() };
    }
    return {
        locale: foundLocale,
        extension: extension,
    };
}

function CanonicalizeUValue$1(ukey, uvalue) {
    // TODO: Implement algorithm for CanonicalizeUValue per https://tc39.es/ecma402/#sec-canonicalizeuvalue
    var lowerValue = uvalue.toLowerCase();
    invariant$1(ukey !== undefined, "ukey must be defined");
    var canonicalized = lowerValue;
    return canonicalized;
}

function CanonicalizeUnicodeLocaleId$1(locale) {
    return Intl.getCanonicalLocales(locale)[0];
}

function InsertUnicodeExtensionAndCanonicalize$1(locale, attributes, keywords) {
    invariant$1(locale.indexOf('-u-') === -1, 'Expected locale to not have a Unicode locale extension');
    var extension = '-u';
    for (var _i = 0, attributes_1 = attributes; _i < attributes_1.length; _i++) {
        var attr = attributes_1[_i];
        extension += "-".concat(attr);
    }
    for (var _a = 0, keywords_1 = keywords; _a < keywords_1.length; _a++) {
        var kw = keywords_1[_a];
        var key = kw.key, value = kw.value;
        extension += "-".concat(key);
        if (value !== '') {
            extension += "-".concat(value);
        }
    }
    if (extension === '-u') {
        return CanonicalizeUnicodeLocaleId$1(locale);
    }
    var privateIndex = locale.indexOf('-x-');
    var newLocale;
    if (privateIndex === -1) {
        newLocale = locale + extension;
    }
    else {
        var preExtension = locale.slice(0, privateIndex);
        var postExtension = locale.slice(privateIndex);
        newLocale = preExtension + extension + postExtension;
    }
    return CanonicalizeUnicodeLocaleId$1(newLocale);
}

/**
 * https://tc39.es/ecma402/#sec-bestavailablelocale
 * @param availableLocales
 * @param locale
 */
function BestAvailableLocale$1(availableLocales, locale) {
    var candidate = locale;
    while (true) {
        if (availableLocales.indexOf(candidate) > -1) {
            return candidate;
        }
        var pos = candidate.lastIndexOf('-');
        if (!~pos) {
            return undefined;
        }
        if (pos >= 2 && candidate[pos - 2] === '-') {
            pos -= 2;
        }
        candidate = candidate.slice(0, pos);
    }
}

/**
 * https://tc39.es/ecma402/#sec-lookupmatcher
 * @param availableLocales
 * @param requestedLocales
 * @param getDefaultLocale
 */
function LookupMatcher$1(availableLocales, requestedLocales, getDefaultLocale) {
    var result = { locale: '' };
    for (var _i = 0, requestedLocales_1 = requestedLocales; _i < requestedLocales_1.length; _i++) {
        var locale = requestedLocales_1[_i];
        var noExtensionLocale = locale.replace(UNICODE_EXTENSION_SEQUENCE_REGEX$1, '');
        var availableLocale = BestAvailableLocale$1(availableLocales, noExtensionLocale);
        if (availableLocale) {
            result.locale = availableLocale;
            if (locale !== noExtensionLocale) {
                result.extension = locale.slice(noExtensionLocale.length, locale.length);
            }
            return result;
        }
    }
    result.locale = getDefaultLocale();
    return result;
}

function UnicodeExtensionComponents$1(extension) {
    invariant$1(extension === extension.toLowerCase(), 'Expected extension to be lowercase');
    invariant$1(extension.slice(0, 3) === '-u-', 'Expected extension to be a Unicode locale extension');
    var attributes = [];
    var keywords = [];
    var keyword;
    var size = extension.length;
    var k = 3;
    while (k < size) {
        var e = extension.indexOf('-', k);
        var len = void 0;
        if (e === -1) {
            len = size - k;
        }
        else {
            len = e - k;
        }
        var subtag = extension.slice(k, k + len);
        invariant$1(len >= 2, 'Expected a subtag to have at least 2 characters');
        if (keyword === undefined && len != 2) {
            if (attributes.indexOf(subtag) === -1) {
                attributes.push(subtag);
            }
        }
        else if (len === 2) {
            keyword = { key: subtag, value: '' };
            if (keywords.find(function (k) { return k.key === (keyword === null || keyword === void 0 ? void 0 : keyword.key); }) === undefined) {
                keywords.push(keyword);
            }
        }
        else if ((keyword === null || keyword === void 0 ? void 0 : keyword.value) === '') {
            keyword.value = subtag;
        }
        else {
            invariant$1(keyword !== undefined, 'Expected keyword to be defined');
            keyword.value += '-' + subtag;
        }
        k += len + 1;
    }
    return { attributes: attributes, keywords: keywords };
}

/**
 * https://tc39.es/ecma402/#sec-resolvelocale
 */
function ResolveLocale$1(availableLocales, requestedLocales, options, relevantExtensionKeys, localeData, getDefaultLocale) {
    var _a;
    var matcher = options.localeMatcher;
    var r;
    if (matcher === 'lookup') {
        r = LookupMatcher$1(Array.from(availableLocales), requestedLocales, getDefaultLocale);
    }
    else {
        r = BestFitMatcher$1(Array.from(availableLocales), requestedLocales, getDefaultLocale);
    }
    if (r == null) {
        r = {
            locale: getDefaultLocale(),
            extension: '',
        };
    }
    var foundLocale = r.locale;
    var foundLocaleData = localeData[foundLocale];
    // TODO: We can't really guarantee that the locale data is available
    // invariant(
    //   foundLocaleData !== undefined,
    //   `Missing locale data for ${foundLocale}`
    // )
    var result = { locale: 'en', dataLocale: foundLocale };
    var components;
    var keywords;
    if (r.extension) {
        components = UnicodeExtensionComponents$1(r.extension);
        keywords = components.keywords;
    }
    else {
        keywords = [];
    }
    var supportedKeywords = [];
    var _loop_1 = function (key) {
        // TODO: Shouldn't default to empty array, see TODO above
        var keyLocaleData = (_a = foundLocaleData === null || foundLocaleData === void 0 ? void 0 : foundLocaleData[key]) !== null && _a !== void 0 ? _a : [];
        invariant$1(Array.isArray(keyLocaleData), "keyLocaleData for ".concat(key, " must be an array"));
        var value = keyLocaleData[0];
        invariant$1(value === undefined || typeof value === 'string', "value must be a string or undefined");
        var supportedKeyword = void 0;
        var entry = keywords.find(function (k) { return k.key === key; });
        if (entry) {
            var requestedValue = entry.value;
            if (requestedValue !== '') {
                if (keyLocaleData.indexOf(requestedValue) > -1) {
                    value = requestedValue;
                    supportedKeyword = {
                        key: key,
                        value: value,
                    };
                }
            }
            else if (keyLocaleData.indexOf('true') > -1) {
                value = 'true';
                supportedKeyword = {
                    key: key,
                    value: value,
                };
            }
        }
        var optionsValue = options[key];
        invariant$1(optionsValue == null || typeof optionsValue === 'string', "optionsValue must be a string or undefined");
        if (typeof optionsValue === 'string') {
            var ukey = key.toLowerCase();
            optionsValue = CanonicalizeUValue$1(ukey, optionsValue);
            if (optionsValue === '') {
                optionsValue = 'true';
            }
        }
        if (optionsValue !== value && keyLocaleData.indexOf(optionsValue) > -1) {
            value = optionsValue;
            supportedKeyword = undefined;
        }
        if (supportedKeyword) {
            supportedKeywords.push(supportedKeyword);
        }
        result[key] = value;
    };
    for (var _i = 0, relevantExtensionKeys_1 = relevantExtensionKeys; _i < relevantExtensionKeys_1.length; _i++) {
        var key = relevantExtensionKeys_1[_i];
        _loop_1(key);
    }
    var supportedAttributes = [];
    if (supportedKeywords.length > 0) {
        supportedAttributes = [];
        foundLocale = InsertUnicodeExtensionAndCanonicalize$1(foundLocale, supportedAttributes, supportedKeywords);
    }
    result.locale = foundLocale;
    return result;
}

/**
 * https://tc39.es/ecma402/#sec-lookupsupportedlocales
 * @param availableLocales
 * @param requestedLocales
 */
function LookupSupportedLocales$1(availableLocales, requestedLocales) {
    var subset = [];
    for (var _i = 0, requestedLocales_1 = requestedLocales; _i < requestedLocales_1.length; _i++) {
        var locale = requestedLocales_1[_i];
        var noExtensionLocale = locale.replace(UNICODE_EXTENSION_SEQUENCE_REGEX$1, '');
        var availableLocale = BestAvailableLocale$1(availableLocales, noExtensionLocale);
        if (availableLocale) {
            subset.push(availableLocale);
        }
    }
    return subset;
}

function match$1(requestedLocales, availableLocales, defaultLocale, opts) {
    return ResolveLocale$1(availableLocales, CanonicalizeLocaleList$1(requestedLocales), {
        localeMatcher: (opts === null || opts === void 0 ? void 0 : opts.algorithm) || 'best fit',
    }, [], {}, function () { return defaultLocale; }).locale;
}

var lib$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    [Symbol.toStringTag]: 'Module',
    match: match$1,
    LookupSupportedLocales: LookupSupportedLocales$1,
    ResolveLocale: ResolveLocale$1
});

var require$$1$1 = /*@__PURE__*/getAugmentedNamespace(lib$1);

var supportedLocales_generated$1 = {};

Object.defineProperty(supportedLocales_generated$1, "__esModule", { value: true });
supportedLocales_generated$1.supportedLocales = void 0;
supportedLocales_generated$1.supportedLocales = ["af", "af-NA", "agq", "ak", "am", "ar", "ar-AE", "ar-BH", "ar-DJ", "ar-DZ", "ar-EG", "ar-EH", "ar-ER", "ar-IL", "ar-IQ", "ar-JO", "ar-KM", "ar-KW", "ar-LB", "ar-LY", "ar-MA", "ar-MR", "ar-OM", "ar-PS", "ar-QA", "ar-SA", "ar-SD", "ar-SO", "ar-SS", "ar-SY", "ar-TD", "ar-TN", "ar-YE", "as", "asa", "ast", "az", "az-Cyrl", "az-Latn", "bas", "be", "be-tarask", "bem", "bez", "bg", "bm", "bn", "bn-IN", "bo", "bo-IN", "br", "brx", "bs", "bs-Cyrl", "bs-Latn", "ca", "ca-AD", "ca-ES-valencia", "ca-FR", "ca-IT", "ccp", "ccp-IN", "ce", "ceb", "cgg", "chr", "ckb", "ckb-IR", "cs", "cy", "da", "da-GL", "dav", "de", "de-AT", "de-BE", "de-CH", "de-IT", "de-LI", "de-LU", "dje", "doi", "dsb", "dua", "dyo", "dz", "ebu", "ee", "ee-TG", "el", "el-CY", "en", "en-001", "en-150", "en-AE", "en-AG", "en-AI", "en-AS", "en-AT", "en-AU", "en-BB", "en-BE", "en-BI", "en-BM", "en-BS", "en-BW", "en-BZ", "en-CA", "en-CC", "en-CH", "en-CK", "en-CM", "en-CX", "en-CY", "en-DE", "en-DG", "en-DK", "en-DM", "en-ER", "en-FI", "en-FJ", "en-FK", "en-FM", "en-GB", "en-GD", "en-GG", "en-GH", "en-GI", "en-GM", "en-GU", "en-GY", "en-HK", "en-IE", "en-IL", "en-IM", "en-IN", "en-IO", "en-JE", "en-JM", "en-KE", "en-KI", "en-KN", "en-KY", "en-LC", "en-LR", "en-LS", "en-MG", "en-MH", "en-MO", "en-MP", "en-MS", "en-MT", "en-MU", "en-MW", "en-MY", "en-NA", "en-NF", "en-NG", "en-NL", "en-NR", "en-NU", "en-NZ", "en-PG", "en-PH", "en-PK", "en-PN", "en-PR", "en-PW", "en-RW", "en-SB", "en-SC", "en-SD", "en-SE", "en-SG", "en-SH", "en-SI", "en-SL", "en-SS", "en-SX", "en-SZ", "en-TC", "en-TK", "en-TO", "en-TT", "en-TV", "en-TZ", "en-UG", "en-UM", "en-VC", "en-VG", "en-VI", "en-VU", "en-WS", "en-ZA", "en-ZM", "en-ZW", "eo", "es", "es-419", "es-AR", "es-BO", "es-BR", "es-BZ", "es-CL", "es-CO", "es-CR", "es-CU", "es-DO", "es-EA", "es-EC", "es-GQ", "es-GT", "es-HN", "es-IC", "es-MX", "es-NI", "es-PA", "es-PE", "es-PH", "es-PR", "es-PY", "es-SV", "es-US", "es-UY", "es-VE", "et", "eu", "ewo", "fa", "fa-AF", "ff", "ff-Adlm", "ff-Adlm-BF", "ff-Adlm-CM", "ff-Adlm-GH", "ff-Adlm-GM", "ff-Adlm-GW", "ff-Adlm-LR", "ff-Adlm-MR", "ff-Adlm-NE", "ff-Adlm-NG", "ff-Adlm-SL", "ff-Adlm-SN", "ff-Latn", "ff-Latn-BF", "ff-Latn-CM", "ff-Latn-GH", "ff-Latn-GM", "ff-Latn-GN", "ff-Latn-GW", "ff-Latn-LR", "ff-Latn-MR", "ff-Latn-NE", "ff-Latn-NG", "ff-Latn-SL", "fi", "fil", "fo", "fo-DK", "fr", "fr-BE", "fr-BF", "fr-BI", "fr-BJ", "fr-BL", "fr-CA", "fr-CD", "fr-CF", "fr-CG", "fr-CH", "fr-CI", "fr-CM", "fr-DJ", "fr-DZ", "fr-GA", "fr-GF", "fr-GN", "fr-GP", "fr-GQ", "fr-HT", "fr-KM", "fr-LU", "fr-MA", "fr-MC", "fr-MF", "fr-MG", "fr-ML", "fr-MQ", "fr-MR", "fr-MU", "fr-NC", "fr-NE", "fr-PF", "fr-PM", "fr-RE", "fr-RW", "fr-SC", "fr-SN", "fr-SY", "fr-TD", "fr-TG", "fr-TN", "fr-VU", "fr-WF", "fr-YT", "fur", "fy", "ga", "ga-GB", "gd", "gl", "gsw", "gsw-FR", "gsw-LI", "gu", "guz", "gv", "ha", "ha-GH", "ha-NE", "haw", "he", "hi", "hr", "hr-BA", "hsb", "hu", "hy", "ia", "id", "ig", "ii", "is", "it", "it-CH", "it-SM", "it-VA", "ja", "jgo", "jmc", "jv", "ka", "kab", "kam", "kde", "kea", "kgp", "khq", "ki", "kk", "kkj", "kl", "kln", "km", "kn", "ko", "ko-KP", "kok", "ks", "ks-Arab", "ksb", "ksf", "ksh", "ku", "kw", "ky", "lag", "lb", "lg", "lkt", "ln", "ln-AO", "ln-CF", "ln-CG", "lo", "lrc", "lrc-IQ", "lt", "lu", "luo", "luy", "lv", "mai", "mas", "mas-TZ", "mer", "mfe", "mg", "mgh", "mgo", "mi", "mk", "ml", "mn", "mni", "mni-Beng", "mr", "ms", "ms-BN", "ms-ID", "ms-SG", "mt", "mua", "my", "mzn", "naq", "nb", "nb-SJ", "nd", "nds", "nds-NL", "ne", "ne-IN", "nl", "nl-AW", "nl-BE", "nl-BQ", "nl-CW", "nl-SR", "nl-SX", "nmg", "nn", "nnh", "no", "nus", "nyn", "om", "om-KE", "or", "os", "os-RU", "pa", "pa-Arab", "pa-Guru", "pcm", "pl", "ps", "ps-PK", "pt", "pt-AO", "pt-CH", "pt-CV", "pt-GQ", "pt-GW", "pt-LU", "pt-MO", "pt-MZ", "pt-PT", "pt-ST", "pt-TL", "qu", "qu-BO", "qu-EC", "rm", "rn", "ro", "ro-MD", "rof", "ru", "ru-BY", "ru-KG", "ru-KZ", "ru-MD", "ru-UA", "rw", "rwk", "sa", "sah", "saq", "sat", "sat-Olck", "sbp", "sc", "sd", "sd-Arab", "sd-Deva", "se", "se-FI", "se-SE", "seh", "ses", "sg", "shi", "shi-Latn", "shi-Tfng", "si", "sk", "sl", "smn", "sn", "so", "so-DJ", "so-ET", "so-KE", "sq", "sq-MK", "sq-XK", "sr", "sr-Cyrl", "sr-Cyrl-BA", "sr-Cyrl-ME", "sr-Cyrl-XK", "sr-Latn", "sr-Latn-BA", "sr-Latn-ME", "sr-Latn-XK", "su", "su-Latn", "sv", "sv-AX", "sv-FI", "sw", "sw-CD", "sw-KE", "sw-UG", "ta", "ta-LK", "ta-MY", "ta-SG", "te", "teo", "teo-KE", "tg", "th", "ti", "ti-ER", "tk", "to", "tr", "tr-CY", "tt", "twq", "tzm", "ug", "uk", "und", "ur", "ur-IN", "uz", "uz-Arab", "uz-Cyrl", "uz-Latn", "vai", "vai-Latn", "vai-Vaii", "vi", "vun", "wae", "wo", "xh", "xog", "yav", "yi", "yo", "yo-BJ", "yrl", "yrl-CO", "yrl-VE", "yue", "yue-Hans", "yue-Hant", "zgh", "zh", "zh-Hans", "zh-Hans-HK", "zh-Hans-MO", "zh-Hans-SG", "zh-Hant", "zh-Hant-HK", "zh-Hant-MO", "zu"];

Object.defineProperty(shouldPolyfill$3, "__esModule", { value: true });
var shouldPolyfill_2$1 = shouldPolyfill$3.shouldPolyfill = shouldPolyfill$2;
var intl_localematcher_1$1 = require$$1$1;
var supported_locales_generated_1$1 = supportedLocales_generated$1;
function supportedLocalesOf$1(locale) {
    if (!locale) {
        return true;
    }
    var locales = Array.isArray(locale) ? locale : [locale];
    return (Intl.RelativeTimeFormat.supportedLocalesOf(locales).length ===
        locales.length);
}
function hasResolvedOptionsNumberingSystem(locale) {
    try {
        return ('numberingSystem' in
            new Intl.RelativeTimeFormat(locale || 'en', {
                numeric: 'auto',
            }).resolvedOptions());
    }
    catch (_) {
        return false;
    }
}
function shouldPolyfill$2(locale) {
    if (locale === void 0) { locale = 'en'; }
    if (!('RelativeTimeFormat' in Intl) ||
        !supportedLocalesOf$1(locale) ||
        !hasResolvedOptionsNumberingSystem(locale)) {
        return (0, intl_localematcher_1$1.match)([locale], supported_locales_generated_1$1.supportedLocales, 'en');
    }
}

var shouldPolyfill$1 = {};

/**
 * http://ecma-international.org/ecma-402/7.0/index.html#sec-canonicalizelocalelist
 * @param locales
 */
function CanonicalizeLocaleList(locales) {
    return Intl.getCanonicalLocales(locales);
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    if (typeof b !== "function" && b !== null)
        throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var data = {
    supplemental: {
        languageMatching: {
            'written-new': [
                {
                    paradigmLocales: {
                        _locales: 'en en_GB es es_419 pt_BR pt_PT',
                    },
                },
                {
                    $enUS: {
                        _value: 'AS+CA+GU+MH+MP+PH+PR+UM+US+VI',
                    },
                },
                {
                    $cnsar: {
                        _value: 'HK+MO',
                    },
                },
                {
                    $americas: {
                        _value: '019',
                    },
                },
                {
                    $maghreb: {
                        _value: 'MA+DZ+TN+LY+MR+EH',
                    },
                },
                {
                    no: {
                        _desired: 'nb',
                        _distance: '1',
                    },
                },
                {
                    bs: {
                        _desired: 'hr',
                        _distance: '4',
                    },
                },
                {
                    bs: {
                        _desired: 'sh',
                        _distance: '4',
                    },
                },
                {
                    hr: {
                        _desired: 'sh',
                        _distance: '4',
                    },
                },
                {
                    sr: {
                        _desired: 'sh',
                        _distance: '4',
                    },
                },
                {
                    aa: {
                        _desired: 'ssy',
                        _distance: '4',
                    },
                },
                {
                    de: {
                        _desired: 'gsw',
                        _distance: '4',
                        _oneway: 'true',
                    },
                },
                {
                    de: {
                        _desired: 'lb',
                        _distance: '4',
                        _oneway: 'true',
                    },
                },
                {
                    no: {
                        _desired: 'da',
                        _distance: '8',
                    },
                },
                {
                    nb: {
                        _desired: 'da',
                        _distance: '8',
                    },
                },
                {
                    ru: {
                        _desired: 'ab',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ach',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    nl: {
                        _desired: 'af',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ak',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'am',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    es: {
                        _desired: 'ay',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    ru: {
                        _desired: 'az',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    ur: {
                        _desired: 'bal',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    ru: {
                        _desired: 'be',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'bem',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    hi: {
                        _desired: 'bh',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'bn',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'bo',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    fr: {
                        _desired: 'br',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    es: {
                        _desired: 'ca',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    fil: {
                        _desired: 'ceb',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'chr',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'ckb',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    fr: {
                        _desired: 'co',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    fr: {
                        _desired: 'crs',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    sk: {
                        _desired: 'cs',
                        _distance: '20',
                    },
                },
                {
                    en: {
                        _desired: 'cy',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ee',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'eo',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    es: {
                        _desired: 'eu',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    da: {
                        _desired: 'fo',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    nl: {
                        _desired: 'fy',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ga',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'gaa',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'gd',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    es: {
                        _desired: 'gl',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    es: {
                        _desired: 'gn',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    hi: {
                        _desired: 'gu',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ha',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'haw',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    fr: {
                        _desired: 'ht',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    ru: {
                        _desired: 'hy',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ia',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ig',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'is',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    id: {
                        _desired: 'jv',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ka',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    fr: {
                        _desired: 'kg',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    ru: {
                        _desired: 'kk',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'km',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'kn',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'kri',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    tr: {
                        _desired: 'ku',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    ru: {
                        _desired: 'ky',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    it: {
                        _desired: 'la',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'lg',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    fr: {
                        _desired: 'ln',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'lo',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'loz',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    fr: {
                        _desired: 'lua',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    hi: {
                        _desired: 'mai',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'mfe',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    fr: {
                        _desired: 'mg',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'mi',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ml',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    ru: {
                        _desired: 'mn',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    hi: {
                        _desired: 'mr',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    id: {
                        _desired: 'ms',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'mt',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'my',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ne',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    nb: {
                        _desired: 'nn',
                        _distance: '20',
                    },
                },
                {
                    no: {
                        _desired: 'nn',
                        _distance: '20',
                    },
                },
                {
                    en: {
                        _desired: 'nso',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ny',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'nyn',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    fr: {
                        _desired: 'oc',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'om',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'or',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'pa',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'pcm',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ps',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    es: {
                        _desired: 'qu',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    de: {
                        _desired: 'rm',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'rn',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    fr: {
                        _desired: 'rw',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    hi: {
                        _desired: 'sa',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'sd',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'si',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'sn',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'so',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'sq',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'st',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    id: {
                        _desired: 'su',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'sw',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ta',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'te',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    ru: {
                        _desired: 'tg',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ti',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    ru: {
                        _desired: 'tk',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'tlh',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'tn',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'to',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    ru: {
                        _desired: 'tt',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'tum',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'ug',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    ru: {
                        _desired: 'uk',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'ur',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    ru: {
                        _desired: 'uz',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    fr: {
                        _desired: 'wo',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'xh',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'yi',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'yo',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'za',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    en: {
                        _desired: 'zu',
                        _distance: '30',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'aao',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'abh',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'abv',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'acm',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'acq',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'acw',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'acx',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'acy',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'adf',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'aeb',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'aec',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'afb',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'ajp',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'apc',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'apd',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'arq',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'ars',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'ary',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'arz',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'auz',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'avl',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'ayh',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'ayl',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'ayn',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'ayp',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'bbz',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'pga',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'shu',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ar: {
                        _desired: 'ssh',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    az: {
                        _desired: 'azb',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    et: {
                        _desired: 'vro',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ff: {
                        _desired: 'ffm',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ff: {
                        _desired: 'fub',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ff: {
                        _desired: 'fue',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ff: {
                        _desired: 'fuf',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ff: {
                        _desired: 'fuh',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ff: {
                        _desired: 'fui',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ff: {
                        _desired: 'fuq',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ff: {
                        _desired: 'fuv',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    gn: {
                        _desired: 'gnw',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    gn: {
                        _desired: 'gui',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    gn: {
                        _desired: 'gun',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    gn: {
                        _desired: 'nhd',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    iu: {
                        _desired: 'ikt',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    kln: {
                        _desired: 'enb',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    kln: {
                        _desired: 'eyo',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    kln: {
                        _desired: 'niq',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    kln: {
                        _desired: 'oki',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    kln: {
                        _desired: 'pko',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    kln: {
                        _desired: 'sgc',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    kln: {
                        _desired: 'tec',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    kln: {
                        _desired: 'tuy',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    kok: {
                        _desired: 'gom',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    kpe: {
                        _desired: 'gkp',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'ida',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'lkb',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'lko',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'lks',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'lri',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'lrm',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'lsm',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'lto',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'lts',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'lwg',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'nle',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'nyd',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    luy: {
                        _desired: 'rag',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    lv: {
                        _desired: 'ltg',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    mg: {
                        _desired: 'bhr',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    mg: {
                        _desired: 'bjq',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    mg: {
                        _desired: 'bmm',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    mg: {
                        _desired: 'bzc',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    mg: {
                        _desired: 'msh',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    mg: {
                        _desired: 'skg',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    mg: {
                        _desired: 'tdx',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    mg: {
                        _desired: 'tkg',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    mg: {
                        _desired: 'txy',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    mg: {
                        _desired: 'xmv',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    mg: {
                        _desired: 'xmw',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    mn: {
                        _desired: 'mvf',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'bjn',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'btj',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'bve',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'bvu',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'coa',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'dup',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'hji',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'id',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'jak',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'jax',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'kvb',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'kvr',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'kxd',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'lce',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'lcf',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'liw',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'max',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'meo',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'mfa',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'mfb',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'min',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'mqg',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'msi',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'mui',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'orn',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'ors',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'pel',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'pse',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'tmw',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'urk',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'vkk',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'vkt',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'xmm',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'zlm',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ms: {
                        _desired: 'zmi',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ne: {
                        _desired: 'dty',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    om: {
                        _desired: 'gax',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    om: {
                        _desired: 'hae',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    om: {
                        _desired: 'orc',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    or: {
                        _desired: 'spv',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ps: {
                        _desired: 'pbt',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    ps: {
                        _desired: 'pst',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qub',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qud',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'quf',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qug',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'quh',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'quk',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qul',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qup',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qur',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qus',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'quw',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qux',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'quy',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qva',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qvc',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qve',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qvh',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qvi',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qvj',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qvl',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qvm',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qvn',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qvo',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qvp',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qvs',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qvw',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qvz',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qwa',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qwc',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qwh',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qws',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qxa',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qxc',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qxh',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qxl',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qxn',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qxo',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qxp',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qxr',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qxt',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qxu',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    qu: {
                        _desired: 'qxw',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    sc: {
                        _desired: 'sdc',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    sc: {
                        _desired: 'sdn',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    sc: {
                        _desired: 'sro',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    sq: {
                        _desired: 'aae',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    sq: {
                        _desired: 'aat',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    sq: {
                        _desired: 'aln',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    syr: {
                        _desired: 'aii',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    uz: {
                        _desired: 'uzs',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    yi: {
                        _desired: 'yih',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'cdo',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'cjy',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'cpx',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'czh',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'czo',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'gan',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'hak',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'hsn',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'lzh',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'mnp',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'nan',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'wuu',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    zh: {
                        _desired: 'yue',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    '*': {
                        _desired: '*',
                        _distance: '80',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'am-Ethi',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'ru-Cyrl': {
                        _desired: 'az-Latn',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'bn-Beng',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'zh-Hans': {
                        _desired: 'bo-Tibt',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'ru-Cyrl': {
                        _desired: 'hy-Armn',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'ka-Geor',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'km-Khmr',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'kn-Knda',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'lo-Laoo',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'ml-Mlym',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'my-Mymr',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'ne-Deva',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'or-Orya',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'pa-Guru',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'ps-Arab',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'sd-Arab',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'si-Sinh',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'ta-Taml',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'te-Telu',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'ti-Ethi',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'ru-Cyrl': {
                        _desired: 'tk-Latn',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'ur-Arab',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'ru-Cyrl': {
                        _desired: 'uz-Latn',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'en-Latn': {
                        _desired: 'yi-Hebr',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'sr-Cyrl': {
                        _desired: 'sr-Latn',
                        _distance: '5',
                    },
                },
                {
                    'zh-Hans': {
                        _desired: 'za-Latn',
                        _distance: '10',
                        _oneway: 'true',
                    },
                },
                {
                    'zh-Hans': {
                        _desired: 'zh-Hani',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    'zh-Hant': {
                        _desired: 'zh-Hani',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    'ar-Arab': {
                        _desired: 'ar-Latn',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    'bn-Beng': {
                        _desired: 'bn-Latn',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    'gu-Gujr': {
                        _desired: 'gu-Latn',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    'hi-Deva': {
                        _desired: 'hi-Latn',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    'kn-Knda': {
                        _desired: 'kn-Latn',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    'ml-Mlym': {
                        _desired: 'ml-Latn',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    'mr-Deva': {
                        _desired: 'mr-Latn',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    'ta-Taml': {
                        _desired: 'ta-Latn',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    'te-Telu': {
                        _desired: 'te-Latn',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    'zh-Hans': {
                        _desired: 'zh-Latn',
                        _distance: '20',
                        _oneway: 'true',
                    },
                },
                {
                    'ja-Jpan': {
                        _desired: 'ja-Latn',
                        _distance: '5',
                        _oneway: 'true',
                    },
                },
                {
                    'ja-Jpan': {
                        _desired: 'ja-Hani',
                        _distance: '5',
                        _oneway: 'true',
                    },
                },
                {
                    'ja-Jpan': {
                        _desired: 'ja-Hira',
                        _distance: '5',
                        _oneway: 'true',
                    },
                },
                {
                    'ja-Jpan': {
                        _desired: 'ja-Kana',
                        _distance: '5',
                        _oneway: 'true',
                    },
                },
                {
                    'ja-Jpan': {
                        _desired: 'ja-Hrkt',
                        _distance: '5',
                        _oneway: 'true',
                    },
                },
                {
                    'ja-Hrkt': {
                        _desired: 'ja-Hira',
                        _distance: '5',
                        _oneway: 'true',
                    },
                },
                {
                    'ja-Hrkt': {
                        _desired: 'ja-Kana',
                        _distance: '5',
                        _oneway: 'true',
                    },
                },
                {
                    'ko-Kore': {
                        _desired: 'ko-Hani',
                        _distance: '5',
                        _oneway: 'true',
                    },
                },
                {
                    'ko-Kore': {
                        _desired: 'ko-Hang',
                        _distance: '5',
                        _oneway: 'true',
                    },
                },
                {
                    'ko-Kore': {
                        _desired: 'ko-Jamo',
                        _distance: '5',
                        _oneway: 'true',
                    },
                },
                {
                    'ko-Hang': {
                        _desired: 'ko-Jamo',
                        _distance: '5',
                        _oneway: 'true',
                    },
                },
                {
                    '*-*': {
                        _desired: '*-*',
                        _distance: '50',
                    },
                },
                {
                    'ar-*-$maghreb': {
                        _desired: 'ar-*-$maghreb',
                        _distance: '4',
                    },
                },
                {
                    'ar-*-$!maghreb': {
                        _desired: 'ar-*-$!maghreb',
                        _distance: '4',
                    },
                },
                {
                    'ar-*-*': {
                        _desired: 'ar-*-*',
                        _distance: '5',
                    },
                },
                {
                    'en-*-$enUS': {
                        _desired: 'en-*-$enUS',
                        _distance: '4',
                    },
                },
                {
                    'en-*-GB': {
                        _desired: 'en-*-$!enUS',
                        _distance: '3',
                    },
                },
                {
                    'en-*-$!enUS': {
                        _desired: 'en-*-$!enUS',
                        _distance: '4',
                    },
                },
                {
                    'en-*-*': {
                        _desired: 'en-*-*',
                        _distance: '5',
                    },
                },
                {
                    'es-*-$americas': {
                        _desired: 'es-*-$americas',
                        _distance: '4',
                    },
                },
                {
                    'es-*-$!americas': {
                        _desired: 'es-*-$!americas',
                        _distance: '4',
                    },
                },
                {
                    'es-*-*': {
                        _desired: 'es-*-*',
                        _distance: '5',
                    },
                },
                {
                    'pt-*-$americas': {
                        _desired: 'pt-*-$americas',
                        _distance: '4',
                    },
                },
                {
                    'pt-*-$!americas': {
                        _desired: 'pt-*-$!americas',
                        _distance: '4',
                    },
                },
                {
                    'pt-*-*': {
                        _desired: 'pt-*-*',
                        _distance: '5',
                    },
                },
                {
                    'zh-Hant-$cnsar': {
                        _desired: 'zh-Hant-$cnsar',
                        _distance: '4',
                    },
                },
                {
                    'zh-Hant-$!cnsar': {
                        _desired: 'zh-Hant-$!cnsar',
                        _distance: '4',
                    },
                },
                {
                    'zh-Hant-*': {
                        _desired: 'zh-Hant-*',
                        _distance: '5',
                    },
                },
                {
                    '*-*-*': {
                        _desired: '*-*-*',
                        _distance: '4',
                    },
                },
            ],
        },
    },
};

// This file is generated from regions-gen.ts
var regions = {
    "001": [
        "001",
        "001-status-grouping",
        "002",
        "005",
        "009",
        "011",
        "013",
        "014",
        "015",
        "017",
        "018",
        "019",
        "021",
        "029",
        "030",
        "034",
        "035",
        "039",
        "053",
        "054",
        "057",
        "061",
        "142",
        "143",
        "145",
        "150",
        "151",
        "154",
        "155",
        "AC",
        "AD",
        "AE",
        "AF",
        "AG",
        "AI",
        "AL",
        "AM",
        "AO",
        "AQ",
        "AR",
        "AS",
        "AT",
        "AU",
        "AW",
        "AX",
        "AZ",
        "BA",
        "BB",
        "BD",
        "BE",
        "BF",
        "BG",
        "BH",
        "BI",
        "BJ",
        "BL",
        "BM",
        "BN",
        "BO",
        "BQ",
        "BR",
        "BS",
        "BT",
        "BV",
        "BW",
        "BY",
        "BZ",
        "CA",
        "CC",
        "CD",
        "CF",
        "CG",
        "CH",
        "CI",
        "CK",
        "CL",
        "CM",
        "CN",
        "CO",
        "CP",
        "CQ",
        "CR",
        "CU",
        "CV",
        "CW",
        "CX",
        "CY",
        "CZ",
        "DE",
        "DG",
        "DJ",
        "DK",
        "DM",
        "DO",
        "DZ",
        "EA",
        "EC",
        "EE",
        "EG",
        "EH",
        "ER",
        "ES",
        "ET",
        "EU",
        "EZ",
        "FI",
        "FJ",
        "FK",
        "FM",
        "FO",
        "FR",
        "GA",
        "GB",
        "GD",
        "GE",
        "GF",
        "GG",
        "GH",
        "GI",
        "GL",
        "GM",
        "GN",
        "GP",
        "GQ",
        "GR",
        "GS",
        "GT",
        "GU",
        "GW",
        "GY",
        "HK",
        "HM",
        "HN",
        "HR",
        "HT",
        "HU",
        "IC",
        "ID",
        "IE",
        "IL",
        "IM",
        "IN",
        "IO",
        "IQ",
        "IR",
        "IS",
        "IT",
        "JE",
        "JM",
        "JO",
        "JP",
        "KE",
        "KG",
        "KH",
        "KI",
        "KM",
        "KN",
        "KP",
        "KR",
        "KW",
        "KY",
        "KZ",
        "LA",
        "LB",
        "LC",
        "LI",
        "LK",
        "LR",
        "LS",
        "LT",
        "LU",
        "LV",
        "LY",
        "MA",
        "MC",
        "MD",
        "ME",
        "MF",
        "MG",
        "MH",
        "MK",
        "ML",
        "MM",
        "MN",
        "MO",
        "MP",
        "MQ",
        "MR",
        "MS",
        "MT",
        "MU",
        "MV",
        "MW",
        "MX",
        "MY",
        "MZ",
        "NA",
        "NC",
        "NE",
        "NF",
        "NG",
        "NI",
        "NL",
        "NO",
        "NP",
        "NR",
        "NU",
        "NZ",
        "OM",
        "PA",
        "PE",
        "PF",
        "PG",
        "PH",
        "PK",
        "PL",
        "PM",
        "PN",
        "PR",
        "PS",
        "PT",
        "PW",
        "PY",
        "QA",
        "QO",
        "RE",
        "RO",
        "RS",
        "RU",
        "RW",
        "SA",
        "SB",
        "SC",
        "SD",
        "SE",
        "SG",
        "SH",
        "SI",
        "SJ",
        "SK",
        "SL",
        "SM",
        "SN",
        "SO",
        "SR",
        "SS",
        "ST",
        "SV",
        "SX",
        "SY",
        "SZ",
        "TA",
        "TC",
        "TD",
        "TF",
        "TG",
        "TH",
        "TJ",
        "TK",
        "TL",
        "TM",
        "TN",
        "TO",
        "TR",
        "TT",
        "TV",
        "TW",
        "TZ",
        "UA",
        "UG",
        "UM",
        "UN",
        "US",
        "UY",
        "UZ",
        "VA",
        "VC",
        "VE",
        "VG",
        "VI",
        "VN",
        "VU",
        "WF",
        "WS",
        "XK",
        "YE",
        "YT",
        "ZA",
        "ZM",
        "ZW"
    ],
    "002": [
        "002",
        "002-status-grouping",
        "011",
        "014",
        "015",
        "017",
        "018",
        "202",
        "AO",
        "BF",
        "BI",
        "BJ",
        "BW",
        "CD",
        "CF",
        "CG",
        "CI",
        "CM",
        "CV",
        "DJ",
        "DZ",
        "EA",
        "EG",
        "EH",
        "ER",
        "ET",
        "GA",
        "GH",
        "GM",
        "GN",
        "GQ",
        "GW",
        "IC",
        "IO",
        "KE",
        "KM",
        "LR",
        "LS",
        "LY",
        "MA",
        "MG",
        "ML",
        "MR",
        "MU",
        "MW",
        "MZ",
        "NA",
        "NE",
        "NG",
        "RE",
        "RW",
        "SC",
        "SD",
        "SH",
        "SL",
        "SN",
        "SO",
        "SS",
        "ST",
        "SZ",
        "TD",
        "TF",
        "TG",
        "TN",
        "TZ",
        "UG",
        "YT",
        "ZA",
        "ZM",
        "ZW"
    ],
    "003": [
        "003",
        "013",
        "021",
        "029",
        "AG",
        "AI",
        "AW",
        "BB",
        "BL",
        "BM",
        "BQ",
        "BS",
        "BZ",
        "CA",
        "CR",
        "CU",
        "CW",
        "DM",
        "DO",
        "GD",
        "GL",
        "GP",
        "GT",
        "HN",
        "HT",
        "JM",
        "KN",
        "KY",
        "LC",
        "MF",
        "MQ",
        "MS",
        "MX",
        "NI",
        "PA",
        "PM",
        "PR",
        "SV",
        "SX",
        "TC",
        "TT",
        "US",
        "VC",
        "VG",
        "VI"
    ],
    "005": [
        "005",
        "AR",
        "BO",
        "BR",
        "BV",
        "CL",
        "CO",
        "EC",
        "FK",
        "GF",
        "GS",
        "GY",
        "PE",
        "PY",
        "SR",
        "UY",
        "VE"
    ],
    "009": [
        "009",
        "053",
        "054",
        "057",
        "061",
        "AC",
        "AQ",
        "AS",
        "AU",
        "CC",
        "CK",
        "CP",
        "CX",
        "DG",
        "FJ",
        "FM",
        "GU",
        "HM",
        "KI",
        "MH",
        "MP",
        "NC",
        "NF",
        "NR",
        "NU",
        "NZ",
        "PF",
        "PG",
        "PN",
        "PW",
        "QO",
        "SB",
        "TA",
        "TK",
        "TO",
        "TV",
        "UM",
        "VU",
        "WF",
        "WS"
    ],
    "011": [
        "011",
        "BF",
        "BJ",
        "CI",
        "CV",
        "GH",
        "GM",
        "GN",
        "GW",
        "LR",
        "ML",
        "MR",
        "NE",
        "NG",
        "SH",
        "SL",
        "SN",
        "TG"
    ],
    "013": [
        "013",
        "BZ",
        "CR",
        "GT",
        "HN",
        "MX",
        "NI",
        "PA",
        "SV"
    ],
    "014": [
        "014",
        "BI",
        "DJ",
        "ER",
        "ET",
        "IO",
        "KE",
        "KM",
        "MG",
        "MU",
        "MW",
        "MZ",
        "RE",
        "RW",
        "SC",
        "SO",
        "SS",
        "TF",
        "TZ",
        "UG",
        "YT",
        "ZM",
        "ZW"
    ],
    "015": [
        "015",
        "DZ",
        "EA",
        "EG",
        "EH",
        "IC",
        "LY",
        "MA",
        "SD",
        "TN"
    ],
    "017": [
        "017",
        "AO",
        "CD",
        "CF",
        "CG",
        "CM",
        "GA",
        "GQ",
        "ST",
        "TD"
    ],
    "018": [
        "018",
        "BW",
        "LS",
        "NA",
        "SZ",
        "ZA"
    ],
    "019": [
        "003",
        "005",
        "013",
        "019",
        "019-status-grouping",
        "021",
        "029",
        "419",
        "AG",
        "AI",
        "AR",
        "AW",
        "BB",
        "BL",
        "BM",
        "BO",
        "BQ",
        "BR",
        "BS",
        "BV",
        "BZ",
        "CA",
        "CL",
        "CO",
        "CR",
        "CU",
        "CW",
        "DM",
        "DO",
        "EC",
        "FK",
        "GD",
        "GF",
        "GL",
        "GP",
        "GS",
        "GT",
        "GY",
        "HN",
        "HT",
        "JM",
        "KN",
        "KY",
        "LC",
        "MF",
        "MQ",
        "MS",
        "MX",
        "NI",
        "PA",
        "PE",
        "PM",
        "PR",
        "PY",
        "SR",
        "SV",
        "SX",
        "TC",
        "TT",
        "US",
        "UY",
        "VC",
        "VE",
        "VG",
        "VI"
    ],
    "021": [
        "021",
        "BM",
        "CA",
        "GL",
        "PM",
        "US"
    ],
    "029": [
        "029",
        "AG",
        "AI",
        "AW",
        "BB",
        "BL",
        "BQ",
        "BS",
        "CU",
        "CW",
        "DM",
        "DO",
        "GD",
        "GP",
        "HT",
        "JM",
        "KN",
        "KY",
        "LC",
        "MF",
        "MQ",
        "MS",
        "PR",
        "SX",
        "TC",
        "TT",
        "VC",
        "VG",
        "VI"
    ],
    "030": [
        "030",
        "CN",
        "HK",
        "JP",
        "KP",
        "KR",
        "MN",
        "MO",
        "TW"
    ],
    "034": [
        "034",
        "AF",
        "BD",
        "BT",
        "IN",
        "IR",
        "LK",
        "MV",
        "NP",
        "PK"
    ],
    "035": [
        "035",
        "BN",
        "ID",
        "KH",
        "LA",
        "MM",
        "MY",
        "PH",
        "SG",
        "TH",
        "TL",
        "VN"
    ],
    "039": [
        "039",
        "AD",
        "AL",
        "BA",
        "ES",
        "GI",
        "GR",
        "HR",
        "IT",
        "ME",
        "MK",
        "MT",
        "PT",
        "RS",
        "SI",
        "SM",
        "VA",
        "XK"
    ],
    "053": [
        "053",
        "AU",
        "CC",
        "CX",
        "HM",
        "NF",
        "NZ"
    ],
    "054": [
        "054",
        "FJ",
        "NC",
        "PG",
        "SB",
        "VU"
    ],
    "057": [
        "057",
        "FM",
        "GU",
        "KI",
        "MH",
        "MP",
        "NR",
        "PW",
        "UM"
    ],
    "061": [
        "061",
        "AS",
        "CK",
        "NU",
        "PF",
        "PN",
        "TK",
        "TO",
        "TV",
        "WF",
        "WS"
    ],
    "142": [
        "030",
        "034",
        "035",
        "142",
        "143",
        "145",
        "AE",
        "AF",
        "AM",
        "AZ",
        "BD",
        "BH",
        "BN",
        "BT",
        "CN",
        "CY",
        "GE",
        "HK",
        "ID",
        "IL",
        "IN",
        "IQ",
        "IR",
        "JO",
        "JP",
        "KG",
        "KH",
        "KP",
        "KR",
        "KW",
        "KZ",
        "LA",
        "LB",
        "LK",
        "MM",
        "MN",
        "MO",
        "MV",
        "MY",
        "NP",
        "OM",
        "PH",
        "PK",
        "PS",
        "QA",
        "SA",
        "SG",
        "SY",
        "TH",
        "TJ",
        "TL",
        "TM",
        "TR",
        "TW",
        "UZ",
        "VN",
        "YE"
    ],
    "143": [
        "143",
        "KG",
        "KZ",
        "TJ",
        "TM",
        "UZ"
    ],
    "145": [
        "145",
        "AE",
        "AM",
        "AZ",
        "BH",
        "CY",
        "GE",
        "IL",
        "IQ",
        "JO",
        "KW",
        "LB",
        "OM",
        "PS",
        "QA",
        "SA",
        "SY",
        "TR",
        "YE"
    ],
    "150": [
        "039",
        "150",
        "151",
        "154",
        "155",
        "AD",
        "AL",
        "AT",
        "AX",
        "BA",
        "BE",
        "BG",
        "BY",
        "CH",
        "CQ",
        "CZ",
        "DE",
        "DK",
        "EE",
        "ES",
        "FI",
        "FO",
        "FR",
        "GB",
        "GG",
        "GI",
        "GR",
        "HR",
        "HU",
        "IE",
        "IM",
        "IS",
        "IT",
        "JE",
        "LI",
        "LT",
        "LU",
        "LV",
        "MC",
        "MD",
        "ME",
        "MK",
        "MT",
        "NL",
        "NO",
        "PL",
        "PT",
        "RO",
        "RS",
        "RU",
        "SE",
        "SI",
        "SJ",
        "SK",
        "SM",
        "UA",
        "VA",
        "XK"
    ],
    "151": [
        "151",
        "BG",
        "BY",
        "CZ",
        "HU",
        "MD",
        "PL",
        "RO",
        "RU",
        "SK",
        "UA"
    ],
    "154": [
        "154",
        "AX",
        "CQ",
        "DK",
        "EE",
        "FI",
        "FO",
        "GB",
        "GG",
        "IE",
        "IM",
        "IS",
        "JE",
        "LT",
        "LV",
        "NO",
        "SE",
        "SJ"
    ],
    "155": [
        "155",
        "AT",
        "BE",
        "CH",
        "DE",
        "FR",
        "LI",
        "LU",
        "MC",
        "NL"
    ],
    "202": [
        "011",
        "014",
        "017",
        "018",
        "202",
        "AO",
        "BF",
        "BI",
        "BJ",
        "BW",
        "CD",
        "CF",
        "CG",
        "CI",
        "CM",
        "CV",
        "DJ",
        "ER",
        "ET",
        "GA",
        "GH",
        "GM",
        "GN",
        "GQ",
        "GW",
        "IO",
        "KE",
        "KM",
        "LR",
        "LS",
        "MG",
        "ML",
        "MR",
        "MU",
        "MW",
        "MZ",
        "NA",
        "NE",
        "NG",
        "RE",
        "RW",
        "SC",
        "SH",
        "SL",
        "SN",
        "SO",
        "SS",
        "ST",
        "SZ",
        "TD",
        "TF",
        "TG",
        "TZ",
        "UG",
        "YT",
        "ZA",
        "ZM",
        "ZW"
    ],
    "419": [
        "005",
        "013",
        "029",
        "419",
        "AG",
        "AI",
        "AR",
        "AW",
        "BB",
        "BL",
        "BO",
        "BQ",
        "BR",
        "BS",
        "BV",
        "BZ",
        "CL",
        "CO",
        "CR",
        "CU",
        "CW",
        "DM",
        "DO",
        "EC",
        "FK",
        "GD",
        "GF",
        "GP",
        "GS",
        "GT",
        "GY",
        "HN",
        "HT",
        "JM",
        "KN",
        "KY",
        "LC",
        "MF",
        "MQ",
        "MS",
        "MX",
        "NI",
        "PA",
        "PE",
        "PR",
        "PY",
        "SR",
        "SV",
        "SX",
        "TC",
        "TT",
        "UY",
        "VC",
        "VE",
        "VG",
        "VI"
    ],
    "EU": [
        "AT",
        "BE",
        "BG",
        "CY",
        "CZ",
        "DE",
        "DK",
        "EE",
        "ES",
        "EU",
        "FI",
        "FR",
        "GR",
        "HR",
        "HU",
        "IE",
        "IT",
        "LT",
        "LU",
        "LV",
        "MT",
        "NL",
        "PL",
        "PT",
        "RO",
        "SE",
        "SI",
        "SK"
    ],
    "EZ": [
        "AT",
        "BE",
        "CY",
        "DE",
        "EE",
        "ES",
        "EZ",
        "FI",
        "FR",
        "GR",
        "IE",
        "IT",
        "LT",
        "LU",
        "LV",
        "MT",
        "NL",
        "PT",
        "SI",
        "SK"
    ],
    "QO": [
        "AC",
        "AQ",
        "CP",
        "DG",
        "QO",
        "TA"
    ],
    "UN": [
        "AD",
        "AE",
        "AF",
        "AG",
        "AL",
        "AM",
        "AO",
        "AR",
        "AT",
        "AU",
        "AZ",
        "BA",
        "BB",
        "BD",
        "BE",
        "BF",
        "BG",
        "BH",
        "BI",
        "BJ",
        "BN",
        "BO",
        "BR",
        "BS",
        "BT",
        "BW",
        "BY",
        "BZ",
        "CA",
        "CD",
        "CF",
        "CG",
        "CH",
        "CI",
        "CL",
        "CM",
        "CN",
        "CO",
        "CR",
        "CU",
        "CV",
        "CY",
        "CZ",
        "DE",
        "DJ",
        "DK",
        "DM",
        "DO",
        "DZ",
        "EC",
        "EE",
        "EG",
        "ER",
        "ES",
        "ET",
        "FI",
        "FJ",
        "FM",
        "FR",
        "GA",
        "GB",
        "GD",
        "GE",
        "GH",
        "GM",
        "GN",
        "GQ",
        "GR",
        "GT",
        "GW",
        "GY",
        "HN",
        "HR",
        "HT",
        "HU",
        "ID",
        "IE",
        "IL",
        "IN",
        "IQ",
        "IR",
        "IS",
        "IT",
        "JM",
        "JO",
        "JP",
        "KE",
        "KG",
        "KH",
        "KI",
        "KM",
        "KN",
        "KP",
        "KR",
        "KW",
        "KZ",
        "LA",
        "LB",
        "LC",
        "LI",
        "LK",
        "LR",
        "LS",
        "LT",
        "LU",
        "LV",
        "LY",
        "MA",
        "MC",
        "MD",
        "ME",
        "MG",
        "MH",
        "MK",
        "ML",
        "MM",
        "MN",
        "MR",
        "MT",
        "MU",
        "MV",
        "MW",
        "MX",
        "MY",
        "MZ",
        "NA",
        "NE",
        "NG",
        "NI",
        "NL",
        "NO",
        "NP",
        "NR",
        "NZ",
        "OM",
        "PA",
        "PE",
        "PG",
        "PH",
        "PK",
        "PL",
        "PT",
        "PW",
        "PY",
        "QA",
        "RO",
        "RS",
        "RU",
        "RW",
        "SA",
        "SB",
        "SC",
        "SD",
        "SE",
        "SG",
        "SI",
        "SK",
        "SL",
        "SM",
        "SN",
        "SO",
        "SR",
        "SS",
        "ST",
        "SV",
        "SY",
        "SZ",
        "TD",
        "TG",
        "TH",
        "TJ",
        "TL",
        "TM",
        "TN",
        "TO",
        "TR",
        "TT",
        "TV",
        "TZ",
        "UA",
        "UG",
        "UN",
        "US",
        "UY",
        "UZ",
        "VC",
        "VE",
        "VN",
        "VU",
        "WS",
        "YE",
        "ZA",
        "ZM",
        "ZW"
    ]
};

var UNICODE_EXTENSION_SEQUENCE_REGEX = /-u(?:-[0-9a-z]{2,8})+/gi;
function invariant(condition, message, Err) {
    if (Err === void 0) { Err = Error; }
    if (!condition) {
        throw new Err(message);
    }
}
// This is effectively 2 languages in 2 different regions in the same cluster
var DEFAULT_MATCHING_THRESHOLD = 838;
var PROCESSED_DATA;
function processData() {
    var _a, _b;
    if (!PROCESSED_DATA) {
        var paradigmLocales = (_b = (_a = data.supplemental.languageMatching['written-new'][0]) === null || _a === void 0 ? void 0 : _a.paradigmLocales) === null || _b === void 0 ? void 0 : _b._locales.split(' ');
        var matchVariables = data.supplemental.languageMatching['written-new'].slice(1, 5);
        var data$1 = data.supplemental.languageMatching['written-new'].slice(5);
        var matches = data$1.map(function (d) {
            var key = Object.keys(d)[0];
            var value = d[key];
            return {
                supported: key,
                desired: value._desired,
                distance: +value._distance,
                oneway: value.oneway === 'true' ? true : false,
            };
        }, {});
        PROCESSED_DATA = {
            matches: matches,
            matchVariables: matchVariables.reduce(function (all, d) {
                var key = Object.keys(d)[0];
                var value = d[key];
                all[key.slice(1)] = value._value.split('+');
                return all;
            }, {}),
            paradigmLocales: __spreadArray(__spreadArray([], paradigmLocales, true), paradigmLocales.map(function (l) {
                return new Intl.Locale(l.replace(/_/g, '-')).maximize().toString();
            }), true),
        };
    }
    return PROCESSED_DATA;
}
function isMatched(locale, languageMatchInfoLocale, matchVariables) {
    var _a = languageMatchInfoLocale.split('-'), language = _a[0], script = _a[1], region = _a[2];
    var matches = true;
    if (region && region[0] === '$') {
        var shouldInclude = region[1] !== '!';
        var matchRegions = shouldInclude
            ? matchVariables[region.slice(1)]
            : matchVariables[region.slice(2)];
        var expandedMatchedRegions = matchRegions
            .map(function (r) { return regions[r] || [r]; })
            .reduce(function (all, list) { return __spreadArray(__spreadArray([], all, true), list, true); }, []);
        matches && (matches = !(expandedMatchedRegions.indexOf(locale.region || '') > -1 !=
            shouldInclude));
    }
    else {
        matches && (matches = locale.region
            ? region === '*' || region === locale.region
            : true);
    }
    matches && (matches = locale.script ? script === '*' || script === locale.script : true);
    matches && (matches = locale.language
        ? language === '*' || language === locale.language
        : true);
    return matches;
}
function serializeLSR(lsr) {
    return [lsr.language, lsr.script, lsr.region].filter(Boolean).join('-');
}
function findMatchingDistanceForLSR(desired, supported, data) {
    for (var _i = 0, _a = data.matches; _i < _a.length; _i++) {
        var d = _a[_i];
        var matches = isMatched(desired, d.desired, data.matchVariables) &&
            isMatched(supported, d.supported, data.matchVariables);
        if (!d.oneway && !matches) {
            matches =
                isMatched(desired, d.supported, data.matchVariables) &&
                    isMatched(supported, d.desired, data.matchVariables);
        }
        if (matches) {
            var distance = d.distance * 10;
            if (data.paradigmLocales.indexOf(serializeLSR(desired)) > -1 !=
                data.paradigmLocales.indexOf(serializeLSR(supported)) > -1) {
                return distance - 1;
            }
            return distance;
        }
    }
    throw new Error('No matching distance found');
}
function findMatchingDistance(desired, supported) {
    var desiredLocale = new Intl.Locale(desired).maximize();
    var supportedLocale = new Intl.Locale(supported).maximize();
    var desiredLSR = {
        language: desiredLocale.language,
        script: desiredLocale.script || '',
        region: desiredLocale.region || '',
    };
    var supportedLSR = {
        language: supportedLocale.language,
        script: supportedLocale.script || '',
        region: supportedLocale.region || '',
    };
    var matchingDistance = 0;
    var data = processData();
    if (desiredLSR.language !== supportedLSR.language) {
        matchingDistance += findMatchingDistanceForLSR({
            language: desiredLocale.language,
            script: '',
            region: '',
        }, {
            language: supportedLocale.language,
            script: '',
            region: '',
        }, data);
    }
    if (desiredLSR.script !== supportedLSR.script) {
        matchingDistance += findMatchingDistanceForLSR({
            language: desiredLocale.language,
            script: desiredLSR.script,
            region: '',
        }, {
            language: supportedLocale.language,
            script: supportedLSR.script,
            region: '',
        }, data);
    }
    if (desiredLSR.region !== supportedLSR.region) {
        matchingDistance += findMatchingDistanceForLSR(desiredLSR, supportedLSR, data);
    }
    return matchingDistance;
}
function findBestMatch(requestedLocales, supportedLocales, threshold) {
    if (threshold === void 0) { threshold = DEFAULT_MATCHING_THRESHOLD; }
    var lowestDistance = Infinity;
    var result = {
        matchedDesiredLocale: '',
        distances: {},
    };
    requestedLocales.forEach(function (desired, i) {
        if (!result.distances[desired]) {
            result.distances[desired] = {};
        }
        supportedLocales.forEach(function (supported) {
            // Add some weight to the distance based on the order of the supported locales
            // Add penalty for the order of the requested locales, which currently is 0 since ECMA-402
            // doesn't really have room for weighted locales like `en; q=0.1`
            var distance = findMatchingDistance(desired, supported) + 0 + i * 40;
            result.distances[desired][supported] = distance;
            if (distance < lowestDistance) {
                lowestDistance = distance;
                result.matchedDesiredLocale = desired;
                result.matchedSupportedLocale = supported;
            }
        });
    });
    if (lowestDistance >= threshold) {
        result.matchedDesiredLocale = undefined;
        result.matchedSupportedLocale = undefined;
    }
    return result;
}

/**
 * https://tc39.es/ecma402/#sec-bestfitmatcher
 * @param availableLocales
 * @param requestedLocales
 * @param getDefaultLocale
 */
function BestFitMatcher(availableLocales, requestedLocales, getDefaultLocale) {
    var foundLocale;
    var extension;
    var noExtensionLocales = [];
    var noExtensionLocaleMap = requestedLocales.reduce(function (all, l) {
        var noExtensionLocale = l.replace(UNICODE_EXTENSION_SEQUENCE_REGEX, '');
        noExtensionLocales.push(noExtensionLocale);
        all[noExtensionLocale] = l;
        return all;
    }, {});
    var result = findBestMatch(noExtensionLocales, availableLocales);
    if (result.matchedSupportedLocale && result.matchedDesiredLocale) {
        foundLocale = result.matchedSupportedLocale;
        extension =
            noExtensionLocaleMap[result.matchedDesiredLocale].slice(result.matchedDesiredLocale.length) || undefined;
    }
    if (!foundLocale) {
        return { locale: getDefaultLocale() };
    }
    return {
        locale: foundLocale,
        extension: extension,
    };
}

function CanonicalizeUValue(ukey, uvalue) {
    // TODO: Implement algorithm for CanonicalizeUValue per https://tc39.es/ecma402/#sec-canonicalizeuvalue
    var lowerValue = uvalue.toLowerCase();
    invariant(ukey !== undefined, "ukey must be defined");
    var canonicalized = lowerValue;
    return canonicalized;
}

function CanonicalizeUnicodeLocaleId(locale) {
    return Intl.getCanonicalLocales(locale)[0];
}

function InsertUnicodeExtensionAndCanonicalize(locale, attributes, keywords) {
    invariant(locale.indexOf('-u-') === -1, 'Expected locale to not have a Unicode locale extension');
    var extension = '-u';
    for (var _i = 0, attributes_1 = attributes; _i < attributes_1.length; _i++) {
        var attr = attributes_1[_i];
        extension += "-".concat(attr);
    }
    for (var _a = 0, keywords_1 = keywords; _a < keywords_1.length; _a++) {
        var kw = keywords_1[_a];
        var key = kw.key, value = kw.value;
        extension += "-".concat(key);
        if (value !== '') {
            extension += "-".concat(value);
        }
    }
    if (extension === '-u') {
        return CanonicalizeUnicodeLocaleId(locale);
    }
    var privateIndex = locale.indexOf('-x-');
    var newLocale;
    if (privateIndex === -1) {
        newLocale = locale + extension;
    }
    else {
        var preExtension = locale.slice(0, privateIndex);
        var postExtension = locale.slice(privateIndex);
        newLocale = preExtension + extension + postExtension;
    }
    return CanonicalizeUnicodeLocaleId(newLocale);
}

/**
 * https://tc39.es/ecma402/#sec-bestavailablelocale
 * @param availableLocales
 * @param locale
 */
function BestAvailableLocale(availableLocales, locale) {
    var candidate = locale;
    while (true) {
        if (availableLocales.indexOf(candidate) > -1) {
            return candidate;
        }
        var pos = candidate.lastIndexOf('-');
        if (!~pos) {
            return undefined;
        }
        if (pos >= 2 && candidate[pos - 2] === '-') {
            pos -= 2;
        }
        candidate = candidate.slice(0, pos);
    }
}

/**
 * https://tc39.es/ecma402/#sec-lookupmatcher
 * @param availableLocales
 * @param requestedLocales
 * @param getDefaultLocale
 */
function LookupMatcher(availableLocales, requestedLocales, getDefaultLocale) {
    var result = { locale: '' };
    for (var _i = 0, requestedLocales_1 = requestedLocales; _i < requestedLocales_1.length; _i++) {
        var locale = requestedLocales_1[_i];
        var noExtensionLocale = locale.replace(UNICODE_EXTENSION_SEQUENCE_REGEX, '');
        var availableLocale = BestAvailableLocale(availableLocales, noExtensionLocale);
        if (availableLocale) {
            result.locale = availableLocale;
            if (locale !== noExtensionLocale) {
                result.extension = locale.slice(noExtensionLocale.length, locale.length);
            }
            return result;
        }
    }
    result.locale = getDefaultLocale();
    return result;
}

function UnicodeExtensionComponents(extension) {
    invariant(extension === extension.toLowerCase(), 'Expected extension to be lowercase');
    invariant(extension.slice(0, 3) === '-u-', 'Expected extension to be a Unicode locale extension');
    var attributes = [];
    var keywords = [];
    var keyword;
    var size = extension.length;
    var k = 3;
    while (k < size) {
        var e = extension.indexOf('-', k);
        var len = void 0;
        if (e === -1) {
            len = size - k;
        }
        else {
            len = e - k;
        }
        var subtag = extension.slice(k, k + len);
        invariant(len >= 2, 'Expected a subtag to have at least 2 characters');
        if (keyword === undefined && len != 2) {
            if (attributes.indexOf(subtag) === -1) {
                attributes.push(subtag);
            }
        }
        else if (len === 2) {
            keyword = { key: subtag, value: '' };
            if (keywords.find(function (k) { return k.key === (keyword === null || keyword === void 0 ? void 0 : keyword.key); }) === undefined) {
                keywords.push(keyword);
            }
        }
        else if ((keyword === null || keyword === void 0 ? void 0 : keyword.value) === '') {
            keyword.value = subtag;
        }
        else {
            invariant(keyword !== undefined, 'Expected keyword to be defined');
            keyword.value += '-' + subtag;
        }
        k += len + 1;
    }
    return { attributes: attributes, keywords: keywords };
}

/**
 * https://tc39.es/ecma402/#sec-resolvelocale
 */
function ResolveLocale(availableLocales, requestedLocales, options, relevantExtensionKeys, localeData, getDefaultLocale) {
    var _a;
    var matcher = options.localeMatcher;
    var r;
    if (matcher === 'lookup') {
        r = LookupMatcher(Array.from(availableLocales), requestedLocales, getDefaultLocale);
    }
    else {
        r = BestFitMatcher(Array.from(availableLocales), requestedLocales, getDefaultLocale);
    }
    if (r == null) {
        r = {
            locale: getDefaultLocale(),
            extension: '',
        };
    }
    var foundLocale = r.locale;
    var foundLocaleData = localeData[foundLocale];
    // TODO: We can't really guarantee that the locale data is available
    // invariant(
    //   foundLocaleData !== undefined,
    //   `Missing locale data for ${foundLocale}`
    // )
    var result = { locale: 'en', dataLocale: foundLocale };
    var components;
    var keywords;
    if (r.extension) {
        components = UnicodeExtensionComponents(r.extension);
        keywords = components.keywords;
    }
    else {
        keywords = [];
    }
    var supportedKeywords = [];
    var _loop_1 = function (key) {
        // TODO: Shouldn't default to empty array, see TODO above
        var keyLocaleData = (_a = foundLocaleData === null || foundLocaleData === void 0 ? void 0 : foundLocaleData[key]) !== null && _a !== void 0 ? _a : [];
        invariant(Array.isArray(keyLocaleData), "keyLocaleData for ".concat(key, " must be an array"));
        var value = keyLocaleData[0];
        invariant(value === undefined || typeof value === 'string', "value must be a string or undefined");
        var supportedKeyword = void 0;
        var entry = keywords.find(function (k) { return k.key === key; });
        if (entry) {
            var requestedValue = entry.value;
            if (requestedValue !== '') {
                if (keyLocaleData.indexOf(requestedValue) > -1) {
                    value = requestedValue;
                    supportedKeyword = {
                        key: key,
                        value: value,
                    };
                }
            }
            else if (keyLocaleData.indexOf('true') > -1) {
                value = 'true';
                supportedKeyword = {
                    key: key,
                    value: value,
                };
            }
        }
        var optionsValue = options[key];
        invariant(optionsValue == null || typeof optionsValue === 'string', "optionsValue must be a string or undefined");
        if (typeof optionsValue === 'string') {
            var ukey = key.toLowerCase();
            optionsValue = CanonicalizeUValue(ukey, optionsValue);
            if (optionsValue === '') {
                optionsValue = 'true';
            }
        }
        if (optionsValue !== value && keyLocaleData.indexOf(optionsValue) > -1) {
            value = optionsValue;
            supportedKeyword = undefined;
        }
        if (supportedKeyword) {
            supportedKeywords.push(supportedKeyword);
        }
        result[key] = value;
    };
    for (var _i = 0, relevantExtensionKeys_1 = relevantExtensionKeys; _i < relevantExtensionKeys_1.length; _i++) {
        var key = relevantExtensionKeys_1[_i];
        _loop_1(key);
    }
    var supportedAttributes = [];
    if (supportedKeywords.length > 0) {
        supportedAttributes = [];
        foundLocale = InsertUnicodeExtensionAndCanonicalize(foundLocale, supportedAttributes, supportedKeywords);
    }
    result.locale = foundLocale;
    return result;
}

/**
 * https://tc39.es/ecma402/#sec-lookupsupportedlocales
 * @param availableLocales
 * @param requestedLocales
 */
function LookupSupportedLocales(availableLocales, requestedLocales) {
    var subset = [];
    for (var _i = 0, requestedLocales_1 = requestedLocales; _i < requestedLocales_1.length; _i++) {
        var locale = requestedLocales_1[_i];
        var noExtensionLocale = locale.replace(UNICODE_EXTENSION_SEQUENCE_REGEX, '');
        var availableLocale = BestAvailableLocale(availableLocales, noExtensionLocale);
        if (availableLocale) {
            subset.push(availableLocale);
        }
    }
    return subset;
}

function match(requestedLocales, availableLocales, defaultLocale, opts) {
    return ResolveLocale(availableLocales, CanonicalizeLocaleList(requestedLocales), {
        localeMatcher: (opts === null || opts === void 0 ? void 0 : opts.algorithm) || 'best fit',
    }, [], {}, function () { return defaultLocale; }).locale;
}

var lib = /*#__PURE__*/Object.freeze({
    __proto__: null,
    [Symbol.toStringTag]: 'Module',
    match: match,
    LookupSupportedLocales: LookupSupportedLocales,
    ResolveLocale: ResolveLocale
});

var require$$1 = /*@__PURE__*/getAugmentedNamespace(lib);

var supportedLocales_generated = {};

Object.defineProperty(supportedLocales_generated, "__esModule", { value: true });
supportedLocales_generated.supportedLocales = void 0;
supportedLocales_generated.supportedLocales = ["af", "ak", "am", "an", "ar", "ars", "as", "asa", "ast", "az", "bal", "be", "bem", "bez", "bg", "bho", "bm", "bn", "bo", "br", "brx", "bs", "ca", "ce", "ceb", "cgg", "chr", "ckb", "cs", "cy", "da", "de", "doi", "dsb", "dv", "dz", "ee", "el", "en", "eo", "es", "et", "eu", "fa", "ff", "fi", "fil", "fo", "fr", "fur", "fy", "ga", "gd", "gl", "gsw", "gu", "guw", "gv", "ha", "haw", "he", "hi", "hnj", "hr", "hsb", "hu", "hy", "ia", "id", "ig", "ii", "io", "is", "it", "iu", "ja", "jbo", "jgo", "jmc", "jv", "jw", "ka", "kab", "kaj", "kcg", "kde", "kea", "kk", "kkj", "kl", "km", "kn", "ko", "ks", "ksb", "ksh", "ku", "kw", "ky", "lag", "lb", "lg", "lij", "lkt", "ln", "lo", "lt", "lv", "mas", "mg", "mgo", "mk", "ml", "mn", "mo", "mr", "ms", "mt", "my", "nah", "naq", "nb", "nd", "ne", "nl", "nn", "nnh", "no", "nqo", "nr", "nso", "ny", "nyn", "om", "or", "os", "osa", "pa", "pap", "pcm", "pl", "prg", "ps", "pt", "pt-PT", "rm", "ro", "rof", "ru", "rwk", "sah", "saq", "sat", "sc", "scn", "sd", "sdh", "se", "seh", "ses", "sg", "sh", "shi", "si", "sk", "sl", "sma", "smi", "smj", "smn", "sms", "sn", "so", "sq", "sr", "ss", "ssy", "st", "su", "sv", "sw", "syr", "ta", "te", "teo", "th", "ti", "tig", "tk", "tl", "tn", "to", "tpi", "tr", "ts", "tzm", "ug", "uk", "und", "ur", "uz", "ve", "vi", "vo", "vun", "wa", "wae", "wo", "xh", "xog", "yi", "yo", "yue", "zh", "zu"];

Object.defineProperty(shouldPolyfill$1, "__esModule", { value: true });
var shouldPolyfill_2 = shouldPolyfill$1.shouldPolyfill = shouldPolyfill;
var intl_localematcher_1 = require$$1;
var supported_locales_generated_1 = supportedLocales_generated;
function supportedLocalesOf(locale) {
    if (!locale) {
        return true;
    }
    var locales = Array.isArray(locale) ? locale : [locale];
    return Intl.PluralRules.supportedLocalesOf(locales).length === locales.length;
}
function shouldPolyfill(locale) {
    if (locale === void 0) { locale = 'en'; }
    if (!('PluralRules' in Intl) ||
        new Intl.PluralRules('en', { minimumFractionDigits: 2 }).select(1) ===
            'one' ||
        !supportedLocalesOf(locale)) {
        return locale ? (0, intl_localematcher_1.match)([locale], supported_locales_generated_1.supportedLocales, 'en') : undefined;
    }
}

const loadPolyfills = async ({
  language
}) => {
  try {
    await Promise.all([polyfillRelativeTimeFormat(language), polyfillPlurals(language)]);
  } catch (e) {
    console.error("loading polyfills:", e);
  }
};
async function polyfillRelativeTimeFormat(locale) {
  if (shouldPolyfill_2$1()) {
    await import('./polyfill-e29ef209.js').then(function (n) { return n.p; });
  }
  if (Intl.RelativeTimeFormat["polyfilled"]) {
    if (locale === ARABIC) {
      await import('./ar-8d01366c.js');
    } else {
      await import('./en-6bf08080.js');
    }
  }
}
async function polyfillPlurals(locale) {
  if (shouldPolyfill_2()) {
    await import('./polyfill-16cb04ff.js').then(function (n) { return n.p; });
  }
  if (Intl.PluralRules["polyfilled"]) {
    if (locale === "ar") {
      await import('./ar-4df658e1.js');
    } else {
      await import('./en-bd1fb034.js');
    }
  }
}

const getLanguageOptions = () => {
  return {
    loadPolyfills,
    shouldPolyfill: () => !!shouldPolyfill_2() || !!shouldPolyfill_2$1(),
    getTranslation
  };
};

const I18nProvider = ({
  browserLanguage,
  options,
  children
}) => {
  if (!options) {
    options = localStorage && localStorage.getItem("noLanguageOptions") ? {
      getTranslation: () => ({})
    } : getLanguageOptions();
  }
  const {
    language,
    translations,
    loading
  } = useLanguage(browserLanguage || getBrowserLanguage(), options);
  if (loading) {
    return null;
  }
  const dir = getDirection(language);
  if (canUseDOM) {
    const el = document.querySelector(".lang");
    if (el) {
      el.setAttribute("dir", dir);
    }
  }
  return /* @__PURE__ */ jsx(IntlProvider, {
    locale: language,
    messages: translations,
    children: /* @__PURE__ */ jsx("div", {
      className: "lang",
      dir,
      children
    })
  });
};

const extractResponsesData = (responsesResult) => {
  if (!responsesResult.extra) {
    responsesResult.extra = {};
  }
  const extra = responsesResult.extra;
  if (!extra.timestamp) {
    extra.timestamp = new Date().toISOString();
  }
  const {
    map: responses,
    list: { loading, error, values: order } = {}
  } = responsesResult;
  return { responses, loading, error, order, ...extra };
};

const loadResponses = async (api, postId, language) => {
  const opts = {
    _id: postId,
    language
  };
  const result = await api.loadResponses(opts);
  const responsesRequestResult = extractResponsesData(listRequest2Redux(result));
  return responsesRequestResult;
};
const loadPage = async (api, postId, options = {}) => {
  return api.get({
    path: `/post-page/${postId}` + buildQueryString(options)
  });
};
const loadPost = async (api, postId, options = {}) => {
  return api.get({
    path: `/post-view/${postId}` + buildQueryString(options)
  });
};
const postResponsesPath = (postId) => `/post-view-responses/${postId}`;
const mutatePostResponses = (postId) => mutate(postResponsesPath(postId));
const useLoadResponses = (postId, op) => {
  const api = useApi();
  const {
    language
  } = useLanguageSelectionContext();
  const result = useSWR(postId ? [postResponsesPath(postId), language] : null, async () => {
    return loadResponses(api, postId, language);
  }, {
    ...swrLoadOnce,
    ...op
  });
  return result;
};
const usePostGroupData = (postId, op) => {
  const api = useApi();
  const result = useSWR(postId ? `/posts/${postId}/group` : null, async () => {
    return loadPostCircleData(api, postId);
  }, {
    ...swrLoadOnce,
    ...op
  });
  return result;
};
const loadPostCircleData = async (api, postId) => {
  return api.makeRequest({
    path: `/posts/${postId}/group`
  });
};
const loadPostViewData = async (api, postId) => {
  const [responsesRequestResult, {
    group,
    membership,
    invitation
  }] = await Promise.all([loadResponses(api, postId), loadPostCircleData(api, postId)]);
  return {
    responsesRequestResult,
    group,
    membership,
    invitation
  };
};

export { postCircleUrl as $, APIProvider as A, BookAuthGate as B, ConfirmationCode as C, useProfile as D, CircleContextProvider as E, ForgotPassword as F, LanguageSelectionContextProvider as G, useLanguageSelectionContext as H, I18nProvider as I, LoadCircleData as J, noop$4 as K, LookupSupportedLocales$1 as L, CircleContext as M, useCircleContext as N, loadCirclePosts as O, PhoneOtpForm as P, loadCircleReferences as Q, ResolveLocale$1 as R, createPostUrl as S, editPostUrl as T, postAudienceUrl as U, objectId as V, importDocumentUrl as W, circleUrl as X, myCirclesUrl as Y, dashboardUrl as Z, __spreadArray$1 as _, __extends$1 as a, circleMembersUrl as a0, postCircleId as a1, circlePostUrl as a2, postUrl as a3, bookCairBrowseUrl as a4, bookCairProfileUrl as a5, bookCairLibraryUrl as a6, bookCairPurchasesUrl as a7, bookCairDashboardUrl as a8, bookCairPaymentUrl as a9, loadPage as aA, loadPost as aB, mutatePostResponses as aC, useLoadResponses as aD, usePostGroupData as aE, loadPostCircleData as aF, loadPostViewData as aG, bookCairPaymentResultUrl as aa, bookCairDesktopUrl as ab, bookCairMobileUrl as ac, bookCairReaderUrl as ad, url4post as ae, circleIdForPost as af, eventCalendarLink as ag, useAssistant as ah, useAssistants as ai, useCirclePostsState as aj, CirclePostsStateContext as ak, useCirclePostsStateContext as al, CirclePostsStateContextProvider as am, usePostAssets as an, usePost as ao, useCircle as ap, useSubCircles as aq, useInstance as ar, useAccessRequests as as, isValidPhoneNumber as at, stripAllWhitespaces as au, isValidateEmail as av, isValidPassword as aw, errorMessage as ax, swrLoadOnce as ay, loadResponses as az, __spreadArray as b, commonjsGlobal as c, ResolveLocale as d, LookupSupportedLocales as e, __extends as f, getAugmentedNamespace as g, require$$1 as h, shouldPolyfill$1 as i, RegistrationContextProvider as j, LoginForm as k, RegistrationForm as l, ChangePassword as m, useSetOnLoginAction as n, useSetOnLoginUrl as o, mobilePhoneData2Save as p, API as q, require$$1$1 as r, shouldPolyfill$3 as s, ApiContext as t, useRegistrationContext as u, useApi as v, PostFeatures as w, CircleStatTypes as x, isLocalhost as y, aiAnswerIntro as z };
//# sourceMappingURL=session-bcba996b.js.map
